// Copyright (c) 2025 Goffy. This file belongs to Goffy. All Rights Reserved.

import {
  Animated,
  Modal,
  Pressable,
  ScrollView,
  Easing,
  Platform,
  Image,
  useWindowDimensions,
  View as NativeView,
} from 'react-native';
import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Redirect, useLocalSearchParams, useRouter, useRootNavigationState, usePathname } from 'expo-router';
import { useIsFocused, useNavigation } from '@react-navigation/native';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';

import { Text, View } from '@/components/Themed';
import GoffyButton from '@/components/GoffyButton';
import { CoachMarkCallout } from '@/components/CoachMarkCallout';
import { t } from '@/i18n';
import {
  BffWebSocketClient,
  createBffWebSocket,
  GameMatchStatus,
  GameResultsEvent,
  HoleUpdateEvent,
  HoleInfoEvent,
  RoundStateEvent,
  type SendOutcome,
  TourLeaderboardStateEvent,
  TourLeaderboardUpdateEvent,
} from '@/services/bffWebSocket';
import { createAppLogger } from '@/services/appLogger';
import { incrementCounter, nowMs, recordHistogram } from '@/services/appMetrics';
import { setCurrentTourId } from '@/services/currentTour';
import { useRoundState } from '@/stores/roundState';
import { useDebugState } from '@/stores/debugState';
import { useDebugTraceState } from '@/stores/debugTraceState';
import { useUser } from '@/app/user-context';
import { useBatteryTelemetry } from '@/features/battery-telemetry/useBatteryTelemetry';
import { recordCourseTraceStroke, useCourseTrace } from '@/features/course-trace/useCourseTrace';
import { useRoundShots, useShotTracking } from '@/features/shot-tracking/useShotTracking';
import { formatHoleDistance } from '@/features/units/distance';
import { useDistanceUnit } from '@/stores/distanceUnitState';
import { useHazardAnimationsEnabled } from '@/stores/hazardAnimationState';
import type { HazardSymbolType } from '@/features/hole/assets';
import { CyclingSlot } from '@/features/hole/screen/CyclingSlot';
import { useShowDistanceEnabled } from '@/stores/showDistanceState';
import { HoleViewModal } from '@/features/hole/holeview/HoleViewModal';
import { useHoleViewGeometry } from '@/features/hole/holeview/useHoleViewGeometry';
import { usePlayerPosition } from '@/features/hole/holeview/usePlayerPosition';
import { distanceMeters } from '@/features/hole/holeview/tileMath';
import { useDeviceHeading } from '@/features/hole/holeview/useDeviceHeading';
import { useHoleElevation } from '@/features/hole/holeview/useHoleElevation';
import { useHoleWind } from '@/features/hole/holeview/useHoleWind';
import { useInviteViewer } from '@/hooks/useInviteViewer';
import { isValidHolesCount } from '@/utils/holesCount';
import * as holeOrder from '@/utils/holeOrder';
import {
  consumePassiveHole,
  consumeRoundEntry,
  hasLocalPosition,
  lastLocalNavigationAt,
  markLocalNavigation,
  markPassiveHole,
  reconnectHoleView,
  resolveInitialPositionTarget,
  resolveRemotePositionTarget,
} from '@/utils/holeSync';
import { getDeviceId } from '@/services/deviceId';
import type { RoundPositionUpdatedEvent } from '@/services/bffWebSocket';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import { getAssetImageSource } from '@/services/assetImageSource';
import { getPlayerAvatarImageSource } from '@/services/playerAvatarImageSource';
import GhostAvatar from '@/components/GhostAvatar';
import { getActiveClaimSessionSync, getClaimClientIdSync, loadClaimSession, subscribeClaimSession } from '@/services/claimSession';
import {
  attachOfflineQueue,
  createEventId,
  sendOrQueue,
  sendWithAck,
} from '@/services/offlineQueue';
import { getOfflineSubjectKey } from '@/services/offlineSubject';
import { patchUserCache, readUserCache } from '@/services/roundCache';
import { ConnectionStatusPill } from '@/features/hole/screen/components/ConnectionStatusPill';
import { holeCompactScoreCoachMarkKey, useCoachMark } from '@/services/coachMarks';
import { useEnhancedInput } from '@/hooks/useEnhancedInput';
import {
  deriveGreenInRegulation,
  EMPTY_ENHANCED_VALUES,
  ENHANCED_EVENT_TYPES,
  FAIRWAY_RESULTS,
  parseFairwayResult,
  stepPutts,
  type EnhancedHoleValues,
} from '@/features/hole/enhancedInput';
import { isMysteryAcknowledged } from '@/services/mysterySetup';
import {
  GAME_ICON_MAP,
  BunkerIcon,
  BirdieImage,
  ChipInIcon,
  GolfballIcon,
  hazardCritterForType,
  hazardScoreInputSymbol,
  hazardTypesInOrder,
  MysteryPrizeIcon,
  OutIcon,
  PenaltyIcon,
  ScorePopupLargeButtonImage,
  ScorePopupLongButtonImage,
  ScorePopupRailBannerImage,
  ThreePuttIcon,
  WaterIcon,
} from '@/features/hole/assets';

import {
  compactPlayerName,
  isGhostPlayerName,
  computeStablefordPointsForHole,
  computeStrokesReceivedForHole,
  hazardTypeFromName,
  isUuidLike,
  mergeSeededPlayers,
  normalizeGameSlug,
  normalizeHoleEntry,
  normalizeHoleEvent,
  normalizePlayerKey,
  normalizeTotalsList,
  playerHasRealName,
} from '@/features/hole/utils';
import type {
  DisplayTotal,
  GameDefinitionSummary,
  GameResultState,
  HoleEntry,
  HoleEventEntry,
  HoleMeta,
  Player,
  RoundSubgameEntry,
  ScoreTotal,
  Subgame,
  TeamSummary,
  TourLeaderboardResponse,
} from '@/features/hole/types';
import { getGameDisplayName, getSubgameDisplayName } from '@/features/game-definitions/i18n';
import { gameGroupHasPositions, getGameGroupId, groupItems } from '@/features/game-definitions/groups';
import { normalizeEclecticSection, normalizeFreezeSection } from '@/features/tour-games/normalize';
import { buildEclecticBestBeforeRound } from '@/features/tour-games/eclectic';
import { buildFreezeStatusBeforeRound } from '@/features/tour-games/freeze';
import type { EclecticSection, FreezeSection } from '@/features/tour-games/types';
import { patchRoundCache, readRoundCache } from '@/services/roundCache';
import {
  buildDisplayTotals,
  buildGameResultPlayers,
  buildGameResultRows,
  buildLeaderboardChangeGameLines,
  buildMysteryWinnersBySubgame,
  buildOrderedTeams,
  buildRoundPlacementByKey,
  buildTourLeaderKeys,
  buildTourPlacementByKey,
  resolveBreakdownGameIds,
} from '@/features/hole/screen/selectors';
import { styles } from '@/features/hole/screen/styles';
import { useHoleActions } from '@/features/hole/screen/useHoleActions';
import {
  cacheRoundHoleEntriesSnapshot,
  cacheRoundHoleEventsSnapshot,
  cacheRoundMetadataSnapshot,
  cacheRoundPlayersSnapshot,
  useHoleData,
} from '@/features/hole/screen/useHoleData';
import { useHoleRealtime } from '@/features/hole/screen/useHoleRealtime';
import type { MatchStatus } from '@/features/hole/screen/selectors';
import type { HoleScreenViewModel } from '@/features/hole/screen/types';
import { HoleHeaderCard } from '@/features/hole/screen/components/HoleHeaderCard';
import HolePhotoButton from '@/features/tour-photos/components/HolePhotoButton';
import { HeaderUser } from '@/components/HeaderUser';
import { requestBugReport } from '@/services/bugReportRequest';
import appColors from '@/theme/colors';

// Cache the tour-long Freeze section per tour so the frozen-hole badge renders
// immediately on mount; the tour.leaderboard websocket response refreshes it.
const freezeSectionCacheByTour = new Map<string, FreezeSection>();
// Same for the tour-long Eclectic section, which drives the "points to beat" badge.
const eclecticSectionCacheByTour = new Map<string, EclecticSection>();
// Cache game results per round so navigating between holes keeps the latest scores visible.
const gameResultsCache: Record<string, GameResultState> = {};
// Server-computed per-game extras (dormie/won match state, skins carry pot) from
// game.result. The rules live in games-service; the phone only renders these.
type ServerGameExtra = {
  match: GameMatchStatus | null;
  skinsCarry: number | null;
  /** Per-ball match state and carry for a game scored per ball; null for whole-round games. */
  balls: Array<{ groupNumber: number; match: GameMatchStatus | null; skinsCarry: number | null }> | null;
};
type ServerGameExtras = Record<string, ServerGameExtra>;
const serverGameExtrasCache: Record<string, ServerGameExtras> = {};
// Cache totals per round so leaderboard stays populated while moving between holes.
const roundTotalsCache: Record<string, ScoreTotal[]> = {};
// Cache leaderboard totals per round to avoid mixing with stroke totals when switching holes.
const roundLeaderboardCache: Record<string, ScoreTotal[]> = {};
// Cache hole metadata per round so route transitions do not wait for hole.info/round.state.
const roundHoleMetaCache: Record<string, Record<number, HoleMeta>> = {};
// Show "next hole mystery prize" popup once per user+round+hole in current app runtime.
const seenNextMysteryPopupByUserRound = new Set<string>();
const HoleMarkImage = getAssetImageSource('images/hole_mark.png', 'icon', '/round/[roundId]/hole/[hole]');
const PegImage = getAssetImageSource('images/peg.png', '', '/round/[roundId]/hole/[hole]', { width: 80, height: 80, bucketStep: 1 });
const HOLE_MENU_COUNT = 18;
// The podium is what the hole screen shows by default; the rest is behind the toggle.
const LEADERBOARD_COLLAPSED_COUNT = 3;
const HAZARD_GAME_META: Record<'water' | 'penalty' | 'bunker' | 'threePutt' | 'chipIn' | 'out', { gameId: string; gameName: string }> = {
  water: { gameId: 'e0b7c8af-3dc8-4a9f-8b77-5d99e5da0a4b', gameName: 'Water' },
  penalty: { gameId: '6b9c72e0-6c1c-4d17-9f1f-6c0e2f71a742', gameName: 'Penalty' },
  out: { gameId: '8b62c07d-7959-4677-8c75-39fecc966517', gameName: 'Out' },
  bunker: { gameId: '4d3a1a42-4f67-41a4-b63d-9a9b50a83f1f', gameName: 'Bunker' },
  threePutt: { gameId: 'f9d88a1b-1a7e-4c86-9b2e-8e7f7f0fcb2b', gameName: '3 Putts' },
  chipIn: { gameId: '7a2e1c94-3b6d-4f81-9c2a-5d8e0f1a2b3c', gameName: 'Chip in' },
};

// The round chips a hazard wears when animations are off — the same vocabulary on the score
// button and in the leaderboard, so switching animations off changes the art, not the layout.
const HAZARD_CHIPS = {
  bunker: { labelKey: 'hole.event.bunker', style: styles.compactScoreHazardBadgeSand, textStyle: styles.compactScoreHazardBadgeTextLight },
  water: { labelKey: 'hole.event.water', style: styles.compactScoreHazardBadgeWater, textStyle: styles.compactScoreHazardBadgeTextLight },
  penalty: { labelKey: 'hole.event.penalty', style: styles.compactScoreHazardBadgeOther, textStyle: styles.compactScoreHazardBadgeTextDark },
  out: { labelKey: 'hole.event.out', style: styles.compactScoreHazardBadgeOut, textStyle: styles.compactScoreHazardBadgeTextLight },
  threeputt: { labelKey: 'hole.event.threePutt', style: styles.compactScoreHazardBadgeThreeputt, textStyle: styles.compactScoreHazardBadgeTextDark },
} as const satisfies Record<HazardSymbolType, { labelKey: string; style: unknown; textStyle: unknown }>;

const capitalizeFirst = (value: string): string => value.charAt(0).toUpperCase() + value.slice(1);

// Result keys that belong to no game definition — Nassau front/back/press segments arrive under
// ids games-service derives per game. They are never part of the round's selection, so a prune
// against that selection has to let them through.
const derivedGameIds = (gameIds: string[], knownGameDefinitionIds: Set<string>): string[] =>
  gameIds.filter((gameId) => !knownGameDefinitionIds.has(gameId));

// Sentinel subgame id used when a mystery prize is awarded directly from the score popup without
// assigning a specific sub-challenge first. One generic prize per hole (see handleMysteryPrizeToggle).
const GENERIC_MYSTERY_PRIZE_SUBGAME_ID = 'ffffffff-ffff-ffff-ffff-ffffffffffff';

function mergeHoleEntriesForHole(
  existingEntries: HoleEntry[],
  targetHoleNumber: number,
  replacementEntries: HoleEntry[]
): HoleEntry[] {
  const next = existingEntries.filter((entry) => entry.holeNumber !== targetHoleNumber);
  replacementEntries.forEach((entry) => next.push(entry));
  return next;
}

type LeaderboardMovement = 'up' | 'down' | 'same';

/** The hole-complete modal is a glance at the top of the board, not the whole leaderboard. */
const LEADERBOARD_CHANGE_VISIBLE_ROWS = 4;

type LeaderboardChangeRow = {
  playerId: string;
  playerName: string;
  movement: LeaderboardMovement;
  roundRank: number | null;
  roundPoints?: number;
};

type RankSnapshotEntry = {
  rank: number | null;
  tieSize: number;
  score: number;
};

function extractRoundGameDefinitionIdsFromLeaderboardState(
  message: TourLeaderboardStateEvent,
  roundId: string
): string[] {
  const normalizedRoundId = roundId.trim().toLowerCase();
  const result = new Set<string>();

  const rounds = (message.rounds ?? []) as Array<Record<string, unknown>>;
  rounds.forEach((round) => {
    const id = String(round.id ?? round.Id ?? '').trim().toLowerCase();
    if (!id || id !== normalizedRoundId) return;
    const ids = (round.gameDefinitionIds ?? round.GameDefinitionIds ?? []) as unknown[];
    ids.forEach((value) => {
      if (typeof value !== 'string') return;
      const trimmed = value.trim();
      if (isUuidLike(trimmed)) {
        result.add(trimmed);
      }
    });
  });

  const games = (message.games ?? []) as Array<Record<string, unknown>>;
  games.forEach((game) => {
    const gameRoundId = String(game.roundId ?? game.RoundId ?? '').trim().toLowerCase();
    if (gameRoundId && gameRoundId !== normalizedRoundId) return;
    const candidates = [game.gameDefinitionId, game.GameDefinitionId];
    candidates.forEach((candidate) => {
      if (typeof candidate !== 'string') return;
      const trimmed = candidate.trim();
      if (isUuidLike(trimmed)) {
        result.add(trimmed);
      }
    });
  });

  const scoresByRoundGame = (message.scoresByRoundGame ?? []) as Array<Record<string, unknown>>;
  scoresByRoundGame.forEach((row) => {
    const rowRoundId = String(row.roundId ?? row.RoundId ?? '').trim().toLowerCase();
    if (!rowRoundId || rowRoundId !== normalizedRoundId) return;
    const candidates = [row.gameDefinitionId, row.GameDefinitionId];
    candidates.forEach((candidate) => {
      if (typeof candidate !== 'string') return;
      const trimmed = candidate.trim();
      if (isUuidLike(trimmed)) {
        result.add(trimmed);
      }
    });
  });

  return Array.from(result);
}

export default function HoleScreen() {
  const {
    debug: debugParam,
    roundId: roundIdParam,
    hole: holeParam,
    gameDefinitionIds: gameDefinitionIdsParam,
    dir,
  } = useLocalSearchParams<{
    debug?: string;
    roundId?: string;
    hole?: string;
    gameDefinitionIds?: string;
    dir?: string;
  }>();
  const router = useRouter();
  const pathname = usePathname();
  const navState = useRootNavigationState();
  const isNavigationReady = !!navState?.key;
  const isFocused = useIsFocused();
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const { height: windowHeight, width: windowWidth } = useWindowDimensions();
  const { user } = useUser();
  const [claimSession, setClaimSession] = useState(() => getActiveClaimSessionSync());
  const [claimSessionLoaded, setClaimSessionLoaded] = useState(false);
  const safePathname = pathname ?? '';
  const claimClientId = claimSession?.claimClientId ?? getClaimClientIdSync();
  const claimPlayerId = claimSession?.sourcePlayerId ?? null;
  const isGuestClaimMode = !user?.token && !!claimSession && !!claimClientId;
  const effectiveClaimClientId = isGuestClaimMode ? claimClientId : null;
  // Durable offline-queue identity: entries persist per signed-in user (or
  // guest claim) so they survive restarts and resume flushing after re-login.
  // claimSession state changes re-render, keeping the derived key fresh.
  const offlineSubjectKey = getOfflineSubjectKey(user);
  const isAnonymousReadOnlyMode = !user?.token;
  // An invite viewer is the *other* kind of anonymous visitor: no claim session, no player, just an
  // invite token in the URL. Unlike a claim guest they never score, so they take the REST data path
  // (every round read authorizes ?inviteToken=) instead of the claim-guest websocket bypass.
  const { isInviteViewer, inviteToken: viewerInviteToken, viewerRouteParams } = useInviteViewer();
  // Spectating beats every other credential. A signed-in user who chose to watch must not get
  // score entry back just because their token would be accepted, and neither must a signed-out
  // one carrying a claim session left over from some other round.
  const canWrite = !isInviteViewer && (!!user?.token || isGuestClaimMode);
  const shouldDeferAnonymousBootstrap = !user?.token && !isInviteViewer && !claimSessionLoaded;
  const storedRoundId = useRoundState((state) => state.roundId);
  const storedHole = useRoundState((state) => state.hole);
  const holeMenuRequestVersion = useRoundState((state) => state.holeMenuRequestVersion);
  const clearHoleMenuRequest = useRoundState((state) => state.clearHoleMenuRequest);
  const holeViewRequestVersion = useRoundState((state) => state.holeViewRequestVersion);
  const clearHoleViewRequest = useRoundState((state) => state.clearHoleViewRequest);
  const setRoundId = useRoundState((state) => state.setRoundId);
  const setHole = useRoundState((state) => state.setHole);
  const startHole = useRoundState((state) => state.startHole);
  const setStartHole = useRoundState((state) => state.setStartHole);
  const roundIdFromParams = useMemo(() => {
    const raw = Array.isArray(roundIdParam) ? roundIdParam[0] : roundIdParam;
    return raw ?? null;
  }, [roundIdParam]);
  const roundIdFromPath = useMemo(() => {
    if (!safePathname) return null;
    const parts = safePathname.split('/').filter(Boolean);
    const roundIndex = parts.indexOf('round');
    if (roundIndex < 0) return null;
    return parts[roundIndex + 1] ?? null;
  }, [safePathname]);
  const holeFromParams = useMemo(() => {
    const raw = Array.isArray(holeParam) ? holeParam[0] : holeParam;
    if (!raw) return null;
    const parsed = Number.parseInt(raw, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, [holeParam]);
  const holeFromPath = useMemo(() => {
    if (!safePathname) return null;
    const parts = safePathname.split('/').filter(Boolean);
    const holeIndex = parts.indexOf('hole');
    if (holeIndex < 0) return null;
    const raw = parts[holeIndex + 1];
    if (!raw) return null;
    const parsed = Number.parseInt(raw, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, [safePathname]);
  const debug = useMemo(() => {
    const raw = Array.isArray(debugParam) ? debugParam[0] : debugParam;
    return raw === 'true' || raw === '1';
  }, [debugParam]);
  const isSmallScorePopupScreen = windowHeight <= 820;
  const shouldTopAlignScorePopup = isSmallScorePopupScreen || isGuestClaimMode;
  const setDebugEnabled = useDebugState((state) => state.setEnabled);
  const holeViewTraceId = useDebugTraceState((state) => state.holeViewTraceId);
  const setHoleViewTraceId = useDebugTraceState((state) => state.setHoleViewTraceId);
  const setHoleScoreTraceId = useDebugTraceState((state) => state.setHoleScoreTraceId);
  const gameDefinitionIdsFromParams = useMemo(() => {
    const rawValue = Array.isArray(gameDefinitionIdsParam) ? gameDefinitionIdsParam[0] : gameDefinitionIdsParam;
    const raw = typeof rawValue === 'string' ? rawValue : '';
    if (!raw) return [] as string[];
    return raw
      .split(',')
      .map((value: string) => value.trim())
      .filter((value: string) => isUuidLike(value));
  }, [gameDefinitionIdsParam]);
  const [tourId, setTourId] = useState<string | undefined>(undefined);
  const roundId = roundIdFromParams ?? roundIdFromPath ?? storedRoundId;
  const holeNumber = holeFromParams ?? holeFromPath ?? storedHole ?? 1;
  // When opening a round that includes the Mystery game but whose subgames haven't been assigned
  // yet, send the user to the mystery-prize selection screen first (once per open). Uses a fresh
  // server fetch so it can't loop after the selection is saved.
  const mysteryRedirectCheckedRef = useRef(false);
  useEffect(() => {
    if (mysteryRedirectCheckedRef.current) return;
    if (!roundId || !user?.token || isGuestClaimMode) return;
    mysteryRedirectCheckedRef.current = true;
    let cancelled = false;
    (async () => {
      try {
        // Mystery setup is optional; if the user already saved or skipped it for this round, never
        // redirect them back (the assignment write is async, so subgames may still read as empty).
        if (await isMysteryAcknowledged(roundId)) return;
        const headers = { Authorization: `Bearer ${user.token}` };
        const [roundRes, defsRes, subgamesRes] = await Promise.all([
          fetch(`${apiBaseUrl}/api/rounds/${roundId}`, { headers }),
          fetch(`${apiBaseUrl}/api/game-definitions`, { headers }),
          fetch(`${apiBaseUrl}/api/rounds/${roundId}/subgames`, { headers }),
        ]);
        if (!roundRes.ok || !defsRes.ok || !subgamesRes.ok) return;
        const roundPayload = (await roundRes.json()) as {
          gameDefinitionIds?: string[];
          GameDefinitionIds?: string[];
          tourId?: string;
          TourId?: string;
        };
        const defs = (await defsRes.json()) as Array<{ id?: string; name?: string }>;
        const subgames = (await subgamesRes.json()) as unknown;
        const selectedIds = new Set(roundPayload.gameDefinitionIds ?? roundPayload.GameDefinitionIds ?? []);
        const hasMystery = defs.some(
          (def) => def?.id && selectedIds.has(def.id) && (def.name ?? '').toLowerCase() === 'mystery'
        );
        const subgameCount = Array.isArray(subgames) ? subgames.length : 0;
        if (!cancelled && hasMystery && subgameCount === 0) {
          const resolvedTourId = roundPayload.tourId ?? roundPayload.TourId ?? tourId ?? '';
          router.replace(`/round/${roundId}/mystery-prize?tourId=${resolvedTourId}&debug=${debug ? 'true' : 'false'}`);
        }
      } catch {
        // Ignore; the mystery prize can still be set from the menu.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, roundId, user?.token, isGuestClaimMode, router, tourId, debug]);
  const currentHoleDataKey = roundId ? `${roundId}:${holeNumber}` : null;
  const compactScoreButtonCoachMarkRef = useRef<NativeView>(null);
  const [compactScoreCoachMarkDismissed, setCompactScoreCoachMarkDismissed] = useState(false);
  const [players, setPlayers] = useState<Player[]>([]);
  const [scores, setScores] = useState<Record<string, string>>({});
  const [explicitScoreSelections, setExplicitScoreSelections] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totals, setTotals] = useState<ScoreTotal[]>([]);
  const [leaderboardTotals, setLeaderboardTotals] = useState<ScoreTotal[]>([]);
  const [selectedPlayerId, setSelectedPlayerId] = useState<string | null>(null);
  // The leaderboard opens on the podium only — the full field is one tap away. With eight players
  // it otherwise pushes the hole's own scoring below the fold.
  const [leaderboardExpanded, setLeaderboardExpanded] = useState(false);
  const [leaderboardGamePoints, setLeaderboardGamePoints] = useState<Record<string, Array<{ gameId?: string; roundGamePoints: number; position?: number }>>>(
    {}
  );
  const [tourTotals, setTourTotals] = useState<Record<string, number>>({});
  // Tour-long Freeze state (from tour.leaderboard events) drives the frozen-hole
  // badge next to each player's score button.
  const [freezeSection, setFreezeSection] = useState<FreezeSection | null>(null);
  // Tour-long Eclectic state (same event source) drives the "points to beat"
  // badge left of the score button from the tour's second round onwards.
  const [eclecticSection, setEclecticSection] = useState<EclecticSection | null>(null);
  const [teams, setTeams] = useState<TeamSummary[]>([]);
  const [gameDefinitionTypes, setGameDefinitionTypes] = useState<Record<string, string>>({});
  const [gameDefinitionNames, setGameDefinitionNames] = useState<GameDefinitionSummary[]>([]);
  const [selectedGameDefinitionIds, setSelectedGameDefinitionIds] = useState<string[]>(gameDefinitionIdsFromParams);
  const [holesCount, setHolesCount] = useState(18);
  const [holeEntries, setHoleEntries] = useState<HoleEntry[]>([]);
  const [allHoleEntries, setAllHoleEntries] = useState<HoleEntry[]>([]);
  const [allHoleEntriesLoadedRoundId, setAllHoleEntriesLoadedRoundId] = useState<string | null>(null);
  const [loggedInPlayerId, setLoggedInPlayerId] = useState<string | null>(null);
  // null until the player picks a ball themselves; see activeGamesBall for the default.
  const [selectedGamesBall, setSelectedGamesBall] = useState<number | null>(null);
  const enhancedInputEnabled = useEnhancedInput(apiBaseUrl, user?.id, user?.token);
  const [holeMenuOpen, setHoleMenuOpen] = useState(false);
  const [socketClient, setSocketClient] = useState<BffWebSocketClient | null>(null);
  const isolatedSocketRef = useRef<BffWebSocketClient | null>(null);
  if (!isolatedSocketRef.current) {
    isolatedSocketRef.current = createBffWebSocket(apiBaseUrl);
  }
  const [gameResults, setGameResults] = useState<GameResultState>({});
  const [serverGameExtras, setServerGameExtras] = useState<ServerGameExtras>({});
  const [strokeIndex, setStrokeIndex] = useState<number | null>(null);
  const [holeMeta, setHoleMeta] = useState<Record<number, HoleMeta>>(() => (roundId ? roundHoleMetaCache[roundId] ?? {} : {}));
  const [holePar, setHolePar] = useState<number | null>(null);
  const hasSelectedGames = selectedGameDefinitionIds.length > 0;

  useEffect(() => {
    setDebugEnabled(debug);
  }, [debug, setDebugEnabled]);

  useEffect(() => {
    if (!roundId) return;
    setHoleViewTraceId(generateTraceId());
  }, [roundId, holeNumber, setHoleViewTraceId]);
  const pendingAutoAdvanceHoleKeyRef = useRef<string | null>(null);
  // Hole the user just scored live on this device — the leaderboard-change modal only fires
  // for this hole, so it never shows when re-entering an already-scored or previous hole.
  const liveScoredHoleKeyRef = useRef<string | null>(null);
  const baselineRoundRankByHoleRef = useRef<Record<string, Record<string, RankSnapshotEntry>>>({});
  const shownLeaderboardChangeModalByHoleRef = useRef<Record<string, boolean>>({});
  const currentRoundRankSnapshotRef = useRef<Record<string, RankSnapshotEntry>>({});
  // Course-mapping GPS capture. Android-only and consent-gated — this is a no-op on web
  // and iOS, and on Android it still does nothing until the player opts in and the course
  // is one that still needs surveying.
  useCourseTrace({
    apiBaseUrl,
    token: user?.token,
    roundId,
    holeNumber,
    isRoundActive: !isAnonymousReadOnlyMode && !isInviteViewer,
  });
  // Minute-level battery sampling, sealed hourly and shipped over the same socket. Android-only
  // and needs no consent — it collects no location and no player identity. What it measures is
  // WHOLE-DEVICE battery over the round, annotated with what the app was doing.
  useBatteryTelemetry({
    socketClient,
    roundId,
    holeNumber,
    isRoundActive: !isAnonymousReadOnlyMode && !isInviteViewer,
  });

  const roundIdRef = useRef(roundId);
  const lastSyncedRoundIdRef = useRef<string | null>(null);
  const lastSyncedHoleRef = useRef<number | null>(null);
  const tourIdRef = useRef(tourId);
  const holeNumberRef = useRef(holeNumber);
  const holesCountRef = useRef(holesCount);
  // Cross-device hole sync. A user's devices share one "current hole" per round; only the
  // user's own navigations and scores on this device are recorded as that position, while a
  // follow of another device or of the persisted position is passive and never recorded — so
  // an opening device cannot drag the user back to hole 1. The state lives in the holeSync
  // session registry rather than in refs, because this screen remounts on every hole change.
  const lastActiveViewKeyRef = useRef<string | null>(null);
  const navigateToHoleRef = useRef<(hole: number, options?: { passive?: boolean }) => void>(() => {});
  const debugRef = useRef(false);
  const [isClient, setIsClient] = useState(Platform.OS !== 'web');
  const [showHoleDetails, setShowHoleDetails] = useState(false);
  const [eventCounts, setEventCounts] = useState<Record<string, { water: number; penalty: number; bunker: number; threePutt: number; chipIn: number; out: number }>>({});
  const hazardOptimisticLockUntilRef = useRef<Record<string, number>>({});
  // Shot detail the user opted into on the profile screen. Keyed `${playerId}:${hole}`; a field
  // is null until it is set, which is what keeps an untouched hole out of the statistics.
  const [enhancedValues, setEnhancedValues] = useState<Record<string, EnhancedHoleValues>>({});
  const enhancedOptimisticLockUntilRef = useRef<Record<string, number>>({});
  const [roundSubgames, setRoundSubgames] = useState<RoundSubgameEntry[]>([]);
  const [subgames, setSubgames] = useState<Subgame[]>([]);
  const [mysteryModalOpen, setMysteryModalOpen] = useState(false);
  const [nextMysteryModalOpen, setNextMysteryModalOpen] = useState(false);
  const [nextMysteryModalRows, setNextMysteryModalRows] = useState<Array<{ subgame: string; player: string }>>([]);
  const [holeEvents, setHoleEvents] = useState<HoleEventEntry[]>([]);
  const [scorePopupPlayerId, setScorePopupPlayerId] = useState<string | null>(null);
  const [scorePopupEightPlusOpen, setScorePopupEightPlusOpen] = useState(false);
  // Confirmation before overwriting a score that was already set when the popup opened.
  const [pendingScoreChange, setPendingScoreChange] = useState<{
    player: Player;
    value: string;
    currentValue: string;
  } | null>(null);
  const scorePopupInitialScoreRef = useRef<string | null>(null);
  const [leaderboardChangeModalOpen, setLeaderboardChangeModalOpen] = useState(false);
  const [holeViewOpen, setHoleViewOpen] = useState(false);
  /**
   * The hole the aerial view's ‹ › arrows have stepped to, when it differs from the hole
   * being scored. Kept separate from `holeNumber` on purpose: that value drives the route,
   * the header badges and score entry, and `navigateToHole` — the way every other "next
   * hole" control in this screen changes it — replaces the URL, which tears down this modal
   * along with the rest of the screen. Stepping the arrows must only change which hole's
   * imagery is being previewed, not leave the screen the player is scoring on.
   */
  const [holeViewStep, setHoleViewStep] = useState<number | null>(null);
  const holeViewHoleNumber = holeViewStep ?? holeNumber;
  // Starts fresh at the hole actually being played each time the view is reopened, rather
  // than resuming wherever a previous visit's arrows left off.
  useEffect(() => {
    if (!holeViewOpen) setHoleViewStep(null);
  }, [holeViewOpen]);
  // The aerial hole view is open to every signed-in player; the token is what the geometry
  // lookup needs. Courses that course mapping has not reached yet resolve to `unavailable`,
  // which the view reports rather than drawing an empty hole.
  const canUseHoleView = !!user?.token;
  // Per-device opt-in (profile > Distance to green) for a live distance-to-green badge in the
  // header, independent of the hole view modal being open.
  const showDistanceEnabled = useShowDistanceEnabled();
  // Per-device opt-out (profile > Hazard animations) for the animated critters in the leaderboard.
  const hazardAnimationsEnabled = useHazardAnimationsEnabled();
  // Gated on the modal being open, or on the header badge needing a live fix, so the course
  // and feature lookups never run for the players who use neither. Keyed on the *stepped*
  // hole so the arrows re-fetch imagery for whichever hole is being previewed.
  const holeViewGeometry = useHoleViewGeometry({
    apiBaseUrl,
    token: user?.token,
    roundId,
    holeNumber: holeViewHoleNumber,
    enabled: canUseHoleView && (holeViewOpen || showDistanceEnabled),
  });
  // Positioning runs only while the view is open or the header badge is on — see the hook for
  // why it is not tied to the round.
  const playerPosition = usePlayerPosition({ enabled: canUseHoleView && (holeViewOpen || showDistanceEnabled) });
  const headerDistanceUnit = useDistanceUnit();
  const distanceToGreenMeters =
    showDistanceEnabled && playerPosition.position && holeViewGeometry.green
      ? distanceMeters(playerPosition.position, holeViewGeometry.green)
      : null;
  const distanceToGreenLabel =
    distanceToGreenMeters != null ? formatHoleDistance(distanceToGreenMeters, headerDistanceUnit) : null;
  // Which way the phone is pointing, so the player marker can show what it is aimed at.
  const deviceHeading = useDeviceHeading({ enabled: canUseHoleView && holeViewOpen });
  // Wind is reported for the hole, not the player — the request carries the course's rough
  // position and never the player's own fix.
  const holeWind = useHoleWind({
    enabled: canUseHoleView && holeViewOpen,
    at: holeViewGeometry.green,
  });
  // Terrain heights along the hole, for the climb or drop to the green. Only once the course
  // has been resolved, and only for courses the height model actually covers.
  const holeElevation = useHoleElevation({
    apiBaseUrl,
    token: user?.token,
    courseId: holeViewGeometry.courseId,
    holeNumber: holeViewHoleNumber,
    enabled: canUseHoleView && holeViewOpen,
  });
  // The coach mark renders its own full-screen Modal; react-native-web aria-hides sibling
  // modals beneath the most recently mounted one, so keep it off while a dialog is open.
  const anyDialogOpen =
    !!scorePopupPlayerId ||
    !!selectedPlayerId ||
    mysteryModalOpen ||
    nextMysteryModalOpen ||
    (canUseHoleView && holeViewOpen) ||
    leaderboardChangeModalOpen;
  const {
    visible: showCompactScoreCoachMark,
    dismissForever: dismissCompactScoreCoachMarkForever,
    disableAll: disableAllCoachMarks,
  } = useCoachMark(
    apiBaseUrl,
    holeCompactScoreCoachMarkKey,
    isFocused && !compactScoreCoachMarkDismissed && players.length > 0 && !anyDialogOpen
  );
  // The hole whose change the modal is celebrating; rows are derived live from current standings.
  const [leaderboardChangeHoleKey, setLeaderboardChangeHoleKey] = useState<string | null>(null);
  // While set, a leaderboard-change decision is pending (debouncing until standings settle).
  const [leaderboardDecisionPendingKey, setLeaderboardDecisionPendingKey] = useState<string | null>(null);
  const scorePopupSheetTranslateY = useRef(new Animated.Value(420)).current;
  const [activeHoleDataKey, setActiveHoleDataKey] = useState<string | null>(null);
  const hasExactHoleData = !!currentHoleDataKey && activeHoleDataKey === currentHoleDataKey;

  useEffect(() => {
    const socket = isolatedSocketRef.current;
    return () => {
      socket?.dispose();
      isolatedSocketRef.current = null;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    loadClaimSession()
      .catch(() => null)
      .finally(() => {
        if (!cancelled) {
          setClaimSession(getActiveClaimSessionSync());
          setClaimSessionLoaded(true);
        }
      });
    const unsubscribe = subscribeClaimSession((nextClaimSession) => {
      if (cancelled) {
        return;
      }

      setClaimSession(nextClaimSession);
      setClaimSessionLoaded(true);
    });
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (tourId) {
      setCurrentTourId(tourId, { preserveRound: true });
    }
  }, [tourId]);

  useEffect(() => {
    const nextRoundId = roundIdFromParams ?? roundIdFromPath;
    if (nextRoundId && nextRoundId !== lastSyncedRoundIdRef.current) {
      lastSyncedRoundIdRef.current = nextRoundId;
      if (nextRoundId !== storedRoundId) {
        setRoundId(nextRoundId);
      }
    }
  }, [roundIdFromParams, roundIdFromPath, setRoundId, storedRoundId]);

  useEffect(() => {
    const nextHole = holeFromParams ?? holeFromPath;
    if (nextHole && nextHole !== lastSyncedHoleRef.current) {
      lastSyncedHoleRef.current = nextHole;
      if (nextHole !== storedHole) {
        setHole(nextHole);
      }
    }
  }, [holeFromParams, holeFromPath, setHole, storedHole]);

  useEffect(() => {
    if (roundId && storedHole == null && !holeFromParams && !holeFromPath) {
      setHole(1);
    }
  }, [holeFromParams, holeFromPath, roundId, setHole, storedHole]);

  useEffect(() => {
    if (!scorePopupPlayerId) {
      setScorePopupEightPlusOpen(false);
      setPendingScoreChange(null);
      scorePopupInitialScoreRef.current = null;
    }
  }, [scorePopupPlayerId]);

  useEffect(() => {
    if (!scorePopupPlayerId) return;
    scorePopupSheetTranslateY.setValue(420);
    Animated.timing(scorePopupSheetTranslateY, {
      toValue: 0,
      duration: 220,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: true,
    }).start();
  }, [scorePopupPlayerId, scorePopupSheetTranslateY]);

  const closeScorePopup = useCallback(() => {
    if (!scorePopupPlayerId) return;
    setScorePopupEightPlusOpen(false);
    Animated.timing(scorePopupSheetTranslateY, {
      toValue: 420,
      duration: 180,
      easing: Easing.in(Easing.cubic),
      useNativeDriver: true,
    }).start(() => {
      setScorePopupPlayerId(null);
    });
  }, [scorePopupPlayerId, scorePopupSheetTranslateY]);

  useHoleRealtime({
    socket: isolatedSocketRef.current,
    userToken: user?.token,
    claimClientId: effectiveClaimClientId,
    claimAccessToken: claimSession?.claimAccessToken ?? null,
    // A viewer's token comes from the route, a claim guest's from the stored session. Either way it
    // rides on the handshake as ?inviteToken=&anonymous=true, which is what gets the BFF to admit
    // the connection to the invited tour's rounds.
    inviteToken: viewerInviteToken || claimSession?.inviteToken || null,
    allowAnonymous: isAnonymousReadOnlyMode,
    setSocketClient,
  });

  useEffect(() => {
    if (!isNavigationReady || !isFocused) return;
    const socket = isolatedSocketRef.current;
    if (!socket) return;
    socket.setSubscriptionContext({ roundId, tourId: tourId ?? null });
  }, [isFocused, isNavigationReady, roundId, tourId]);

  // Tour-long games (Freeze) only travel on tour.leaderboard events, which the
  // BFF delivers to a round-joined connection solely when it is also subscribed
  // to the tour. The tour id resolves async after the round is joined, so
  // request the tour leaderboard as soon as it is known; reconnects are covered
  // by resubscribeRound. Guest claim mode requests it with the claim session.
  useEffect(() => {
    if (isGuestClaimMode || !tourId) return;
    if (!socketClient?.isConnected()) return;
    socketClient.sendTourLeaderboardRequest({ tourId, requestId: generateTraceId() });
  }, [isGuestClaimMode, socketClient, tourId]);

  // Seed the Freeze and Eclectic sections from cache so their badges show
  // without waiting for the websocket round trip; the live response overwrites.
  useEffect(() => {
    if (!tourId || !roundId) return;
    const cachedFreeze = freezeSectionCacheByTour.get(tourId);
    if (cachedFreeze) {
      setFreezeSection((prev) => prev ?? cachedFreeze);
    }
    const cachedEclectic = eclecticSectionCacheByTour.get(tourId);
    if (cachedEclectic) {
      setEclecticSection((prev) => prev ?? cachedEclectic);
    }
    if (cachedFreeze && cachedEclectic) return;
    let cancelled = false;
    readRoundCache(user?.id, roundId)
      .then((persisted) => {
        if (cancelled || !persisted) return;
        if (!cachedFreeze) {
          const normalized = normalizeFreezeSection(persisted.freeze);
          if (normalized) {
            freezeSectionCacheByTour.set(tourId, normalized);
            setFreezeSection((prev) => prev ?? normalized);
          }
        }
        if (!cachedEclectic) {
          const normalized = normalizeEclecticSection(persisted.eclectic);
          if (normalized) {
            eclecticSectionCacheByTour.set(tourId, normalized);
            setEclecticSection((prev) => prev ?? normalized);
          }
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [roundId, tourId, user?.id]);

  useEffect(() => {
    setNextMysteryModalOpen(false);
    setNextMysteryModalRows([]);
  }, [holeNumber]);

  useEffect(() => {
    roundIdRef.current = roundId;
    tourIdRef.current = tourId;
    holeNumberRef.current = holeNumber;
    holesCountRef.current = holesCount;
    debugRef.current = debug;
  }, [debug, holeNumber, holesCount, roundId, tourId]);

  useEffect(() => {
    if (!tourId || !user?.token) return;
    const loadTourTotals = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/tours/${tourId}/leaderboard`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        if (!response.ok) return;
        const payload = (await response.json()) as TourLeaderboardResponse & {
          Totals?: Array<{ PlayerId?: string; TotalPoints?: number }>;
        };
        const normalized =
          payload.totals ??
          payload.Totals?.map((t) => ({
            playerId: t.PlayerId ?? '',
            totalPoints: t.TotalPoints ?? 0,
          })) ??
          [];
        const map: Record<string, number> = {};
        normalized.forEach((t) => {
          if (!t.playerId) return;
          map[t.playerId] = t.totalPoints ?? 0;
          const match = playersRef.current.find((p) => p.id === t.playerId);
          if (match?.name) map[match.name] = t.totalPoints ?? 0;
        });
        setTourTotals(map);
      } catch {
        // ignore
      }
    };
    loadTourTotals();
  }, [apiBaseUrl, tourId, user?.token]);
  const [birdsVisible, setBirdsVisible] = useState(false);
  const birdieScale = useRef(new Animated.Value(0.05)).current;
  const birdieRotate = useRef(new Animated.Value(0)).current;
  const birdieOpacity = useRef(new Animated.Value(1)).current;
  const hasSyncedRoundRef = useRef(false);
  const lastHoleViewKeyRef = useRef<string | null>(null);
  const lastHoleViewSentAtRef = useRef<number>(0);
  const socketConnectionEpochRef = useRef(0);
  const lastHoleViewEpochRef = useRef(-1);
  const playersRef = useRef<Player[]>([]);
  const selectedGameDefinitionIdsRef = useRef<string[]>([]);
  // Every id the game-definition service knows. Used to tell a deselected game apart from a
  // derived id the round never selected (Nassau segments), which must survive the prune below.
  const knownGameDefinitionIdsRef = useRef<Set<string>>(new Set());
  const logToBff = useMemo(
    () =>
      createAppLogger(apiBaseUrl, user?.token, {
        roundId,
        holeNumber,
        tourId,
        source: 'hole-ui',
        traceId: holeViewTraceId ?? undefined,
      }, isolatedSocketRef.current),
    [apiBaseUrl, holeNumber, holeViewTraceId, roundId, tourId, user?.token]
  );

  useEffect(() => {
    playersRef.current = players;
  }, [players]);
  useEffect(() => {
    selectedGameDefinitionIdsRef.current = selectedGameDefinitionIds;
  }, [selectedGameDefinitionIds]);
  useEffect(() => {
    knownGameDefinitionIdsRef.current = new Set(
      gameDefinitionNames
        .map((definition) => definition.id)
        .filter((id): id is string => typeof id === 'string' && id.length > 0)
    );
  }, [gameDefinitionNames]);
  useEffect(() => {
    if (Platform.OS === 'web') {
      setIsClient(true);
    }
  }, []);
  useEffect(() => {
    if (birdsVisible) {
      birdieScale.setValue(0.05);
      birdieRotate.setValue(0);
      birdieOpacity.setValue(1);
    }
  }, [birdsVisible, birdieOpacity, birdieRotate, birdieScale]);
  useEffect(() => {
    if (!isNavigationReady) return;
  }, [isNavigationReady]);

  const navigation = useNavigation();
  useLayoutEffect(() => {
    const parInTitle = holePar ?? holeMeta[holeNumber]?.par ?? '–';
    const hcpInTitle = strokeIndex ?? holeMeta[holeNumber]?.strokeIndex ?? '–';
    const holeTitle = `${t('hole.holeLabel')} ${holeNumber}`;
    const parTitle = t('hole.parBadge', { par: parInTitle });
    const hcpTitle = t('hole.hcpIndexBadge', { hcpIndex: hcpInTitle });
    const distanceTitle = distanceToGreenLabel
      ? t('hole.distanceToGreenBadge', { distance: distanceToGreenLabel })
      : null;
    const headerTitle = () => (
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
        <Text style={{ color: '#E8EFD7', fontSize: 18, fontWeight: '700' }}>{holeTitle}</Text>
        <View
          style={{
            paddingHorizontal: 8,
            paddingVertical: 2,
            borderRadius: 999,
            borderWidth: 1,
            borderColor: 'rgba(161, 212, 147, 0.45)',
            backgroundColor: 'rgba(20, 62, 31, 0.6)',
          }}
        >
          <Text style={{ color: '#C6E3B8', fontSize: 11, fontWeight: '700' }}>{parTitle}</Text>
        </View>
        <View
          style={{
            paddingHorizontal: 8,
            paddingVertical: 2,
            borderRadius: 999,
            borderWidth: 1,
            borderColor: 'rgba(161, 212, 147, 0.45)',
            backgroundColor: 'rgba(20, 62, 31, 0.6)',
          }}
        >
          <Text style={{ color: '#C6E3B8', fontSize: 11, fontWeight: '700' }}>{hcpTitle}</Text>
        </View>
        {distanceTitle ? (
          <Pressable
            testID="hole-header-distance-badge"
            accessibilityRole="button"
            accessibilityLabel={t('hole.holeViewTitle', { hole: holeNumber })}
            onPress={() => setHoleViewOpen(true)}
            style={{
              paddingHorizontal: 8,
              paddingVertical: 2,
              borderRadius: 999,
              borderWidth: 1,
              borderColor: 'rgba(161, 212, 147, 0.45)',
              backgroundColor: 'rgba(20, 62, 31, 0.6)',
            }}
          >
            <Text style={{ color: '#C6E3B8', fontSize: 11, fontWeight: '700' }}>{distanceTitle}</Text>
          </Pressable>
        ) : null}
      </View>
    );
    // Photo capture lives here rather than on the gallery screen: this is where players are when
    // something worth photographing happens, and the round and hole tag themselves from context.
    // The button owns all of its own state — upload progress lives in a module-scoped queue — so
    // advancing to the next hole cannot cancel an upload in flight.
    // setOptions replaces the global headerRight wholesale, so the avatar menu (profile,
    // statistics, friends, language, logout) has to be re-rendered here or it disappears for
    // the whole round. The bug-report modal lives in RootLayoutNav, hence requestBugReport.
    const headerRight = () => (
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
        {/* Capture belongs to people playing the round, not to someone watching it. */}
        {canWrite ? (
          <HolePhotoButton tourId={tourId} roundId={roundId} holeNumber={holeNumber} token={user?.token} />
        ) : null}
        <HeaderUser hideProfileAndFriends={!user?.token} onOpenBugReport={requestBugReport} />
      </View>
    );
    if (dir === '-1') {
      navigation.setOptions({ animation: 'slide_from_left', headerTitle, headerRight });
    } else {
      navigation.setOptions({ animation: 'slide_from_right', headerTitle, headerRight });
    }
    // canWrite belongs here: a claim session loads asynchronously, so it flips after mount and the
    // header has to be rebuilt for the photo button to appear.
  }, [
    canWrite,
    dir,
    distanceToGreenLabel,
    holeMeta,
    holeNumber,
    holePar,
    navigation,
    roundId,
    strokeIndex,
    t,
    tourId,
    user?.token,
  ]);

  useLayoutEffect(() => {
    setExplicitScoreSelections({});
    setActiveHoleDataKey(null);
  }, [holeNumber, roundId]);

  useEffect(() => {
    setHoleMeta(roundId ? roundHoleMetaCache[roundId] ?? {} : {});
  }, [roundId]);

  useEffect(() => {
    if (roundId && gameResultsCache[roundId]) {
      setGameResults(gameResultsCache[roundId]);
    } else {
      setGameResults({});
    }
    setServerGameExtras(roundId ? serverGameExtrasCache[roundId] ?? {} : {});
    if (roundId && roundTotalsCache[roundId]) {
      setTotals(roundTotalsCache[roundId]);
    } else {
      setTotals([]);
    }
    if (roundId && roundLeaderboardCache[roundId]) {
      const cached = roundLeaderboardCache[roundId];
      setLeaderboardTotals(cached);
      const gamePointsMap: Record<string, Array<{ gameId?: string; roundGamePoints: number; position?: number }>> = {};
      cached.forEach((total) => {
        if (!total.playerId || !total.roundGamePoints) return;
        gamePointsMap[total.playerId] = total.roundGamePoints;
      });
      setLeaderboardGamePoints(gamePointsMap);
      const tourPointsMap: Record<string, number> = {};
      cached.forEach((total) => {
        if (total.tourGamesPoints === undefined) return;
        if (total.playerId) {
          tourPointsMap[total.playerId] = total.tourGamesPoints;
        }
        if (total.playerName) {
          tourPointsMap[total.playerName] = total.tourGamesPoints;
        }
      });
      if (Object.keys(tourPointsMap).length > 0) {
        setTourTotals((prev) => ({ ...prev, ...tourPointsMap }));
      }
    } else {
      setLeaderboardTotals([]);
      setLeaderboardGamePoints({});
    }
  }, [roundId]);

  useEffect(() => {
    if (!apiBaseUrl || !user?.token) {
      setGameDefinitionTypes({});
      return;
    }
    let cancelled = false;
    const fetchDefinitions = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/game-definitions`, {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        });
        if (!response.ok) return;
        const payload = (await response.json()) as GameDefinitionSummary[];
        patchUserCache(user?.id ?? null, { gameDefinitions: payload });
        if (cancelled) return;
        applyGameDefinitions(payload);
      } catch {
        // Offline fallback: last-known game definitions from durable cache.
        const cached = await readUserCache(user?.id ?? null).catch(() => null);
        if (cancelled) return;
        if (cached?.gameDefinitions && cached.gameDefinitions.length > 0) {
          applyGameDefinitions(cached.gameDefinitions);
        } else {
          setGameDefinitionTypes({});
          setGameDefinitionNames([]);
        }
      }
    };
    const applyGameDefinitions = (payload: GameDefinitionSummary[]) => {
      const next: Record<string, string> = {};
      payload.forEach((def) => {
        if (!def?.id) return;
        const type = def.competitionType?.toLowerCase();
        if (type) {
          next[def.id] = type;
        }
      });
      setGameDefinitionTypes(next);
      setGameDefinitionNames(payload);
    };
    fetchDefinitions();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, user?.id, user?.token]);
  useEffect(() => {
    if (gameDefinitionIdsFromParams.length > 0) {
      setSelectedGameDefinitionIds(gameDefinitionIdsFromParams);
    }
  }, [gameDefinitionIdsFromParams]);

  useEffect(() => {
    if (gameDefinitionNames.length === 0) return;
    const allowedIds = new Set(
      gameDefinitionNames
        .map((definition) => definition.id)
        .filter((id): id is string => typeof id === 'string' && id.trim().length > 0)
    );
    setSelectedGameDefinitionIds((prev) => {
      const filtered = prev.filter((id) => allowedIds.has(id));
      if (filtered.length === prev.length) {
        return prev;
      }
      return filtered;
    });
  }, [gameDefinitionNames]);

  const applyHandicapsFromMap = useCallback((mapObj: Record<string, unknown>) => {
    const map = new Map<string, number>();
    Object.entries(mapObj ?? {}).forEach(([k, v]) => {
      const num = typeof v === 'number' ? v : typeof v === 'string' ? parseInt(v, 10) : undefined;
      if (!Number.isFinite(num as number)) return;
      map.set(k, num as number);
    });
    if (map.size === 0) return;
    setPlayers((prev) => {
      let changed = false;
      const next = prev.map((p) => {
        const h = map.get(p.id) ?? map.get(p.name);
        if (h !== undefined && h !== p.handicap) {
          changed = true;
          return { ...p, handicap: h };
        }
        return p;
      });
      map.forEach((hcp, key) => {
        if (!next.some((p) => p.id === key || p.name === key)) {
          next.push({ id: key, name: key, createdAt: '', handicap: hcp });
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, []);

  const applyHandicapsFromEntries = useCallback((entries: HoleEntry[]) => {
    const map: Record<string, number> = {};
    entries.forEach((e) => {
      if (e.playingHandicap !== null && e.playingHandicap !== undefined) {
        const key = e.playerId ?? e.playerName;
        if (key) {
          map[key] = e.playingHandicap;
        }
      }
    });
    if (Object.keys(map).length === 0) return;
    applyHandicapsFromMap(map);
  }, [applyHandicapsFromMap]);

  useEffect(() => {
    const meta = holeMeta[holeNumber];
    setHolePar(meta?.par ?? null);
    setStrokeIndex(meta?.strokeIndex ?? null);
  }, [holeMeta, holeNumber]);

  const setLoadedAllHoleEntries = useCallback((entries: HoleEntry[]) => {
    setAllHoleEntries(entries);
    setAllHoleEntriesLoadedRoundId(roundId ?? null);
  }, [roundId]);
  const allHoleEntriesLoadedForCurrentRound = !!roundId && allHoleEntriesLoadedRoundId === roundId;

  useLayoutEffect(() => {
    if (!currentHoleDataKey) return;
    if (!allHoleEntriesLoadedForCurrentRound) return;
    const exactHoleEntries = allHoleEntries.filter((entry) => entry.holeNumber === holeNumber);
    setHoleEntries(exactHoleEntries);
    if (exactHoleEntries.length > 0) {
      applyHandicapsFromEntries(exactHoleEntries);
    }
    setActiveHoleDataKey(currentHoleDataKey);
  }, [allHoleEntries, allHoleEntriesLoadedForCurrentRound, applyHandicapsFromEntries, currentHoleDataKey, holeNumber]);

  useEffect(() => {
    if (!hasExactHoleData) return;
    const mapped: Record<string, string> = {};
    holeEntries.forEach((e) => {
      const val = e.grossStrokes === null ? (e.pickup ? 'Pickup' : '') : String(e.grossStrokes);
      const key = e.playerId ?? e.playerName;
      if (key) {
        if (val) mapped[key] = val;
      }
      if (e.playerName) {
        if (val) mapped[e.playerName] = val;
      }
    });
    setScores(mapped);
  }, [hasExactHoleData, holeEntries, holeNumber]);

  useEffect(() => {
    if (!holeEvents.length) return;
    const hydratedCounts: Record<string, { water: number; penalty: number; bunker: number; threePutt: number; chipIn: number; out: number }> = {};
    holeEvents.forEach((event) => {
      if (event.holeNumber !== holeNumber) return;
      const hazardType = hazardTypeFromName(event.eventType);
      if (!hazardType) return;
      const playerIdNorm = normalizePlayerKey(event.playerId);
      const matchedPlayer = playersRef.current.find((player) => {
        const idNorm = normalizePlayerKey(player.id);
        const nameNorm = normalizePlayerKey(player.name);
        return playerIdNorm === idNorm || playerIdNorm === nameNorm;
      });
      const eventKeys = new Set<string>([
        `${event.playerId}:${holeNumber}`,
      ]);
      if (matchedPlayer?.id) {
        eventKeys.add(`${matchedPlayer.id}:${holeNumber}`);
      }
      if (matchedPlayer?.name) {
        eventKeys.add(`${matchedPlayer.name}:${holeNumber}`);
      }
      const firstExisting = Array.from(eventKeys)
        .map((key) => hydratedCounts[key])
        .find((value) => !!value);
      const current = firstExisting ?? { water: 0, penalty: 0, bunker: 0, threePutt: 0, chipIn: 0, out: 0 };
      if (hazardType === 'threeputt') {
        current.threePutt += 1;
      } else if (hazardType === 'water') {
        current.water += 1;
      } else if (hazardType === 'bunker') {
        current.bunker += 1;
      } else if (hazardType === 'penalty') {
        current.penalty += 1;
      } else if (hazardType === 'chipin') {
        current.chipIn += 1;
      } else if (hazardType === 'out') {
        current.out += 1;
      }
      eventKeys.forEach((key) => {
        hydratedCounts[key] = { ...current };
      });
    });
    if (Object.keys(hydratedCounts).length === 0) return;
    setEventCounts((prev) => {
      let changed = false;
      const next = { ...prev };
      const now = Date.now();
      Object.entries(hydratedCounts).forEach(([eventKey, counts]) => {
        const existing = next[eventKey];
        const lockUntil = hazardOptimisticLockUntilRef.current[eventKey] ?? 0;
        const shouldKeepOptimistic = !!existing && lockUntil > now;
        const candidate = shouldKeepOptimistic ? existing : counts;
        const differs =
          !existing ||
          existing.water !== candidate.water ||
          existing.penalty !== candidate.penalty ||
          existing.bunker !== candidate.bunker ||
          existing.threePutt !== candidate.threePutt ||
          existing.chipIn !== candidate.chipIn ||
          existing.out !== candidate.out;
        if (differs) {
          next[eventKey] = candidate;
          changed = true;
        }
      });
      Object.entries(hazardOptimisticLockUntilRef.current).forEach(([eventKey, lockUntil]) => {
        if (lockUntil <= now) {
          delete hazardOptimisticLockUntilRef.current[eventKey];
        }
      });
      return changed ? next : prev;
    });
  }, [holeEvents, holeNumber]);

  // Shot detail is a set-value, not a tally: each new value supersedes the previous row, so the
  // last event of a type wins. Server order is authoritative except while an optimistic write is
  // still in flight, matching how the hazard counters guard their own updates.
  useEffect(() => {
    const hydrated: Record<string, EnhancedHoleValues> = {};
    holeEvents.forEach((event) => {
      if (event.holeNumber !== holeNumber) return;
      const eventType = event.eventType?.trim().toLowerCase();
      if (!eventType || !ENHANCED_EVENT_TYPES.some((type) => type === eventType)) return;
      const playerId = event.playerId?.trim();
      if (!playerId) return;
      const key = `${playerId}:${holeNumber}`;
      const current = hydrated[key] ?? { ...EMPTY_ENHANCED_VALUES };
      if (eventType === 'fairway') {
        current.fairway = parseFairwayResult(event.eventValue);
      } else if (eventType === 'putts') {
        const parsed = Number.parseInt(event.eventValue ?? '', 10);
        current.putts = Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
      } else {
        current.gir = event.eventValue?.trim().toLowerCase() === 'hit';
      }
      hydrated[key] = current;
    });

    setEnhancedValues((prev) => {
      const now = Date.now();
      const next = { ...prev };
      let changed = false;
      const keys = new Set([...Object.keys(prev), ...Object.keys(hydrated)]);
      keys.forEach((key) => {
        if (!key.endsWith(`:${holeNumber}`)) return;
        if ((enhancedOptimisticLockUntilRef.current[key] ?? 0) > now) return;
        const candidate = hydrated[key] ?? EMPTY_ENHANCED_VALUES;
        const existing = next[key];
        if (
          !existing ||
          existing.fairway !== candidate.fairway ||
          existing.putts !== candidate.putts ||
          existing.gir !== candidate.gir
        ) {
          next[key] = candidate;
          changed = true;
        }
      });
      Object.entries(enhancedOptimisticLockUntilRef.current).forEach(([key, lockUntil]) => {
        if (lockUntil <= now) {
          delete enhancedOptimisticLockUntilRef.current[key];
        }
      });
      return changed ? next : prev;
    });
  }, [holeEvents, holeNumber]);

  const roundIdIsValid = useMemo(
    () => !!roundId && roundId !== '00000000-0000-0000-0000-000000000000',
    [roundId]
  );
  const formatLogMessage = useCallback((event: string, payload: Record<string, unknown>) => {
    const detailKeys = [
      'roundId',
      'tourId',
      'hole',
      'incomingRound',
      'expectedRound',
      'gameId',
      'playerId',
      'playerKey',
      'points',
      'games',
      'results',
      'entries',
      'totals',
      'joined',
      'requested',
      'type',
      'par',
      'strokeIndex',
      'updatedHoleNumber',
      'updatedPlayerId',
    ];
    const detailParts = detailKeys
      .filter((key) => key in payload)
      .map((key) => `${key}=${String(payload[key])}`);
    const messageText = typeof payload.message === 'string' ? payload.message : '';
    const prefix = messageText ? `${event} - ${messageText}` : event;
    return detailParts.length > 0 ? `${prefix} | ${detailParts.join(' ')}` : prefix;
  }, []);
  const baseLogContext = useMemo(
    () => ({ screen: 'hole', roundId, tourId, hole: holeNumber }),
    [roundId, tourId, holeNumber]
  );
  const logWithContext = useCallback(
    (event: string, payload: Record<string, unknown>, level?: 'debug' | 'warn' | 'error') => {
      const merged = { ...baseLogContext, ...payload };
      logToBff(formatLogMessage(event, merged), merged, level);
    },
    [baseLogContext, formatLogMessage, logToBff]
  );
  const logWithContextRef = useRef(logWithContext);
  useEffect(() => {
    logWithContextRef.current = logWithContext;
  }, [logWithContext]);
  const { navigateToHole, finishRound } = useHoleActions({
    debug,
    holeNumber,
    startHole,
    holesCount,
    roundId,
    // Deliberately narrower than canWrite: claim guests never finish a round (round.status.update
    // is not even in the BFF's anonymous allowlist). Spectators are excluded on top of that.
    canMutateRoundStatus: !isInviteViewer && !!user?.token,
    viewerRouteParams,
    router: {
      push: (path: string) => router.push(path as never),
      replace: (path: string) => router.replace(path as never),
    },
    selectedGameDefinitionIds,
    setHole,
    onNavigate: (targetHole, passive) => {
      if (!roundId) return;
      if (passive) {
        markPassiveHole(roundId, targetHole);
        return;
      }
      markLocalNavigation(roundId);
    },
    socketClient,
    logWithContext,
  });
  navigateToHoleRef.current = navigateToHole;
  const scorePopupPlayer = useMemo(
    () => players.find((player) => player.id === scorePopupPlayerId) ?? null,
    [players, scorePopupPlayerId]
  );
  const hazardCountKeysForPlayer = useCallback((player: Player) => {
    const keys = new Set<string>();
    const addKey = (value?: string | null) => {
      const trimmed = value?.trim();
      if (!trimmed) return;
      keys.add(`${trimmed}:${holeNumber}`);
    };
    addKey(player.id);
    addKey(player.name);
    return Array.from(keys);
  }, [holeNumber]);
  const resolveHazardCountsForPlayer = useCallback((player: Player | null) => {
    const empty = { water: 0, penalty: 0, bunker: 0, threePutt: 0, chipIn: 0, out: 0 };
    if (!player) return empty;
    let countsFromEvents = empty;
    let hasEventCounts = false;
    const keys = hazardCountKeysForPlayer(player);
    for (const key of keys) {
      const existing = eventCounts[key];
      if (existing) {
        countsFromEvents = existing;
        hasEventCounts = true;
        break;
      }
    }
    const keyNorms = new Set<string>();
    if (player.id) keyNorms.add(normalizePlayerKey(player.id));
    if (player.name) keyNorms.add(normalizePlayerKey(player.name));
    const readFromGame = (gameId: string) => {
      const game = gameResults[gameId];
      if (!game?.scorePoints) {
        return { value: 0, hasMatchingPlayer: false };
      }
      for (const [key, value] of Object.entries(game.scorePoints)) {
        if (keyNorms.has(normalizePlayerKey(key))) {
          const numericValue = typeof value === 'number' ? value : Number(value);
          return {
            value: Number.isFinite(numericValue) ? Math.max(0, numericValue) : 0,
            hasMatchingPlayer: true,
          };
        }
      }
      return { value: 0, hasMatchingPlayer: false };
    };
    const waterFromGame = readFromGame(HAZARD_GAME_META.water.gameId);
    const penaltyFromGame = readFromGame(HAZARD_GAME_META.penalty.gameId);
    const bunkerFromGame = readFromGame(HAZARD_GAME_META.bunker.gameId);
    const threePuttFromGame = readFromGame(HAZARD_GAME_META.threePutt.gameId);
    const chipInFromGame = readFromGame(HAZARD_GAME_META.chipIn.gameId);
    const outFromGame = readFromGame(HAZARD_GAME_META.out.gameId);
    const hasGameBackedHazards =
      waterFromGame.hasMatchingPlayer ||
      penaltyFromGame.hasMatchingPlayer ||
      bunkerFromGame.hasMatchingPlayer ||
      threePuttFromGame.hasMatchingPlayer ||
      chipInFromGame.hasMatchingPlayer ||
      outFromGame.hasMatchingPlayer;
    const derived = {
      water: waterFromGame.value,
      penalty: penaltyFromGame.value,
      bunker: bunkerFromGame.value,
      threePutt: threePuttFromGame.value,
      chipIn: chipInFromGame.value,
      out: outFromGame.value,
    };
    if (hasEventCounts) {
      return countsFromEvents;
    }
    if (hasGameBackedHazards) {
      return derived;
    }
    return empty;
  }, [eventCounts, gameResults, hazardCountKeysForPlayer]);

  // Bunker first, then water, then the rest — the order the animals rotate in on the score button
  // and beside the player's photo, so both slots tell the same story in the same sequence.
  const hazardBadgesForPlayer = useCallback(
    (player: Player) => {
      const counts = resolveHazardCountsForPlayer(player);
      const countByType: Record<HazardSymbolType, number> = {
        bunker: counts.bunker,
        water: counts.water,
        penalty: counts.penalty,
        out: counts.out,
        threeputt: counts.threePutt,
      };
      return hazardTypesInOrder(
        (Object.keys(countByType) as HazardSymbolType[]).filter((type) => countByType[type] > 0),
      ).map((type) => ({
        key: type,
        hazardType: type,
        label: t(HAZARD_CHIPS[type].labelKey),
        style: HAZARD_CHIPS[type].style,
        textStyle: HAZARD_CHIPS[type].textStyle,
      }));
    },
    [resolveHazardCountsForPlayer, t],
  );
  const updateCompactHazardCount = useCallback(
    (player: Player, field: 'water' | 'penalty' | 'bunker' | 'threePutt' | 'chipIn' | 'out', delta: number) => {
      let nextValue = 0;
      setEventCounts((prev) => {
        const next = { ...prev };
        const keys = hazardCountKeysForPlayer(player);
        const lockUntil = Date.now() + 1200;
        keys.forEach((key) => {
          hazardOptimisticLockUntilRef.current[key] = lockUntil;
        });
        const current = keys
          .map((key) => next[key])
          .find((value) => !!value) ?? { water: 0, penalty: 0, bunker: 0, threePutt: 0, chipIn: 0, out: 0 };
        nextValue = Math.max(0, current[field] + delta);
        const updated = { ...current, [field]: nextValue };
        keys.forEach((key) => {
          next[key] = updated;
        });
        return next;
      });
      if (roundIdIsValid && roundId) {
        const queueEntryId = createEventId();
        // Only positive deltas carry an eventId: the backend treats an undo's
        // eventId as "the exact row to remove", and the +/- buttons don't know
        // which row that is, so undo relies on the legacy latest-match removal.
        void sendOrQueue(
          socketClient,
          { subjectKey: offlineSubjectKey, roundId },
          'hole.event',
          queueEntryId,
          {
            type: 'hole.event',
            roundId,
            playerId: player.id,
            holeNumber,
            eventType: field,
            eventValue: null,
            eventDelta: delta,
            timestamp: new Date().toISOString(),
            ...(delta >= 0 ? { eventId: queueEntryId } : {}),
          },
          { playerName: player.name }
        );
        const hazardGame = HAZARD_GAME_META[field];
        if (hazardGame) {
          setGameResults((prev) => {
            const next = { ...prev };
            const existing = next[hazardGame.gameId] ?? { name: hazardGame.gameName, scorePoints: {} };
            const idKey = player.id;
            const nameKey = player.name;
            const currentValue =
              existing.scorePoints[idKey] ??
              existing.scorePoints[nameKey] ??
              0;
            const nextValue = currentValue + delta;
            existing.name = existing.name ?? hazardGame.gameName;
            existing.scorePoints = {
              ...existing.scorePoints,
              [idKey]: nextValue,
              [nameKey]: nextValue,
            };
            next[hazardGame.gameId] = existing;
            if (roundId) {
              gameResultsCache[roundId] = next;
            }
            return next;
          });
          setLeaderboardGamePoints((prev) => {
            const next = { ...prev };
            const existingList = [...(next[player.id] ?? [])];
            const idx = existingList.findIndex((entry) => (entry.gameId ?? '') === hazardGame.gameId);
            if (idx >= 0) {
              existingList[idx] = {
                ...existingList[idx],
                roundGamePoints: (existingList[idx].roundGamePoints ?? 0) + delta,
              };
            } else {
              existingList.push({
                gameId: hazardGame.gameId,
                roundGamePoints: delta,
              });
            }
            next[player.id] = existingList;
            return next;
          });
        }
      }
    },
    [hazardCountKeysForPlayer, offlineSubjectKey, roundId, roundIdIsValid, socketClient, holeNumber]
  );

  /**
   * Writes one shot-detail field. Unlike the hazard counters these replace rather than accumulate,
   * so the previous row is removed first: a negative delta with no eventValue drops the latest
   * matching (player, hole, type) row server-side, and no-ops when there is none. Passing null
   * clears the field.
   */
  const setEnhancedInputValue = useCallback(
    <TField extends keyof EnhancedHoleValues>(
      player: Player,
      field: TField,
      value: EnhancedHoleValues[TField]
    ) => {
      const key = `${player.id}:${holeNumber}`;
      setEnhancedValues((prev) => {
        const current = prev[key] ?? EMPTY_ENHANCED_VALUES;
        if (current[field] === value) return prev;
        enhancedOptimisticLockUntilRef.current[key] = Date.now() + 1200;
        return { ...prev, [key]: { ...current, [field]: value } };
      });

      if (!roundIdIsValid || !roundId) return;

      const queueContext = { subjectKey: offlineSubjectKey, roundId };
      const basePayload = {
        type: 'hole.event' as const,
        roundId,
        playerId: player.id,
        holeNumber,
        eventType: field,
      };

      void sendOrQueue(
        socketClient,
        queueContext,
        'hole.event',
        createEventId(),
        {
          ...basePayload,
          eventValue: null,
          eventDelta: -1,
          timestamp: new Date().toISOString(),
        },
        { playerName: player.name }
      );

      if (value === null) return;

      const eventValue =
        field === 'gir' ? (value ? 'hit' : 'miss') : String(value);
      const eventId = createEventId();
      void sendOrQueue(
        socketClient,
        queueContext,
        'hole.event',
        eventId,
        {
          ...basePayload,
          eventValue,
          eventDelta: 1,
          eventId,
          timestamp: new Date().toISOString(),
        },
        { playerName: player.name }
      );
    },
    [holeNumber, offlineSubjectKey, roundId, roundIdIsValid, socketClient]
  );

  useEffect(() => {
    if (!socketClient || !roundIdIsValid || !roundId) return;
    const holeViewKey = `${roundId}:${holeNumber}`;
    const connectionEpoch = socketConnectionEpochRef.current;
    const shouldSend =
      lastHoleViewKeyRef.current !== holeViewKey || lastHoleViewEpochRef.current !== connectionEpoch;
    if (!shouldSend) return;
    lastHoleViewKeyRef.current = holeViewKey;
    lastHoleViewEpochRef.current = connectionEpoch;
    lastHoleViewSentAtRef.current = Date.now();
    // Passive until the user navigates on this device: the first hole after opening, and any
    // follow of another device, resend the hole state but are not this user's position.
    const passive = consumePassiveHole(roundId, holeNumber) || !hasLocalPosition(roundId);
    if (!passive) lastActiveViewKeyRef.current = holeViewKey;
    socketClient.send({ type: 'hole.view', roundId, hole: holeNumber, deviceId: getDeviceId(), passive });
    logWithContextRef.current('hole.view.sent', { message: 'Sent hole.view request to BFF.', passive });
  }, [socketClient, roundIdIsValid, roundId, holeNumber]);

  useHoleData({
    apiBaseUrl,
    user: user ?? null,
    allowGuestClaimMode: isGuestClaimMode || shouldDeferAnonymousBootstrap,
    inviteToken: isInviteViewer ? viewerInviteToken : null,
    claimPlayerId,
    roundId,
    roundIdIsValid,
    isNavigationReady,
    setLoading,
    setError,
    setPlayers,
    setTourId,
    setTeams,
    setLoggedInPlayerId,
    setAllHoleEntries: setLoadedAllHoleEntries,
    gameDefinitionIdsFromParamsCount: gameDefinitionIdsFromParams.length,
    setSelectedGameDefinitionIds,
    setHolesCount,
    setStartHole,
    setRoundSubgames,
    setHoleEvents,
    setSubgames,
    logWithContext,
  });

  useEffect(() => {
    if (!isFocused || !isNavigationReady) return;
    if (!safePathname.includes('/hole/')) return;
    if (!holeMenuRequestVersion) return;
    setHoleMenuOpen((prev) => !prev);
    clearHoleMenuRequest();
  }, [clearHoleMenuRequest, holeMenuRequestVersion, isFocused, isNavigationReady, safePathname]);

  // The bottom menu owns the button; the view itself lives here, so the press arrives as a
  // request version. Toggling matches the hole menu: a second press closes it.
  useEffect(() => {
    if (!isFocused || !isNavigationReady) return;
    if (!safePathname.includes('/hole/')) return;
    if (!holeViewRequestVersion) return;
    if (canUseHoleView) setHoleViewOpen((prev) => !prev);
    clearHoleViewRequest();
  }, [
    canUseHoleView,
    clearHoleViewRequest,
    holeViewRequestVersion,
    isFocused,
    isNavigationReady,
    safePathname,
  ]);

  useEffect(() => {
    if (!roundId || !isNavigationReady) return;
    const client = isolatedSocketRef.current;
    if (!client) return;
    const cacheUserId = user?.id ?? null;
    const cacheFreezeSection = (section: FreezeSection) => {
      const sectionTourId = section.tourId || tourIdRef.current;
      if (sectionTourId) {
        freezeSectionCacheByTour.set(sectionTourId, section);
      }
      patchRoundCache(cacheUserId, roundId, { freeze: section });
    };
    const cacheEclecticSection = (section: EclecticSection) => {
      const sectionTourId = section.tourId || tourIdRef.current;
      if (sectionTourId) {
        eclecticSectionCacheByTour.set(sectionTourId, section);
      }
      patchRoundCache(cacheUserId, roundId, { eclectic: section });
    };

    const resubscribeRound = () => {
      const joined = client.send({ type: 'round.join', roundId });
      const joinedTour =
        isGuestClaimMode && !!claimSession?.tourId
          ? client.send({ type: 'tour.join', tourId: claimSession.tourId })
          : false;
      const requested = client.send({ type: 'round.requestState', roundId });
      // false = not attempted; otherwise the send() outcome (queued still counts as in flight).
      let requestedTourLeaderboard: SendOutcome | false = false;
      if (isGuestClaimMode && (claimSession?.tourId || claimSession?.inviteToken)) {
        requestedTourLeaderboard = client.sendTourLeaderboardRequest({
          tourId: claimSession?.tourId ?? undefined,
          inviteToken: claimSession.inviteToken ?? undefined,
          requestId: generateTraceId(),
        });
      } else if (!isGuestClaimMode && tourIdRef.current) {
        // Subscribes this connection to the tour so Freeze state keeps flowing
        // after reconnects (see the tourId effect above for the initial load).
        requestedTourLeaderboard = client.sendTourLeaderboardRequest({
          tourId: tourIdRef.current,
          requestId: generateTraceId(),
        });
      }
      hasSyncedRoundRef.current = true;
      logWithContextRef.current('hole.round.resubscribe', {
        message: 'Requested round.join and round.requestState.',
        joined,
        joinedTour,
        requested,
        requestedTourLeaderboard,
      });
    };
    const unsubscribeMessages = client.onMessage((evt) => {
      const evtType = (evt as { type?: string }).type;
      const currentHoleNumber = holeNumberRef.current;
      logWithContextRef.current(
        `hole.ws.message.${evtType ?? 'unknown'}`,
        { message: 'Received websocket message.', type: evtType },
        'debug'
      );
      if ((evt as { type?: string }).type === 'round.state') {
        logWithContextRef.current('hole.ws.round.state.raw', { message: 'Raw round.state payload.' }, 'debug');
      }
      if ((evt as HoleUpdateEvent).type === 'hole.update' && (evt as HoleUpdateEvent).roundId === roundId) {
        const holeEvent = evt as HoleUpdateEvent;
        logWithContextRef.current(
          'hole.event.update',
          {
            message: 'Applied hole.update event.',
            hole: holeEvent.hole,
            totals: holeEvent.totals?.length ?? 0,
            entries: holeEvent.entries?.length ?? 0,
            strokeIndex: holeEvent.strokeIndex,
            par: holeEvent.par,
          },
          'debug'
        );
        const playingHandicapsMap = (holeEvent as unknown as { playingHandicaps?: Record<string, unknown>; handicaps?: Record<string, unknown> }).playingHandicaps ??
          (holeEvent as unknown as { handicaps?: Record<string, unknown> }).handicaps;
        if (playingHandicapsMap) {
          applyHandicapsFromMap(playingHandicapsMap as Record<string, unknown>);
        }
        if (holeEvent.entries) {
          const filtered = holeEvent.entries
            .map((e) =>
              normalizeHoleEntry({
                ...e,
                holeNumber: holeEvent.hole,
                strokeIndex: holeEvent.strokeIndex ?? null,
              } as Record<string, unknown>)
            );
          setAllHoleEntries((prev) => {
            const merged = mergeHoleEntriesForHole(prev, holeEvent.hole, filtered);
            cacheRoundHoleEntriesSnapshot(apiBaseUrl, roundId, cacheUserId, merged);
            return merged;
          });
          setAllHoleEntriesLoadedRoundId(roundId);
          applyHandicapsFromEntries(filtered);
          const firstIdx =
            holeEvent.strokeIndex ??
            filtered.find((e) => e.strokeIndex !== undefined && e.strokeIndex !== null)?.strokeIndex ??
            null;
          setHoleMeta((prev) => {
            const current = prev[holeEvent.hole] ?? {};
            const strokeIndex = firstIdx ?? current.strokeIndex;
            const par = holeEvent.par ?? current.par;
            const next = { ...prev, [holeEvent.hole]: { ...current, strokeIndex, par } };
            if (roundId) {
              roundHoleMetaCache[roundId] = next;
            }
            return next;
          });
          if (holeEvent.hole === currentHoleNumber) {
            setHoleEntries(filtered);
            if (roundId) {
              setActiveHoleDataKey(`${roundId}:${currentHoleNumber}`);
            }
            setStrokeIndex(firstIdx);
            const mapped: Record<string, string> = {};
            filtered.forEach((e) => {
              const gross = e.grossStrokes ?? null;
              const val = gross === null ? (e.pickup ? 'Pickup' : '') : String(gross);
              const key = e.playerId ?? e.playerName;
              if (key && val) mapped[key] = val;
              if (e.playerName && val) mapped[e.playerName] = val;
            });
            setScores(mapped);
          }
        }
        if (holeEvent.totals) {
          setTotals(normalizeTotalsList(holeEvent.totals as ScoreTotal[], playersRef.current));
        }
      } else if ((evt as TourLeaderboardStateEvent).type === 'tour.leaderboard.state') {
        const leaderboardState = evt as TourLeaderboardStateEvent;
        // Guest claim mode seeds players and games from the tour leaderboard;
        // logged-in mode gets those from round events and must not let the
        // tour-wide player list replace the round's players. Both modes read
        // the Freeze section, which only exists on tour.leaderboard events.
        const incomingPlayers = isGuestClaimMode
          ? ((leaderboardState.players ?? []) as Array<Record<string, unknown>>)
          : [];
        if (incomingPlayers.length > 0) {
          const mappedPlayers = incomingPlayers
            .map((player) => {
              const id = String(player.id ?? player.Id ?? '').trim();
              if (!id) return null;
              const name = String(player.name ?? player.Name ?? '').trim();
              return {
                id,
                name: name || id,
                createdAt: String(player.createdAt ?? player.CreatedAt ?? '').trim(),
              } satisfies Player;
            })
            .filter((player): player is Player => player !== null);
          if (mappedPlayers.length > 0) {
            let merged = mappedPlayers;
            setPlayers((prev) => {
              merged = mergeSeededPlayers(prev, mappedPlayers);
              return merged;
            });
            cacheRoundPlayersSnapshot(apiBaseUrl, roundId, cacheUserId, {
              players: merged,
              tourId: leaderboardState.tourId,
            });
          }
        }
        const leaderboardGameIds = isGuestClaimMode
          ? extractRoundGameDefinitionIdsFromLeaderboardState(leaderboardState, roundId)
          : [];
        if (leaderboardGameIds.length > 0) {
          setSelectedGameDefinitionIds(leaderboardGameIds);
          cacheRoundMetadataSnapshot(apiBaseUrl, roundId, cacheUserId, { gameDefinitionIds: leaderboardGameIds });
        }
        const freezeState = normalizeFreezeSection(leaderboardState.freeze);
        if (freezeState) {
          setFreezeSection(freezeState);
          cacheFreezeSection(freezeState);
        }
        const eclecticState = normalizeEclecticSection(leaderboardState.eclectic);
        if (eclecticState) {
          setEclecticSection(eclecticState);
          cacheEclecticSection(eclecticState);
        }
      } else if ((evt as TourLeaderboardUpdateEvent).type === 'tour.leaderboard.update') {
        // Intentionally ignore score payloads here.
        // Hole page game results are driven by round/game events to keep claimed/logged-in parity.
        // The tour-long sections are the exception: Freeze and Eclectic only live
        // on tour.leaderboard events and feed the badges next to the score button.
        const freezeUpdate = normalizeFreezeSection((evt as TourLeaderboardUpdateEvent).freeze);
        if (freezeUpdate) {
          setFreezeSection(freezeUpdate);
          cacheFreezeSection(freezeUpdate);
        }
        const eclecticUpdate = normalizeEclecticSection((evt as TourLeaderboardUpdateEvent).eclectic);
        if (eclecticUpdate) {
          setEclecticSection(eclecticUpdate);
          cacheEclecticSection(eclecticUpdate);
        }
      } else if ((evt as RoundStateEvent).type === 'round.state' && (evt as RoundStateEvent).roundId === roundId) {
        const roundState = evt as RoundStateEvent;
        let roundStateHolesCount: number | undefined;
        if (Array.isArray(roundState.players) && roundState.players.length > 0) {
          const mappedPlayers = roundState.players
            .map((player) => ({
              id: String(player.id ?? '').trim(),
              name: String(player.name ?? '').trim(),
              createdAt: String(player.createdAt ?? '').trim(),
            }))
            .filter((player) => !!player.id)
            .map((player) => ({
              ...player,
              name: player.name || player.id,
              joinedAt: player.createdAt || undefined,
            })) as Player[];
          if (mappedPlayers.length > 0) {
            let merged = mappedPlayers;
            setPlayers((prev) => {
              merged = mergeSeededPlayers(prev, mappedPlayers);
              return merged;
            });
            cacheRoundPlayersSnapshot(apiBaseUrl, roundId, cacheUserId, { players: merged });
          }
        }
        const roundStateGameIds =
          ((roundState as unknown as { gameDefinitionIds?: string[]; GameDefinitionIds?: string[] }).gameDefinitionIds ??
            (roundState as unknown as { GameDefinitionIds?: string[] }).GameDefinitionIds ??
            [])
            .filter((id): id is string => typeof id === 'string' && isUuidLike(id));
        if (roundStateGameIds.length > 0) {
          setSelectedGameDefinitionIds(roundStateGameIds);
        }
        // holesCount arrives as its own field; the holes-array length is the legacy fallback.
        if (roundStateHolesCount === undefined && isValidHolesCount(roundState.holesCount)) {
          roundStateHolesCount = roundState.holesCount;
          setHolesCount(roundState.holesCount!);
        }
        const roundStateStartHole =
          typeof roundState.startHoleNumber === 'number' && roundState.startHoleNumber >= 1
            ? roundState.startHoleNumber
            : undefined;
        if (roundStateStartHole !== undefined) {
          // Through the store (stable setter) so the WS effect's dependency list is unchanged.
          useRoundState.getState().setStartHole(roundStateStartHole);
        }
        // The user's persisted "current hole" across their devices, applied once per entry into
        // the round and only when an entry point opened it: a deep link, a reload or a tap to
        // the next hole each name the hole they want, and that hole must win. Skipped too once
        // the user has taken a position on this device — then this device is the truth.
        if (roundId && consumeRoundEntry(roundId)) {
          const initialTarget = resolveInitialPositionTarget(roundState.myHole, {
            currentHole: holeNumberRef.current,
            userHasNavigated: hasLocalPosition(roundId),
            holesCount: holesCountRef.current,
          });
          if (initialTarget !== null) {
            logWithContextRef.current('hole.position.initial', { message: 'Opening on synced hole.', hole: initialTarget });
            navigateToHoleRef.current(initialTarget, { passive: true });
          }
        }
        if (roundState.holes) {
          if (roundStateHolesCount === undefined) {
            roundStateHolesCount = isValidHolesCount(roundState.holes.length) ? roundState.holes.length : undefined;
            if (roundStateHolesCount !== undefined) {
              setHolesCount(roundStateHolesCount);
            }
          }
          setHoleMeta((prev) => {
            const next = { ...prev };
            roundState.holes?.forEach((h) => {
              const prior = next[h.holeNumber] ?? {};
              const strokeIndex = h.strokeIndex ?? h.index ?? prior.strokeIndex;
              next[h.holeNumber] = {
                ...prior,
                par: h.par ?? prior.par,
                strokeIndex,
                name: h.name ?? prior.name,
                description: h.description ?? prior.description,
              };
            });
            if (roundId) {
              roundHoleMetaCache[roundId] = next;
            }
            return next;
          });
        }
        if (roundStateGameIds.length > 0 || roundStateHolesCount !== undefined || roundStateStartHole !== undefined) {
          cacheRoundMetadataSnapshot(apiBaseUrl, roundId, cacheUserId, {
            gameDefinitionIds: roundStateGameIds.length > 0 ? roundStateGameIds : undefined,
            holesCount: roundStateHolesCount,
            startHoleNumber: roundStateStartHole,
          });
        }
        if (roundState.holeEntries) {
          const normalizedEntries = roundState.holeEntries
            .map((h) => normalizeHoleEntry(h as Record<string, unknown>))
            .filter((entry) => entry.holeNumber > 0);
          setAllHoleEntries(normalizedEntries);
          setAllHoleEntriesLoadedRoundId(roundId);
          cacheRoundHoleEntriesSnapshot(apiBaseUrl, roundId, cacheUserId, normalizedEntries);
          const filtered = normalizedEntries.filter((h) => h.holeNumber === currentHoleNumber);
          setHoleEntries(filtered);
          applyHandicapsFromEntries(filtered);
          if (roundId) {
            setActiveHoleDataKey(`${roundId}:${currentHoleNumber}`);
          }
        }
        const rawHoleEvents =
          ((roundState as unknown as { holeEvents?: Array<Record<string, unknown>>; HoleEvents?: Array<Record<string, unknown>> }).holeEvents ??
            (roundState as unknown as { HoleEvents?: Array<Record<string, unknown>> }).HoleEvents);
        if (Array.isArray(rawHoleEvents)) {
          const normalizedHoleEvents = rawHoleEvents
            .map((event) => normalizeHoleEvent(event))
            .filter((event): event is HoleEventEntry => event !== null);
          setHoleEvents(normalizedHoleEvents);
          cacheRoundHoleEventsSnapshot(apiBaseUrl, roundId, cacheUserId, normalizedHoleEvents);
        }
        if (roundState.totals) {
          const normalizedTotals = normalizeTotalsList(roundState.totals as ScoreTotal[], playersRef.current);
          setTotals(normalizedTotals);
          if (roundId) {
            roundTotalsCache[roundId] = normalizedTotals;
          }
        }
      } else if ((evt as HoleInfoEvent).type === 'hole.info') {
        const info = evt as HoleInfoEvent;
        logWithContextRef.current(
          'hole.event.info',
          {
            message: 'Received hole.info.',
            hole: info.hole,
            par: info.par,
            strokeIndex: info.strokeIndex,
            name: info.name,
            lengthMeters: info.lengthMeters,
          },
          'debug'
        );
        setHoleMeta((prev) => {
          const current = prev[info.hole] ?? {};
          const next = {
            ...prev,
            [info.hole]: {
              ...current,
              par: info.par ?? current.par,
              strokeIndex: info.strokeIndex ?? current.strokeIndex,
              name: info.name ?? current.name,
              description: info.description ?? current.description,
              lengthMeters: info.lengthMeters ?? current.lengthMeters,
            },
          };
          if (roundId) {
            roundHoleMetaCache[roundId] = next;
          }
          return next;
        });
        if (info.hole === currentHoleNumber) {
          setHolePar((info.par ?? null) ?? null);
          setStrokeIndex((info.strokeIndex ?? null) ?? null);
        }
      } else if (((evt as HoleUpdateEvent).type === 'hole.update' || (evt as { type?: string }).type === 'hole.updated') && (evt as HoleUpdateEvent).roundId === roundId) {
        const holeEvent = evt as HoleUpdateEvent;
        logWithContextRef.current(
          'hole.event.updated',
          {
            message: 'Received hole.updated or secondary hole.update.',
            hole: holeEvent.hole,
            totals: holeEvent.totals?.length ?? 0,
            entries: holeEvent.entries?.length ?? 0,
            par: holeEvent.par,
            strokeIndex: holeEvent.strokeIndex,
          },
          'debug'
        );
        const entries =
          holeEvent.entries?.map((e) =>
            normalizeHoleEntry({
              ...e,
              holeNumber: holeEvent.hole,
              strokeIndex: holeEvent.strokeIndex ?? null,
            } as Record<string, unknown>)
          ) ?? [];
        setAllHoleEntries((prev) => {
          const merged = mergeHoleEntriesForHole(prev, holeEvent.hole, entries);
          cacheRoundHoleEntriesSnapshot(apiBaseUrl, roundId, cacheUserId, merged);
          return merged;
        });
        setAllHoleEntriesLoadedRoundId(roundId);
        if (holeEvent.par !== undefined || holeEvent.strokeIndex !== undefined) {
          setHoleMeta((prev) => {
            const current = prev[holeEvent.hole] ?? {};
            const next = {
              ...prev,
              [holeEvent.hole]: {
                ...current,
                par: holeEvent.par ?? current.par,
                strokeIndex: holeEvent.strokeIndex ?? current.strokeIndex,
              },
            };
            if (roundId) {
              roundHoleMetaCache[roundId] = next;
            }
            return next;
          });
        }
        if (holeEvent.hole === currentHoleNumber) {
          setHoleEntries(entries);
          applyHandicapsFromEntries(entries);
          if (roundId) {
            setActiveHoleDataKey(`${roundId}:${currentHoleNumber}`);
          }
          if (holeEvent.par !== undefined && holeEvent.par !== null) setHolePar(holeEvent.par);
          if (holeEvent.strokeIndex !== undefined && holeEvent.strokeIndex !== null) setStrokeIndex(holeEvent.strokeIndex);
        }
        if (holeEvent.totals) {
          const normalizedTotals = normalizeTotalsList(holeEvent.totals as ScoreTotal[], playersRef.current);
          setTotals(normalizedTotals);
          if (roundId) {
            roundTotalsCache[roundId] = normalizedTotals;
          }
        }
        // Ask for the latest round state to backfill any missed results/holes.
        if (client && roundId) {
          client.send({ type: 'round.requestState', roundId });
        }
      } else if ((evt as { type?: string }).type === 'round.leaderboard' && (evt as { roundId?: string }).roundId === roundId) {
        const lbTotals = (evt as { totals?: ScoreTotal[] }).totals;
        if (lbTotals) {
          const normalizedTotals = normalizeTotalsList(lbTotals as ScoreTotal[], playersRef.current);
          setLeaderboardTotals(normalizedTotals);
          const gamePointsMap: Record<string, Array<{ gameId?: string; roundGamePoints: number; position?: number }>> = {};
          normalizedTotals.forEach((total) => {
            if (!total.roundGamePoints) return;
            if (total.playerId) {
              gamePointsMap[total.playerId] = total.roundGamePoints;
            }
            if (total.playerName) {
              gamePointsMap[total.playerName] = total.roundGamePoints;
            }
          });
          setLeaderboardGamePoints(gamePointsMap);
          const tourPointsMap: Record<string, number> = {};
          normalizedTotals.forEach((total) => {
            if (total.tourGamesPoints === undefined) return;
            if (total.playerId) {
              tourPointsMap[total.playerId] = total.tourGamesPoints;
            }
            if (total.playerName) {
              tourPointsMap[total.playerName] = total.tourGamesPoints;
            }
          });
          if (Object.keys(tourPointsMap).length > 0) {
            setTourTotals((prev) => ({ ...prev, ...tourPointsMap }));
          }
          if (roundId) {
            roundLeaderboardCache[roundId] = normalizedTotals;
          }
        }
        if (client && roundId) {
          client.send({ type: 'round.requestState', roundId });
        }
      } else if (
        (evt as RoundPositionUpdatedEvent).type === 'round.position.updated' &&
        (evt as RoundPositionUpdatedEvent).roundId === roundId
      ) {
        // One of the user's OTHER devices moved (the BFF only delivers this to the user's own
        // connections): follow it, unless this is our own echo or we just navigated ourselves.
        const position = evt as RoundPositionUpdatedEvent;
        const target = resolveRemotePositionTarget(position, {
          deviceId: getDeviceId(),
          currentHole: holeNumberRef.current,
          lastLocalNavigationAt: lastLocalNavigationAt(roundId),
          now: Date.now(),
          holesCount: holesCountRef.current,
        });
        if (target !== null) {
          logWithContextRef.current('hole.position.follow', {
            message: 'Following another device to its hole.',
            hole: target,
            fromDeviceId: position.deviceId,
          });
          navigateToHoleRef.current(target, { passive: true });
        }
      } else if ((evt as { type?: string }).type === 'round.updated' && (evt as { roundId?: string }).roundId === roundId) {
        // Detected or explicitly set start hole; drives play-order navigation.
        const updatedStartHoleRaw =
          (evt as { startHoleNumber?: number; StartHoleNumber?: number }).startHoleNumber ??
          (evt as { StartHoleNumber?: number }).StartHoleNumber;
        const updatedStartHole =
          typeof updatedStartHoleRaw === 'number' && updatedStartHoleRaw >= 1 ? updatedStartHoleRaw : undefined;
        if (updatedStartHole !== undefined) {
          // Zustand setters are stable; read through the store so the WS effect's dependency
          // list stays as it was (it deliberately does not re-subscribe on store changes).
          useRoundState.getState().setStartHole(updatedStartHole);
          cacheRoundMetadataSnapshot(apiBaseUrl, roundId, cacheUserId, { startHoleNumber: updatedStartHole });
        }
        const updatedIds =
          (evt as { gameDefinitionIds?: string[]; GameDefinitionIds?: string[] }).gameDefinitionIds ??
          (evt as { GameDefinitionIds?: string[] }).GameDefinitionIds ??
          [];
        if (Array.isArray(updatedIds)) {
          const validIds = updatedIds.filter((id): id is string => typeof id === 'string' && isUuidLike(id));
          if (validIds.length > 0) {
            setSelectedGameDefinitionIds(validIds);
          }
          const updatedHolesCountRaw =
            (evt as { holesCount?: number; HolesCount?: number }).holesCount ??
            (evt as { HolesCount?: number }).HolesCount;
          const updatedHolesCount = isValidHolesCount(updatedHolesCountRaw) ? updatedHolesCountRaw : undefined;
          if (updatedHolesCount !== undefined) {
            setHolesCount(updatedHolesCount);
          }
          if (validIds.length > 0 || updatedHolesCount !== undefined) {
            cacheRoundMetadataSnapshot(apiBaseUrl, roundId, cacheUserId, {
              gameDefinitionIds: validIds.length > 0 ? validIds : undefined,
              holesCount: updatedHolesCount,
            });
          }
        }
      } else if ((evt as { type?: string }).type === 'game.result') {
        const aggregatedEvent = evt as GameResultsEvent;
        const incomingRound = aggregatedEvent.roundId;
        const normalizedIncomingRound = String(incomingRound ?? '').trim().toLowerCase();
        const normalizedExpectedRound = String(roundId ?? '').trim().toLowerCase();
        const incomingGameId = aggregatedEvent.games?.[0]?.gameId ?? 'game';
        logWithContextRef.current(
          'hole.game.result.received',
          {
            message: 'Received game.result.',
            incomingRound,
            expectedRound: roundId,
            gameId: incomingGameId,
            updatedHoleNumber: aggregatedEvent.updatedHoleNumber,
            updatedPlayerId: aggregatedEvent.updatedPlayerId,
            games: aggregatedEvent.games?.length ?? 0,
          },
          'debug'
        );
        if (!normalizedIncomingRound || !normalizedExpectedRound || normalizedIncomingRound !== normalizedExpectedRound) {
          logWithContextRef.current(
            'hole.game.result.skipped',
            {
              message: 'Skipped game.result for other round.',
              incomingRound,
              expectedRound: roundId,
              gameId: incomingGameId,
            },
            'warn'
          );
          return;
        }
        const aggregated: GameResultState = {};
        aggregatedEvent.games.forEach((game) => {
          const gameId = game.gameId ?? 'game';
          const current = aggregated[gameId] ?? { name: game.gameName ?? 'Game', scorePoints: {} };
          // Which players were scored in which ball, as the server applied it. The table reads
          // standings inside the ball off this rather than re-deriving the split.
          const incomingBalls = (game.balls ?? [])
            .map((ball) => ({ playerIds: (ball.playerIds ?? []).filter((id): id is string => Boolean(id)) }))
            .filter((ball) => ball.playerIds.length > 0);
          if (incomingBalls.length > 0) {
            current.balls = incomingBalls;
          }
          (game.players ?? []).forEach((player) => {
            const matchingPlayer = playersRef.current.find((p) => p.id === player.playerId || p.name === player.playerId);
            const playerKey = matchingPlayer?.id ?? player.playerId;
            const playerNameKey = matchingPlayer?.name && matchingPlayer.name !== playerKey ? matchingPlayer.name : null;
            const normIdKey = normalizePlayerKey(playerKey);
            const normNameKey = normalizePlayerKey(playerNameKey);
            if (playerKey) current.scorePoints[playerKey] = player.lastScorePoints;
            if (playerNameKey) current.scorePoints[playerNameKey] = player.lastScorePoints;
            if (normIdKey) current.scorePoints[normIdKey] = player.lastScorePoints;
            if (normNameKey) current.scorePoints[normNameKey] = player.lastScorePoints;
          });
          current.name = current.name ?? game.gameName ?? 'Game';
          aggregated[gameId] = current;
        });
        // Merge instead of replace: games-service omits comparative games (Skins,
        // Best ball - worst ball) while the current hole is only partially scored,
        // so a wholesale replace would wipe their last known standings. Previously
        // known games survive until a fresh result arrives; games deselected from
        // the round are pruned via the selected-game-definition list.
        //
        // An *incoming* game is kept only if the round still plays it or if it is not a game
        // definition at all (Nassau front/back/press segments arrive under derived ids). A late
        // result for a removed game used to re-enter here, put its hazard counter back on screen,
        // and the next tap on that counter re-attached the game server-side.
        setGameResults((prev) => {
          const merged: GameResultState = { ...prev, ...aggregated };
          const selectedIds = selectedGameDefinitionIdsRef.current;
          if (selectedIds.length > 0) {
            const keep = new Set([...selectedIds, ...derivedGameIds(Object.keys(aggregated), knownGameDefinitionIdsRef.current)]);
            Object.keys(merged).forEach((gameId) => {
              if (!keep.has(gameId)) {
                delete merged[gameId];
              }
            });
          }
          if (roundId) {
            gameResultsCache[roundId] = merged;
          }
          return merged;
        });
        // Server-computed match state and skins carry ride on the same event.
        // Same merge semantics as the scores: a game present in the event
        // replaces its entry (match:null clears a banner that fell back below
        // dormie), while games the service omitted mid-entry keep their last
        // known values instead of flickering off.
        const incomingExtras: ServerGameExtras = {};
        aggregatedEvent.games.forEach((game) => {
          const gameId = game.gameId ?? 'game';
          incomingExtras[gameId] = {
            match: game.match ?? null,
            skinsCarry: game.skinsCarry ?? null,
            // A ball-scoped game was calculated separately inside each ball, so its match state
            // and carry are per ball rather than round-wide.
            balls:
              game.balls && game.balls.length > 0
                ? game.balls.map((ball) => ({
                    groupNumber: ball.groupNumber,
                    match: ball.match ?? null,
                    skinsCarry: ball.skinsCarry ?? null,
                  }))
                : null,
          };
        });
        setServerGameExtras((prev) => {
          const merged: ServerGameExtras = { ...prev, ...incomingExtras };
          const selectedIds = selectedGameDefinitionIdsRef.current;
          if (selectedIds.length > 0) {
            const keep = new Set([...selectedIds, ...derivedGameIds(Object.keys(incomingExtras), knownGameDefinitionIdsRef.current)]);
            Object.keys(merged).forEach((gameId) => {
              if (!keep.has(gameId)) {
                delete merged[gameId];
              }
            });
          }
          if (roundId) {
            serverGameExtrasCache[roundId] = merged;
          }
          return merged;
        });
        logWithContextRef.current(
          'hole.game.result.applied',
          {
            message: 'Merged aggregated game.result into local gameResults.',
            roundId,
            games: Object.keys(aggregated).length,
            gameIds: Object.keys(aggregated),
          },
          'debug'
        );
        if (client && roundId) {
          client.send({ type: 'round.requestState', roundId });
        }
      }
    });

    const detachOfflineQueue = roundId
      ? attachOfflineQueue(client, { subjectKey: offlineSubjectKey, roundId })
      : null;

    const unsubscribeConnection = client.onConnectionChange((state) => {
      if (state === 'connected') {
        setError(null);
        socketConnectionEpochRef.current += 1;
        hasSyncedRoundRef.current = false;
        if (!hasSyncedRoundRef.current) {
          resubscribeRound();
        }
        const currentRoundId = roundIdRef.current;
        const currentHole = holeNumberRef.current;
        if (currentRoundId) {
          const holeViewKey = `${currentRoundId}:${currentHole}`;
          const connectionEpoch = socketConnectionEpochRef.current;
          const shouldSendHoleView =
            lastHoleViewKeyRef.current !== holeViewKey || lastHoleViewEpochRef.current !== connectionEpoch;
          if (shouldSendHoleView) {
            lastHoleViewKeyRef.current = holeViewKey;
            lastHoleViewEpochRef.current = connectionEpoch;
            lastHoleViewSentAtRef.current = Date.now();
            // Passive with a device id: a reconnect refreshes the hole's state but is not a
            // navigation — an active, id-less claim here re-asserted this device's stale hole
            // on every radio drop and dragged the user's other devices back to it.
            client.send(reconnectHoleView(currentRoundId, currentHole, getDeviceId()));
            logWithContextRef.current('hole.view.sent', { message: 'Sent hole.view request to BFF after reconnect.', passive: true });
          }
        }
        if (isGuestClaimMode && (claimSession?.tourId || claimSession?.inviteToken)) {
          client.sendTourLeaderboardRequest({
            tourId: claimSession?.tourId ?? undefined,
            inviteToken: claimSession.inviteToken ?? undefined,
            requestId: generateTraceId(),
          });
        }
      } else {
        hasSyncedRoundRef.current = false;
      }
    });

    const unsubscribeErrors = client.onError((message) => {
      const normalized = String(message ?? '').trim().toLowerCase();
      if (normalized.includes('anonymous connections are read-only')) {
        return;
      }
      // Show a friendly message instead of the raw backend error code.
      if (normalized === 'round.join.forbidden' || normalized === 'tour.join.forbidden') {
        setError(t('hole.notRoundMember'));
        return;
      }
      setError(message);
    });

    resubscribeRound();

    return () => {
      client.send({ type: 'round.leave', roundId });
      hasSyncedRoundRef.current = false;
      detachOfflineQueue?.();
      unsubscribeMessages();
      unsubscribeConnection();
      unsubscribeErrors();
      logWithContextRef.current('hole.round.leave', { message: 'Sent round.leave for cleanup.' }, 'debug');
    };
  }, [
    apiBaseUrl,
    effectiveClaimClientId,
    offlineSubjectKey,
    roundId,
    user?.id,
    user?.token,
    isGuestClaimMode,
    isAnonymousReadOnlyMode,
    claimSession?.tourId,
    claimSession?.inviteToken,
    claimSession?.claimAccessToken,
    isNavigationReady,
    applyHandicapsFromEntries,
    applyHandicapsFromMap,
    t,
  ]);

  const handleScore = async (
    player: Player,
    value: string,
    options?: { suppressCelebration?: boolean; suppressAutoAdvance?: boolean }
  ) => {
    if (!roundId) return;
    if (value === 'Pickup' && isStrokePlayGame) {
      logToBff('hole.pickup.blocked.strokePlay', { playerId: player.id, holeNumber }, 'warn');
      return;
    }
    const inputStart = nowMs();
    incrementCounter('goffy.frontend.score.input.count', 1, { method: 'tap' });
    const traceId = generateTraceId();
    setHoleScoreTraceId(traceId);
    const parToSend = holePar ?? currentHoleMeta.par ?? 4;
    if (!holePar && !currentHoleMeta.par) {
      logToBff('hole.birdie.par.missing', { holeNumber, parToSend, value }, 'warn');
    }
    const numericValue = Number.parseInt(value, 10);
    if (!Number.isNaN(numericValue)) {
      if (numericValue === parToSend - 1 && !options?.suppressCelebration) {
        triggerBirdies();
      }
      logToBff('hole.birdie.handleScore', { scorePopupPlayerId: scorePopupPlayerId ?? null, value }, 'info');
    }
    if (!options?.suppressAutoAdvance) {
      pendingAutoAdvanceHoleKeyRef.current = `${roundId}:${holeNumber}`;
      liveScoredHoleKeyRef.current = `${roundId}:${holeNumber}`;
      if (!baselineRoundRankByHoleRef.current[pendingAutoAdvanceHoleKeyRef.current]) {
        baselineRoundRankByHoleRef.current[pendingAutoAdvanceHoleKeyRef.current] = { ...currentRoundRankSnapshotRef.current };
      }
    }
    if (currentHoleDataKey) {
      setActiveHoleDataKey(currentHoleDataKey);
    }
    setScores((prev) => ({ ...prev, [player.name]: value, [player.id]: value }));
    setExplicitScoreSelections((prev) => ({ ...prev, [player.name]: true, [player.id]: true }));

    // The rAF fires after the next frame is produced, approximating
    // tap-to-rendered latency for the optimistic update.
    const scheduleCommitMeasure =
      typeof requestAnimationFrame !== 'undefined'
        ? requestAnimationFrame
        : (callback: () => void) => setTimeout(callback, 0);
    scheduleCommitMeasure(() => {
      recordHistogram('goffy.frontend.score.input.commit.duration', nowMs() - inputStart, { method: 'tap' });
    });

    try {
      const requestId = createEventId();
      const body =
        value === 'Pickup'
          ? {
              roundId,
              playerId: player.id,
              hole: holeNumber,
              par: parToSend,
              strokes: null,
              pickup: true,
              timestamp: new Date().toISOString(),
              traceId,
              requestId,
            }
          : {
              roundId,
              playerId: player.id,
              hole: holeNumber,
              par: parToSend,
              strokes: parseInt(value, 10),
              pickup: false,
              timestamp: new Date().toISOString(),
              traceId,
              requestId,
            };
      // Durable send: the score is persisted before the send and stays queued
      // until a hole.update positively confirms it (requestId echo or semantic
      // match), so a send into a half-dead socket is retried, not lost.
      await sendWithAck(
        socketClient
          ? {
              // sendScoreUpdate (not raw send) so the traceId latency metric
              // still tracks direct sends.
              send: () => socketClient.sendScoreUpdate(body),
              isConnected: () => socketClient.isConnected(),
            }
          : null,
        { subjectKey: offlineSubjectKey, roundId },
        'score.update',
        requestId,
        { type: 'score.update', ...body },
        { playerName: player.name }
      );

      // Entering a score is the strongest "I am here": record this hole as the device's position
      // even when it was reached passively (opened from a resume, followed from another device).
      markLocalNavigation(roundId);
      const activeViewKey = `${roundId}:${holeNumber}`;
      if (socketClient && lastActiveViewKeyRef.current !== activeViewKey) {
        lastActiveViewKeyRef.current = activeViewKey;
        socketClient.send({ type: 'hole.view', roundId, hole: holeNumber, deviceId: getDeviceId(), passive: false });
      }

      // Bounds which hole a stretch of trace belongs to. Attribution only — this fires
      // wherever the score was entered, which is routinely the next tee.
      recordCourseTraceStroke(holeNumber);

      const popupSeenKey = `${user?.id ?? 'unknown'}:${roundId}:${holeNumber}:mystery-next-hole`;
      const nextHole = holeOrder.nextHole(holeNumber, startHole, holesCount);
      if (nextHole !== null) {
        const upcomingEntries = roundSubgames.filter((entry) => entry.holeNumber === nextHole);
        if (upcomingEntries.length > 0) {
          if (!seenNextMysteryPopupByUserRound.has(popupSeenKey)) {
            const rows = upcomingEntries
              .map((entry) => ({
                subgame: subgameNameById[entry.subgameId] ?? t('hole.mysteryPrizeUnknownSubgame'),
                player: playerNameById[entry.playerId] ?? t('hole.mysteryPrizeUnknownPlayer'),
              }))
              .sort((a, b) => {
                const bySubgame = a.subgame.localeCompare(b.subgame);
                if (bySubgame !== 0) return bySubgame;
                return a.player.localeCompare(b.player);
              });
            setNextMysteryModalRows(rows);
            setNextMysteryModalOpen(true);
            seenNextMysteryPopupByUserRound.add(popupSeenKey);
          }
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kunde inte spara resultatet.');
    }
  };

  // A score selected in the popup goes through here: if the popup opened with a score
  // already set and the new value differs, ask for confirmation instead of saving directly.
  const requestScoreFromPopup = (player: Player, value: string) => {
    const initialScore = scorePopupInitialScoreRef.current;
    const currentScore =
      (hasExactHoleData ? (scores[player.id] ?? scores[player.name]) : null) ?? initialScore;
    if (initialScore && currentScore && value !== currentScore) {
      setPendingScoreChange({ player, value, currentValue: currentScore });
      return;
    }
    handleScore(player, value);
    closeScorePopup();
  };

  const confirmPendingScoreChange = () => {
    if (!pendingScoreChange) return;
    handleScore(pendingScoreChange.player, pendingScoreChange.value);
    setPendingScoreChange(null);
    closeScorePopup();
  };

  const triggerBirdies = () => {
    setBirdsVisible(true);
    birdieScale.setValue(0.05);
    birdieRotate.setValue(0);
    birdieOpacity.setValue(1);
    logToBff('hole.birdie.trigger', { holeNumber }, 'info');
    const birdieMaxSize = 300;
    const targetSize = Math.min(windowWidth, birdieMaxSize);
    const fillScale = targetSize / birdieMaxSize;
    const blowScale = fillScale;

    const startAnimation = () => {
      Animated.sequence([
        Animated.parallel([
          Animated.timing(birdieScale, {
            toValue: fillScale,
            duration: 2300,
            easing: Easing.out(Easing.cubic),
            useNativeDriver: false,
          }),
          Animated.timing(birdieRotate, {
            toValue: 1,
            duration: 2300,
            easing: Easing.linear,
            useNativeDriver: false,
          }),
        ]),
        Animated.delay(1000),
        Animated.parallel([
          Animated.timing(birdieScale, {
            toValue: blowScale,
            duration: 300,
            easing: Easing.out(Easing.quad),
            useNativeDriver: false,
          }),
          Animated.timing(birdieOpacity, {
            toValue: 0,
            duration: 300,
            easing: Easing.out(Easing.quad),
            useNativeDriver: false,
          }),
        ]),
      ]).start(() => {
        setBirdsVisible(false);
        birdieScale.setValue(0.05);
        birdieRotate.setValue(0);
        birdieOpacity.setValue(1);
      });
    };

    if (typeof requestAnimationFrame !== 'undefined') {
      requestAnimationFrame(startAnimation);
    } else {
      setTimeout(startAnimation, 0);
    }
  };

  const currentHoleMeta = holeMeta[holeNumber] ?? {};
  const parLabel = holePar ?? currentHoleMeta.par ?? '–';
  const hcpLabel = strokeIndex ?? currentHoleMeta.strokeIndex ?? '–';
  const parForHole = holePar ?? currentHoleMeta.par ?? null;
  const scorePopupSelectedValue = scorePopupPlayer
    ? (hasExactHoleData ? (scores[scorePopupPlayer.id] ?? scores[scorePopupPlayer.name] ?? null) : null)
    : null;
  const scorePopupSelectedMarkIndex = useMemo(() => {
    if (!scorePopupSelectedValue || scorePopupSelectedValue === 'Pickup') return null;
    if (scorePopupSelectedValue === '9+' || scorePopupSelectedValue === '10+') return 7;
    const numeric = Number.parseInt(scorePopupSelectedValue, 10);
    if (Number.isNaN(numeric)) return null;
    if (numeric >= 8) return 7;
    if (numeric >= 1 && numeric <= 7) return numeric - 1;
    return null;
  }, [scorePopupSelectedValue]);
  const scorePopupParMarkIndex = useMemo(() => {
    if (parForHole == null) return null;
    if (parForHole >= 8) return 7;
    if (parForHole >= 1 && parForHole <= 7) return parForHole - 1;
    return null;
  }, [parForHole]);
  const scorePopupEventCounts = useMemo(() => {
    return resolveHazardCountsForPlayer(scorePopupPlayer);
  }, [resolveHazardCountsForPlayer, scorePopupPlayer]);
  const scorePopupEnhancedValues = useMemo<EnhancedHoleValues>(() => {
    if (!scorePopupPlayer) return EMPTY_ENHANCED_VALUES;
    return enhancedValues[`${scorePopupPlayer.id}:${holeNumber}`] ?? EMPTY_ENHANCED_VALUES;
  }, [enhancedValues, holeNumber, scorePopupPlayer]);
  // Never entered by hand: the score, the putts and par answer it.
  const scorePopupDerivedGir = useMemo(() => {
    const strokes =
      scorePopupSelectedValue && scorePopupSelectedValue !== 'Pickup'
        ? Number.parseInt(scorePopupSelectedValue, 10)
        : Number.NaN;
    return deriveGreenInRegulation(
      Number.isFinite(strokes) ? strokes : null,
      scorePopupEnhancedValues.putts,
      parForHole
    );
  }, [parForHole, scorePopupEnhancedValues.putts, scorePopupSelectedValue]);
  const scorePopupGir = scorePopupEnhancedValues.gir ?? scorePopupDerivedGir;
  const allPlayersScoredForCurrentHole = useMemo(() => {
    if (!hasExactHoleData) return false;
    if (players.length === 0) return false;
    return players.every((player) => {
      const existing = holeEntries.find((entry) => {
        if (entry.holeNumber !== holeNumber) return false;
        const entryName = (entry.playerName ?? '').trim().toLowerCase();
        const playerName = player.name.trim().toLowerCase();
        return entry.playerId === player.id || entryName === playerName || entry.playerName === player.id;
      });
      if (existing && (existing.grossStrokes !== null && existing.grossStrokes !== undefined)) {
        return true;
      }
      if (existing?.pickup) {
        return true;
      }
      const selectedScore = scores[player.id] ?? scores[player.name];
      if (!selectedScore) {
        return false;
      }
      if (selectedScore === 'Pickup') {
        const hasExplicitScore =
          explicitScoreSelections[player.id] === true ||
          explicitScoreSelections[player.name] === true;
        return hasExplicitScore || !!existing?.pickup;
      }
      return true;
    });
  }, [explicitScoreSelections, hasExactHoleData, holeEntries, holeNumber, players, scores]);
  useEffect(() => {
    if (!roundId) return;
    if (!allPlayersScoredForCurrentHole) return;
    const upcomingHole = holeOrder.nextHole(holeNumber, startHole, holesCount);
    if (upcomingHole === null) return;
    // The wrap hole offers "continue to hole 1" and "finish round" side by side; that
    // choice belongs to the player, so never auto-advance across the wrap.
    if (holeOrder.isWrapHole(holeNumber, startHole, holesCount)) return;
    if (leaderboardChangeModalOpen) return;
    // Hold auto-advance while a leaderboard-change decision is still settling, so we don't
    // navigate away before a change modal can appear.
    if (leaderboardDecisionPendingKey) return;
    const currentHoleKey = `${roundId}:${holeNumber}`;
    if (pendingAutoAdvanceHoleKeyRef.current !== currentHoleKey) return;
    pendingAutoAdvanceHoleKeyRef.current = null;
    const timeout = setTimeout(() => {
      navigateToHole(upcomingHole);
    }, 220);
    return () => clearTimeout(timeout);
  }, [allPlayersScoredForCurrentHole, holeNumber, holesCount, leaderboardChangeModalOpen, leaderboardDecisionPendingKey, navigateToHole, roundId, startHole]);
  const strokeIndexForHole = strokeIndex ?? currentHoleMeta.strokeIndex ?? null;
  const hasMysteryPrizeForHole = useMemo(
    () => roundSubgames.some((entry) => entry.holeNumber === holeNumber),
    [roundSubgames, holeNumber]
  );
  const mysteryEntriesForHole = useMemo(
    () => roundSubgames.filter((entry) => entry.holeNumber === holeNumber),
    [roundSubgames, holeNumber]
  );
  const playerNameById = useMemo(() => {
    const map: Record<string, string> = {};
    players.forEach((player) => {
      map[player.id] = player.name;
    });
    return map;
  }, [players]);
  const subgameNameById = useMemo(() => {
    const map: Record<string, string> = {};
    subgames.forEach((subgame) => {
      map[subgame.id] = getSubgameDisplayName(subgame, t('hole.mysteryPrizeUnknownSubgame'));
    });
    return map;
  }, [subgames]);
  const selectedHazardTypes = useMemo(() => {
    const types = new Set<string>();
    const selected = new Set(selectedGameDefinitionIds);
    const knownIds = new Set(
      gameDefinitionNames
        .map((def) => def.id)
        .filter((id): id is string => typeof id === 'string' && id.length > 0)
    );
    if (selectedGameDefinitionIds.length > 0) {
      gameDefinitionNames
        .filter((def) => def.id && selected.has(def.id))
        .forEach((def) => {
          const hazardType = hazardTypeFromName(def.name ?? (def as { Name?: string }).Name);
          if (hazardType) {
            types.add(hazardType);
          }
        });
    }
    // Results are the fallback for a client that never learned the round's selection (guest claim,
    // definitions still loading). A game the round demonstrably no longer plays is not a fallback:
    // keeping its counter on screen is how a removed hazard game got tapped back into the round.
    Object.entries(gameResults).forEach(([gameId, game]) => {
      if (selected.size > 0 && knownIds.has(gameId) && !selected.has(gameId)) {
        return;
      }
      const hazardType = hazardTypeFromName(game.name);
      if (hazardType) {
        types.add(hazardType);
      }
    });
    return types;
  }, [gameDefinitionNames, gameResults, selectedGameDefinitionIds]);
  const isStablefordGame = useMemo(() => {
    const selected = new Set(selectedGameDefinitionIds);
    const fromDefinitions = gameDefinitionNames.some((def) => {
      if (!def.id || (selected.size > 0 && !selected.has(def.id))) return false;
      const name = (def.name ?? (def as { Name?: string }).Name ?? '').toLowerCase();
      return name.includes('stableford');
    });
    if (fromDefinitions) return true;
    return Object.values(gameResults).some((game) => (game.name ?? '').toLowerCase().includes('stableford'));
  }, [gameDefinitionNames, gameResults, selectedGameDefinitionIds]);
  // Stroke play requires holing out on every hole, so pickup is not a valid score entry.
  const isStrokePlayGame = useMemo(() => {
    const selected = new Set(selectedGameDefinitionIds);
    const fromDefinitions = gameDefinitionNames.some((def) => {
      if (!def.id || (selected.size > 0 && !selected.has(def.id))) return false;
      const name = (def.name ?? (def as { Name?: string }).Name ?? '').toLowerCase();
      return name.includes('stroke play');
    });
    if (fromDefinitions) return true;
    return Object.values(gameResults).some((game) => (game.name ?? '').toLowerCase().includes('stroke play'));
  }, [gameDefinitionNames, gameResults, selectedGameDefinitionIds]);
  const isMysterySelected = useMemo(() => {
    const selected = new Set(selectedGameDefinitionIds);
    const fromDefinitions = gameDefinitionNames.some((def) => {
      if (!def.id || (selected.size > 0 && !selected.has(def.id))) return false;
      const name = (def.name ?? (def as { Name?: string }).Name ?? '').toLowerCase();
      return name === 'mystery';
    });
    if (fromDefinitions) return true;
    return Object.values(gameResults).some((game) => (game.name ?? '').toLowerCase() === 'mystery');
  }, [gameDefinitionNames, gameResults, selectedGameDefinitionIds]);
  const isSkinsSelected = useMemo(() => {
    const selected = new Set(selectedGameDefinitionIds);
    const fromDefinitions = gameDefinitionNames.some((def) => {
      if (!def.id || (selected.size > 0 && !selected.has(def.id))) return false;
      const name = (def.name ?? (def as { Name?: string }).Name ?? '').toLowerCase();
      return name === 'skins';
    });
    if (fromDefinitions) return true;
    return Object.values(gameResults).some((game) => (game.name ?? '').toLowerCase() === 'skins');
  }, [gameDefinitionNames, gameResults, selectedGameDefinitionIds]);
  // Skins pot riding on the next unsettled hole, computed by games-service
  // (SkinsGame) and delivered on game.result — the carry rule no longer lives
  // in this client.
  const serverSkinsCarry = useMemo(() => {
    if (!isSkinsSelected) return 1;
    for (const extras of Object.values(serverGameExtras)) {
      if (extras.skinsCarry !== null && extras.skinsCarry !== undefined) return extras.skinsCarry;
    }
    return 1;
  }, [isSkinsSelected, serverGameExtras]);
  // Skins played inside each ball has a pot per ball, so the banner names the ball rather than
  // quoting one ball's pot at everyone. Empty for a whole-round game, which uses the value above.
  const serverSkinsCarryByBall = useMemo(() => {
    if (!isSkinsSelected) return [];
    for (const extras of Object.values(serverGameExtras)) {
      const balls = (extras.balls ?? []).filter(
        (ball) => ball.skinsCarry !== null && ball.skinsCarry !== undefined
      );
      if (balls.length > 0) {
        return balls.map((ball) => ({ groupNumber: ball.groupNumber, carry: ball.skinsCarry as number }));
      }
    }
    return [];
  }, [isSkinsSelected, serverGameExtras]);
  // Unlike allPlayersScoredForCurrentHole this ignores optimistic local scores: the next-hole pot
  // needs the persisted entries (with handicaps) before the current hole's outcome can be judged.
  const isHoleSettled = useCallback(
    (hole: number, entries: HoleEntry[]) => {
      if (players.length === 0) return false;
      return players.every((player) => {
        const playerName = player.name.trim().toLowerCase();
        return entries.some((entry) => {
          if (entry.holeNumber !== hole) return false;
          const entryName = (entry.playerName ?? '').trim().toLowerCase();
          const matches = entry.playerId === player.id || entryName === playerName || entry.playerName === player.id;
          if (!matches) return false;
          return entry.pickup === true || (entry.grossStrokes !== null && entry.grossStrokes !== undefined);
        });
      });
    },
    [players]
  );
  const currentHoleScoredInEntries = useMemo(
    () => hasExactHoleData && isHoleSettled(holeNumber, holeEntries),
    [hasExactHoleData, holeEntries, holeNumber, isHoleSettled]
  );
  // The earliest hole in play order still to be finished. The skins pot and the match
  // state are the round as it stands, so they belong here and on the hole just settled —
  // shown against an older hole they read as that hole's own state, which is how
  // a 17-skin pot ended up on hole 1. Walks the play order (a round can start on hole
  // 10 and wrap 18 -> 1), returning holesCount as "everything settled" sentinel.
  const livePlayIndex = useMemo(() => {
    if (!allHoleEntriesLoadedForCurrentRound) return holeOrder.playIndex(holeNumber, startHole, holesCount);
    const order = holeOrder.playOrder(startHole, holesCount);
    for (let i = 0; i < order.length; i += 1) {
      if (!isHoleSettled(order[i], allHoleEntries)) return i;
    }
    return holesCount;
  }, [allHoleEntries, allHoleEntriesLoadedForCurrentRound, holeNumber, holesCount, isHoleSettled, startHole]);
  const showsCurrentMatchState = holeOrder.playIndex(holeNumber, startHole, holesCount) >= livePlayIndex - 1;
  // The server value is always "the pot on the next unsettled hole", so the
  // wording flips on whether the viewed hole is settled: while scoring it, that
  // pot is in play here; once every player has entered, it belongs to the next.
  const skinsBannerText = useMemo(() => {
    if (!showsCurrentMatchState) return undefined;
    const settled = currentHoleScoredInEntries;
    if (settled && holeOrder.isLastHole(holeNumber, startHole, holesCount)) return undefined;
    const carryText = (count: number) =>
      settled ? t('hole.skinsCarryNext', { count }) : t('hole.skinsCarry', { count });

    if (serverSkinsCarryByBall.length > 0) {
      const riding = serverSkinsCarryByBall.filter((ball) => ball.carry > 1);
      if (riding.length === 0) return undefined;
      return riding
        .map((ball) => t('hole.skinsCarryBall', { ball: ball.groupNumber, carry: carryText(ball.carry) }))
        .join(' · ');
    }

    if (serverSkinsCarry <= 1) return undefined;
    return carryText(serverSkinsCarry);
  }, [
    currentHoleScoredInEntries,
    holeNumber,
    holesCount,
    serverSkinsCarry,
    serverSkinsCarryByBall,
    showsCurrentMatchState,
    startHole,
    t,
  ]);
  // Every player has a mystery prize configured once each has a subgame selection for the round.
  // The hole-screen prompt is only useful while setup is still incomplete.
  const allPlayersHaveMystery = useMemo(() => {
    if (players.length === 0) return false;
    const playersWithSelection = new Set(roundSubgames.map((entry) => entry.playerId));
    return players.every((player) => playersWithSelection.has(player.id));
  }, [players, roundSubgames]);
  const showPenaltyEvent = useMemo(() => selectedHazardTypes.has('penalty'), [selectedHazardTypes]);
  const showWaterEvent = useMemo(() => selectedHazardTypes.has('water'), [selectedHazardTypes]);
  const showBunkerEvent = useMemo(() => selectedHazardTypes.has('bunker'), [selectedHazardTypes]);
  const showThreePuttEvent = useMemo(() => selectedHazardTypes.has('threeputt'), [selectedHazardTypes]);
  const showChipInEvent = useMemo(() => selectedHazardTypes.has('chipin'), [selectedHazardTypes]);
  const showOutEvent = useMemo(() => selectedHazardTypes.has('out'), [selectedHazardTypes]);
  const mysteryWinnersBySubgame = useMemo(
    () =>
      buildMysteryWinnersBySubgame({
        holeEvents,
        holeNumber,
      }),
    [holeEvents, holeNumber]
  );
  // A hole pays exactly one mystery prize, so its latest award is the winner whichever subgame id
  // it was recorded under — the designated challenge or the generic prize. This is the same rule
  // the server scores by (MysteryGameCalculator); reading the ids separately let one hole show,
  // and pay, two winners.
  const mysteryWinnerForHole = useMemo(() => {
    const awards = Object.entries(mysteryWinnersBySubgame).map(([subgameId, entry]) => {
      const parsed = Date.parse(entry.occurredAt ?? '');
      return { playerId: entry.playerId, subgameId, occurredAt: Number.isNaN(parsed) ? 0 : parsed };
    });
    if (awards.length === 0) return null;
    // Ties keep the later award, matching the order the round state delivers them in.
    return awards.reduce((latest, award) => (award.occurredAt >= latest.occurredAt ? award : latest));
  }, [mysteryWinnersBySubgame]);
  const mysteryWinnerIdsForHole = useMemo(
    () => new Set(mysteryWinnerForHole ? [mysteryWinnerForHole.playerId] : []),
    [mysteryWinnerForHole]
  );
  // Tour-long Freeze standing per player on this hole, from the tour's second
  // round onwards: a filled snowflake once the hole is locked, an outlined one
  // while it is still in play, with the banked or pending points beside it.
  const freezeStatusByPlayerForHole = useMemo(
    () => buildFreezeStatusBeforeRound(freezeSection, holeNumber, roundId),
    [freezeSection, holeNumber, roundId]
  );
  // Eclectic keeps each player's best points per hole across the tour, so from
  // the tour's second round onwards the score button carries the points already
  // banked on this hole — the number to beat.
  const eclecticBestByPlayerForHole = useMemo(
    () => buildEclecticBestBeforeRound(eclecticSection, holeNumber, roundId),
    [eclecticSection, holeNumber, roundId]
  );
  const mysteryPrizeText = useMemo(() => {
    const entries = mysteryEntriesForHole;
    if (entries.length === 0) {
      return t('hole.mysteryPrizeNone');
    }
    const parts = entries.map((entry) => {
      const subgameName = subgameNameById[entry.subgameId] ?? t('hole.mysteryPrizeUnknownSubgame');
      const playerName = playerNameById[entry.playerId] ?? t('hole.mysteryPrizeUnknownPlayer');
      const winnerId = mysteryWinnerForHole?.playerId;
      if (winnerId) {
        const winnerName = playerNameById[winnerId] ?? t('hole.mysteryPrizeUnknownPlayer');
        return t('hole.mysteryPrizeEntryWinner', { subgame: subgameName, player: playerName, winner: winnerName });
      }
      return t('hole.mysteryPrizeEntry', { subgame: subgameName, player: playerName });
    });
    return `${t('hole.mysteryPrizeTitle')}: ${parts.join(', ')}`;
  }, [mysteryEntriesForHole, mysteryWinnerForHole, playerNameById, subgameNameById]);
  const handleMysteryWinnerSelect = useCallback(
    (subgameId: string, winnerId: string) => {
      if (!roundId) return;
      const entry = mysteryEntriesForHole.find((item) => item.subgameId === subgameId);
      if (!entry) return;
      // One prize per hole: retract whoever currently holds it before awarding, so moving the
      // prize does not leave both players holding a winner row (which the server would read as
      // the hole paying twice). Tapping the current holder clears it.
      const currentWinner = mysteryWinnerForHole;
      const isDeselect = currentWinner?.playerId === winnerId;
      const socket = isolatedSocketRef.current;
      const timestamp = new Date().toISOString();
      const queueCtx = { subjectKey: offlineSubjectKey, roundId };

      void (async () => {
        if (currentWinner) {
          // Retraction uses the legacy latest-match removal (no eventId), since the winner row
          // may have been created by another device.
          await sendOrQueue(socket, queueCtx, 'hole.event', createEventId(), {
            type: 'hole.event',
            roundId,
            playerId: currentWinner.playerId,
            holeNumber: entry.holeNumber,
            eventType: 'mysteryWinner',
            eventValue: currentWinner.subgameId,
            eventDelta: -1,
            timestamp,
          });
        }
        let outcome: 'sent' | 'queued' = 'sent';
        if (!isDeselect) {
          const addEventId = createEventId();
          outcome = await sendOrQueue(socket, queueCtx, 'hole.event', addEventId, {
            type: 'hole.event',
            roundId,
            playerId: winnerId,
            holeNumber: entry.holeNumber,
            eventType: 'mysteryWinner',
            eventValue: subgameId,
            eventDelta: 1,
            timestamp,
            eventId: addEventId,
          });
        }
        if (outcome === 'sent') {
          socket?.send({ type: 'round.requestState', roundId });
        }
      })();

      setHoleEvents((prev) => {
        const next = prev.filter(
          (event) => !(event.holeNumber === entry.holeNumber && event.eventType === 'mysteryWinner')
        );
        const result = isDeselect
          ? next
          : [
              ...next,
              {
                playerId: winnerId,
                holeNumber: entry.holeNumber,
                eventType: 'mysteryWinner',
                eventValue: subgameId,
                occurredAt: timestamp,
              },
            ];
        cacheRoundHoleEventsSnapshot(apiBaseUrl, roundId, user?.id ?? null, result);
        return result;
      });
    },
    [
      apiBaseUrl,
      mysteryEntriesForHole,
      mysteryWinnerForHole,
      offlineSubjectKey,
      roundId,
      user?.id,
    ]
  );
  // Award (or clear) a generic mystery prize for the current hole straight from the score popup,
  // without needing to assign a sub-challenge on the setup screen. Exactly one winner per hole:
  // tapping a player assigns the prize (moving it from a previous winner), tapping the current
  // winner again clears it.
  const handleMysteryPrizeToggle = useCallback(
    (winnerId: string) => {
      if (!roundId) return;
      const socket = isolatedSocketRef.current;
      const subgameId = GENERIC_MYSTERY_PRIZE_SUBGAME_ID;
      const currentWinner = mysteryWinnerForHole;
      const currentWinnerId = currentWinner?.playerId ?? null;
      const isDeselect = currentWinnerId === winnerId;
      const timestamp = new Date().toISOString();
      const queueCtx = { subjectKey: offlineSubjectKey, roundId };

      // Remove any existing winner first (the current player when clearing, or the previous winner
      // when moving the single per-hole prize), then add the new winner. Both go through the
      // durable queue so the ordered pair survives offline periods and restarts.
      void (async () => {
        if (currentWinner) {
          // Retract under the id the prize was actually recorded against, which is the hole's
          // designated challenge when one was assigned after the prize was given.
          await sendOrQueue(socket, queueCtx, 'hole.event', createEventId(), {
            type: 'hole.event',
            roundId,
            playerId: currentWinner.playerId,
            holeNumber,
            eventType: 'mysteryWinner',
            eventValue: currentWinner.subgameId,
            eventDelta: -1,
            timestamp,
          });
        }
        let outcome: 'sent' | 'queued' = 'sent';
        if (!isDeselect) {
          const addEventId = createEventId();
          outcome = await sendOrQueue(socket, queueCtx, 'hole.event', addEventId, {
            type: 'hole.event',
            roundId,
            playerId: winnerId,
            holeNumber,
            eventType: 'mysteryWinner',
            eventValue: subgameId,
            eventDelta: 1,
            timestamp,
            eventId: addEventId,
          });
        }
        if (outcome === 'sent') {
          socket?.send({ type: 'round.requestState', roundId });
        }
      })();

      setHoleEvents((prev) => {
        const next = prev.filter(
          (event) => !(event.holeNumber === holeNumber && event.eventType === 'mysteryWinner')
        );
        const result = isDeselect
          ? next
          : [
              ...next,
              {
                playerId: winnerId,
                holeNumber,
                eventType: 'mysteryWinner',
                eventValue: subgameId,
                occurredAt: timestamp,
              },
            ];
        cacheRoundHoleEventsSnapshot(apiBaseUrl, roundId, user?.id ?? null, result);
        return result;
      });
    },
    [apiBaseUrl, holeNumber, mysteryWinnerForHole, offlineSubjectKey, roundId, user?.id]
  );
  const displayTotals: DisplayTotal[] = useMemo(
    () =>
      buildDisplayTotals({
        leaderboardTotals,
        totals,
        gameResults,
        leaderboardGamePoints,
        players,
        tourTotals,
        unknownPlayerLabel: t('hole.unknownPlayer'),
      }),
    [gameResults, leaderboardGamePoints, leaderboardTotals, players, totals, tourTotals]
  );
  // Ghosts and pure AI opponents are named like anyone else on the leaderboard, so mark them with
  // the same ghost avatar the score list uses. Keyed by id and name because the leaderboard totals
  // arrive keyed either way.
  const aiPlayerKeys = useMemo(() => {
    const keys = new Set<string>();
    players.forEach((player) => {
      if (!player.isAi) return;
      keys.add(normalizePlayerKey(player.id));
      keys.add(normalizePlayerKey(player.name));
    });
    return keys;
  }, [players]);
  const tourLeaderKeys = useMemo(() => buildTourLeaderKeys(displayTotals), [displayTotals]);
  const tourPlacementByKey = useMemo(() => buildTourPlacementByKey(displayTotals), [displayTotals]);
  const roundPlacementByKey = useMemo(() => buildRoundPlacementByKey(displayTotals), [displayTotals]);
  const roundRankSnapshotByPlayerId = useMemo(() => {
    // Source scores from the same displayTotals the visible leaderboard uses, so the
    // modal's positions never disagree with the on-screen leaderboard / medals.
    const scoreByPlayerId: Record<string, number> = {};
    players.forEach((player) => {
      const total = displayTotals.find(
        (entry) =>
          entry.key === player.id ||
          normalizePlayerKey(entry.key) === normalizePlayerKey(player.id) ||
          normalizePlayerKey(entry.name) === normalizePlayerKey(player.name)
      );
      const roundScore = total?.roundPoints ?? 0;
      scoreByPlayerId[player.id] = Number.isFinite(roundScore) ? roundScore : 0;
    });

    const ordered = [...players]
      .map((player) => ({ playerId: player.id, score: scoreByPlayerId[player.id] ?? 0 }))
      .sort((a, b) => b.score - a.score);

    const tieSizeByScore: Record<string, number> = {};
    ordered.forEach((item) => {
      const key = `${item.score}`;
      tieSizeByScore[key] = (tieSizeByScore[key] ?? 0) + 1;
    });

    const snapshot: Record<string, RankSnapshotEntry> = {};
    let previousScore: number | null = null;
    let currentRank = 0;
    ordered.forEach((item, index) => {
      if (previousScore === null || item.score !== previousScore) {
        currentRank = index + 1;
        previousScore = item.score;
      }
      snapshot[item.playerId] = {
        rank: currentRank,
        tieSize: tieSizeByScore[`${item.score}`] ?? 1,
        score: item.score,
      };
    });

    players.forEach((player) => {
      if (!snapshot[player.id]) {
        snapshot[player.id] = { rank: null, tieSize: 0, score: 0 };
      }
    });

    return snapshot;
  }, [displayTotals, players]);
  useEffect(() => {
    currentRoundRankSnapshotRef.current = roundRankSnapshotByPlayerId;
  }, [roundRankSnapshotByPlayerId]);
  // Arm a leaderboard-change decision once the user has scored this hole to completion live.
  useEffect(() => {
    if (!roundId) return;
    if (!allPlayersScoredForCurrentHole) return;
    const currentHoleKey = `${roundId}:${holeNumber}`;
    // Only celebrate a change the user produced by scoring this hole live — not when entering
    // a previous hole or a hole that already had all scores.
    if (liveScoredHoleKeyRef.current !== currentHoleKey) return;
    if (shownLeaderboardChangeModalByHoleRef.current[currentHoleKey]) return;
    // Baseline is the standings captured (in handleScore) just before the first score of this
    // hole was entered. Without one we can't know whether anything changed.
    if (!baselineRoundRankByHoleRef.current[currentHoleKey]) return;
    setLeaderboardDecisionPendingKey((prev) => (prev === currentHoleKey ? prev : currentHoleKey));
  }, [allPlayersScoredForCurrentHole, holeNumber, roundId]);
  // Evaluate the decision only after standings settle. The timeout resets whenever the ranking
  // snapshot changes, so we compare against the final, post-update standings — never a transient
  // mid-update ranking that would show a wrong position or a non-existent change.
  useEffect(() => {
    if (!leaderboardDecisionPendingKey) return;
    const currentHoleKey = leaderboardDecisionPendingKey;
    const timeout = setTimeout(() => {
      const baselineSnapshot = baselineRoundRankByHoleRef.current[currentHoleKey] ?? {};
      const finalSnapshot = currentRoundRankSnapshotRef.current;
      const hasLeaderboardChange = players.some((player) => {
        const before = baselineSnapshot[player.id];
        const after = finalSnapshot[player.id];
        // Only a transition between two *known* standings counts as a change.
        if (!before || before.rank === null || !after || after.rank === null) return false;
        return before.rank !== after.rank || before.tieSize !== after.tieSize;
      });
      shownLeaderboardChangeModalByHoleRef.current[currentHoleKey] = true;
      setLeaderboardDecisionPendingKey(null);
      if (!hasLeaderboardChange) return;
      setLeaderboardChangeHoleKey(currentHoleKey);
      setLeaderboardChangeModalOpen(true);
    }, 800);
    return () => clearTimeout(timeout);
  }, [leaderboardDecisionPendingKey, players, roundRankSnapshotByPlayerId]);
  // Rows are derived from the live standings so the modal always agrees with the leaderboard.
  const leaderboardChangeRows = useMemo<LeaderboardChangeRow[]>(() => {
    if (!leaderboardChangeHoleKey) return [];
    const baselineSnapshot = baselineRoundRankByHoleRef.current[leaderboardChangeHoleKey] ?? {};
    const rows = players.map((player) => {
      const baseline = baselineSnapshot[player.id];
      const finalRank = roundRankSnapshotByPlayerId[player.id] ?? { rank: null, tieSize: 0, score: 0 };
      let movement: LeaderboardMovement = 'same';
      if (baseline && baseline.rank !== null && finalRank.rank !== null) {
        if (finalRank.rank < baseline.rank) movement = 'up';
        else if (finalRank.rank > baseline.rank) movement = 'down';
      }
      return {
        playerId: player.id,
        playerName: player.name,
        movement,
        roundRank: finalRank.rank,
      };
    });
    // Order rows by position so the leaderboard reads top-down (rank 1 first; unranked last).
    rows.sort((a, b) => (a.roundRank ?? Number.POSITIVE_INFINITY) - (b.roundRank ?? Number.POSITIVE_INFINITY));
    return rows.slice(0, LEADERBOARD_CHANGE_VISIBLE_ROWS);
  }, [leaderboardChangeHoleKey, players, roundRankSnapshotByPlayerId]);
  const renderPlacementBadge = useCallback((roundPlace?: number, tourPlace?: number) => {
    const place = (roundPlace && roundPlace >= 1 && roundPlace <= 3)
      ? roundPlace
      : (tourPlace && tourPlace >= 1 && tourPlace <= 3)
        ? tourPlace
        : null;
    if (!place) return null;
    const medal = place === 1 ? '🥇' : place === 2 ? '🥈' : '🥉';
    return (
      <View style={styles.roundPlacementBadge}>
        <Text style={styles.roundPlacementMedal}>{medal}</Text>
      </View>
    );
  }, []);
  const orderedTeams = useMemo(() => buildOrderedTeams(teams), [teams]);
  const gameResultPlayers = useMemo(
    () =>
      buildGameResultPlayers({
        players,
        orderedTeams,
        gameResults,
      }),
    [players, gameResults, orderedTeams]
  );
  const gameDefinitionNameById = useMemo(() => {
    const map: Record<string, string> = {};
    gameDefinitionNames.forEach((def) => {
      if (!def.id) return;
      const name = getGameDisplayName(
        {
          id: def.id,
          name: def.name ?? (def as { Name?: string }).Name,
          info: def.info ?? (def as { Info?: string }).Info,
          description: def.description ?? (def as { Description?: string }).Description,
        },
        t('hole.gameFallbackName')
      );
      if (name) {
        map[def.id] = name;
      }
    });
    return map;
  }, [gameDefinitionNames]);
  const gameResultRows = useMemo(
    () =>
      buildGameResultRows({
        orderedTeams,
        players,
        selectedGameDefinitionIds,
        gameResults,
        leaderboardGamePoints,
        gameDefinitionNameById,
        gameDefinitionTypes,
        gameResultPlayers,
        gameFallbackName: t('hole.gameFallbackName'),
      }),
    [
      gameDefinitionNameById,
      gameDefinitionTypes,
      gameResults,
      gameResultPlayers,
      leaderboardGamePoints,
      orderedTeams,
      players,
      selectedGameDefinitionIds,
    ]
  );
  // Same visual buckets (main / match / team / hazard / …) the game-selection grid uses, so the
  // live results table reads in the order players picked the games in.
  const groupedGameResultRows = useMemo(
    () => groupItems(gameResultRows, (row) => row.key, (row) => row.name),
    [gameResultRows]
  );
  // Dormie / match-won state, computed by games-service (MatchStatusCalculator)
  // and delivered on game.result. This client only resolves display labels:
  // game names from the results/definitions, team names from the round's teams.
  const matchStatuses = useMemo<MatchStatus[]>(() => {
    return Object.entries(serverGameExtras)
      .flatMap(([gameId, extras]) => {
        // One banner per ball for a ball-scoped game — each ball is its own match, and the
        // banner already names the leading team, so the balls stay tellable apart.
        const perBall = (extras.balls ?? [])
          .filter((ball) => !!ball.match)
          .map((ball) => ({
            gameId,
            bannerKey: `${gameId}-ball-${ball.groupNumber}`,
            ballNumber: ball.groupNumber as number | null,
            match: ball.match!,
          }));
        if (perBall.length > 0) return perBall;
        return extras.match
          ? [{ gameId, bannerKey: gameId, ballNumber: null as number | null, match: extras.match }]
          : [];
      })
      .map(({ gameId, bannerKey, ballNumber, match }) => {
        const team =
          orderedTeams.find((candidate) => candidate.id === match.leadingTeamId) ??
          orderedTeams.find((candidate) =>
            (candidate.players ?? []).some((pid) => match.leadingPlayerIds.includes(pid))
          );
        const teamName = (team?.name ?? '').trim();
        return {
          gameId,
          bannerKey,
          ballNumber,
          gameName: gameResults[gameId]?.name ?? gameDefinitionNameById[gameId] ?? '',
          state: match.state,
          teamId: team?.id ?? match.leadingTeamId ?? '',
          teamName: teamName.length > 0 && !isUuidLike(teamName) ? teamName : null,
          teamPlayerIds: team?.players ?? match.leadingPlayerIds,
          lead: match.lead,
          holesRemaining: match.holesRemaining,
        };
      });
  }, [gameDefinitionNameById, gameResults, orderedTeams, serverGameExtras]);
  // The leaderboard works off score totals rather than player records, so ghost membership is
  // resolved once here by id and by name. The round's own isAi flag wins where it is set; the
  // generated "Ghost of {name}" is the fallback for rounds whose players arrived without it.
  // The ball each player is in, by id and by name. Only meaningful once the round has more than
  // one — a single ball is the whole round, and numbering it says nothing.
  const ballNumberByPlayerKey = useMemo(() => {
    const byKey = new Map<string, number>();
    teams.forEach((team) => {
      if (!team.ballNumber) return;
      (team.players ?? []).forEach((playerId) => {
        byKey.set(normalizePlayerKey(playerId), team.ballNumber as number);
        const player = players.find((candidate) => candidate.id === playerId);
        if (player) {
          byKey.set(normalizePlayerKey(player.name), team.ballNumber as number);
        }
      });
    });
    return byKey;
  }, [players, teams]);
  const hasMultipleBalls = useMemo(
    () => new Set([...ballNumberByPlayerKey.values()]).size > 1,
    [ballNumberByPlayerKey]
  );
  const resolveBallNumber = useCallback(
    (playerId?: string | null, playerName?: string | null) => {
      if (!hasMultipleBalls) return null;
      return (
        ballNumberByPlayerKey.get(normalizePlayerKey(playerId)) ??
        ballNumberByPlayerKey.get(normalizePlayerKey(playerName ?? '')) ??
        null
      );
    },
    [ballNumberByPlayerKey, hasMultipleBalls]
  );
  const renderBallBadge = useCallback(
    (playerId?: string | null, playerName?: string | null) => {
      const ballNumber = resolveBallNumber(playerId, playerName);
      if (ballNumber === null) return null;
      return (
        <View style={styles.ballBadge} accessibilityLabel={t('hole.ballNumberLabel', { number: String(ballNumber) })}>
          <Text style={styles.ballBadgeText}>{ballNumber}</Text>
        </View>
      );
    },
    [resolveBallNumber, t]
  );

  // The player list is grouped by ball rather than badged row by row: the number is the same for
  // four players in a row, and the grouping answers "who am I playing with" at the same time.
  const playerRowsByBall = useMemo(() => {
    const rows = players.map((player) => ({
      player,
      ballNumber: resolveBallNumber(player.id, player.name),
    }));
    const ordered = hasMultipleBalls
      ? [...rows].sort((a, b) => (a.ballNumber ?? 0) - (b.ballNumber ?? 0))
      : rows;
    let previousBall: number | null = null;
    return ordered.map((row) => {
      const startsBall = row.ballNumber !== null && row.ballNumber !== previousBall;
      previousBall = row.ballNumber;
      return { ...row, startsBall };
    });
  }, [hasMultipleBalls, players, resolveBallNumber]);

  const ghostPlayerKeys = useMemo(() => {
    const keys = new Set<string>();
    players.forEach((player) => {
      if (!player.isAi && !isGhostPlayerName(player.name)) return;
      keys.add(normalizePlayerKey(player.id));
      keys.add(normalizePlayerKey(player.name));
    });
    return keys;
  }, [players]);

  // Every hazard a player has picked up so far this round, read from the hole events only.
  //
  // The hazard games' `scorePoints` looked like the obvious source but cannot be trusted for
  // attribution: it stores one player's value under several key variants (id, name, normalized
  // forms), so resolving a variant back to a player is guesswork, and a player ended up wearing
  // hazards that belonged to someone else in their ball. Events carry a validated round-member id,
  // cover the whole round, and are deleted rather than negated when a hazard is undone — so an
  // event of a type present here means that player really has that hazard, still, right now.
  const roundHazardTypesFor = useMemo(() => {
    const byPlayerId = new Map<string, Set<string>>();
    holeEvents.forEach((event) => {
      const hazardType = hazardTypeFromName(event.eventType);
      if (!hazardType || !event.playerId) return;
      // Matched the way the per-hole hydration does it: the id on an event is usually the member
      // GUID but can be a name, and anything that resolves to no player is dropped rather than
      // filed under a key of its own.
      const eventKey = normalizePlayerKey(event.playerId);
      const matched = players.find(
        (player) =>
          normalizePlayerKey(player.id) === eventKey || normalizePlayerKey(player.name) === eventKey,
      );
      if (!matched) return;
      const types = byPlayerId.get(matched.id) ?? new Set<string>();
      types.add(hazardType);
      byPlayerId.set(matched.id, types);
    });

    return (playerKey: string, playerName: string) => {
      const key = normalizePlayerKey(playerKey);
      const name = normalizePlayerKey(playerName);
      const matched = players.find(
        (player) =>
          normalizePlayerKey(player.id) === key
          || normalizePlayerKey(player.name) === key
          || normalizePlayerKey(player.name) === name,
      );
      return (matched ? byPlayerId.get(matched.id) : undefined) ?? new Set<string>();
    };
  }, [holeEvents, players]);

  const selectedPlayer = useMemo(() => {
    if (!selectedPlayerId) return null;
    const match = players.find((p) => p.id === selectedPlayerId) ?? displayTotals.find((t) => t.key === selectedPlayerId);
    if (!match) return { id: selectedPlayerId, name: selectedPlayerId };
    if ('name' in match) return { id: selectedPlayerId, name: match.name };
    return { id: selectedPlayerId, name: selectedPlayerId };
  }, [displayTotals, players, selectedPlayerId]);
  const resolveGameRowIcon = useCallback(
    (gameId: string, gameName: string) => {
      const sourceName = gameResults[gameId]?.name ?? gameDefinitionNameById[gameId] ?? gameName;
      const gameSlug = normalizeGameSlug(sourceName);
      if (GAME_ICON_MAP[gameSlug]) {
        return GAME_ICON_MAP[gameSlug];
      }
      if (gameSlug === 'out') return OutIcon;
      if (gameSlug.includes('penalty')) return PenaltyIcon;
      if (gameSlug.includes('3-putts') || gameSlug.includes('three-putts')) return ThreePuttIcon;
      if (gameSlug.includes('sandy-par') || gameSlug.includes('bunker')) return BunkerIcon;
      if (gameSlug.includes('wet-par') || gameSlug.includes('water')) return WaterIcon;
      if (gameSlug.includes('birdie')) return BirdieImage;
      return GolfballIcon;
    },
    [gameDefinitionNameById, gameResults]
  );
  const selectedPlayerGamePoints = useMemo(() => {
    if (!selectedPlayer) return [];
    const selectedGameIds = new Set(selectedGameDefinitionIds);
    const normalizedSelectedId = normalizePlayerKey(selectedPlayer.id);
    const normalizedSelectedName = normalizePlayerKey(selectedPlayer.name);
    const leaderboardPoints =
      leaderboardGamePoints[selectedPlayer.id] ??
      leaderboardGamePoints[selectedPlayer.name] ??
      Object.entries(leaderboardGamePoints).find(([key]) => {
        const normalizedKey = normalizePlayerKey(key);
        return normalizedKey === normalizedSelectedId || normalizedKey === normalizedSelectedName;
      })?.[1] ??
      [];
    const leaderboardPointsByGameId = new Map(
      leaderboardPoints
        .filter((gp) => typeof gp.gameId === 'string' && gp.gameId.trim().length > 0)
        .map((gp) => [gp.gameId as string, { points: gp.roundGamePoints, position: gp.position }])
    );

    const availableGameIds = new Set([
      ...Array.from(leaderboardPointsByGameId.keys()),
      ...Object.keys(gameResults),
    ]);
    const candidateGameIds = resolveBreakdownGameIds({
      selectedGameIds,
      availableGameIds,
      pointsByGameId: leaderboardPointsByGameId,
    });

    return candidateGameIds
      .map((gameId) => {
        const leaderboardValue = leaderboardPointsByGameId.get(gameId);
        const gameName = getGameDisplayName(
          { id: gameId, name: gameResults[gameId]?.name ?? gameDefinitionNameById[gameId] },
          t('hole.gameFallbackName')
        );
        return {
          id: gameId,
          name: gameName,
          points: leaderboardValue?.points ?? 0,
          position: leaderboardValue?.position,
          icon: resolveGameRowIcon(gameId, gameName),
        };
      })
      // Zeros last, then by value: a hazard's -1 says something happened, a 0 says nothing did,
      // so -1 belongs above the games the player never scored in rather than at the very bottom.
      .sort(
        (a, b) =>
          (a.points === 0 ? 1 : 0) - (b.points === 0 ? 1 : 0)
          || b.points - a.points
          || a.name.localeCompare(b.name),
      );
  }, [gameDefinitionNameById, gameResults, leaderboardGamePoints, resolveGameRowIcon, selectedGameDefinitionIds, selectedPlayer]);
  const loggedInRoundPlayer = useMemo(() => {
    const userId = normalizePlayerKey(user?.id);
    if (loggedInPlayerId) {
      const byResolvedId = players.find((player) => normalizePlayerKey(player.id) === normalizePlayerKey(loggedInPlayerId));
      if (byResolvedId) {
        return byResolvedId;
      }
    }
    if (userId) {
      return players.find((player) => normalizePlayerKey(player.id) === userId) ?? null;
    }
    return null;
  }, [loggedInPlayerId, players, user?.id]);
  // Automatic shot marking (microphone + GPS). Android-only and opt-in — a no-op
  // everywhere else, and only ever for the logged-in player's own shots.
  const { trackingActive } = useShotTracking({
    roundId,
    playerId: loggedInRoundPlayer?.id ?? null,
    holeNumber,
    isRoundActive: !isAnonymousReadOnlyMode && !isInviteViewer,
    getSender: () => isolatedSocketRef.current,
    subjectKey: offlineSubjectKey,
  });
  const ownHole = useRoundShots(
    isolatedSocketRef.current,
    roundId,
    loggedInRoundPlayer?.id ?? null,
    holeNumber
  );
  const shotDistanceUnit = useDistanceUnit();
  const shotTrackerText = useMemo(() => {
    const { shots: ownShots, putts: ownPutts } = ownHole;
    if (ownShots.length === 0 && ownPutts === 0) {
      // A live session with nothing heard yet says "0 detected" — silence and
      // "not running" must never look identical on the course.
      return trackingActive ? t('hole.shotTracker.countOnly', { count: 0 }) : undefined;
    }
    // Putts are counted, never measured, and never mixed into the shot list.
    const puttSuffix = ownPutts > 0 ? t('hole.shotTracker.putts', { putts: ownPutts }) : '';
    if (ownShots.length === 0) {
      return t('hole.shotTracker.countOnly', { count: 0 }) + puttSuffix;
    }
    // The newest shot never has a distance yet; the latest finished one does.
    const lastMeasured = [...ownShots].reverse().find((shot) => shot.distanceMeters !== null);
    if (lastMeasured?.distanceMeters != null) {
      return (
        t('hole.shotTracker.lastShot', {
          count: ownShots.length,
          distance: formatHoleDistance(lastMeasured.distanceMeters, shotDistanceUnit),
        }) + puttSuffix
      );
    }
    return t('hole.shotTracker.countOnly', { count: ownShots.length }) + puttSuffix;
  }, [ownHole, shotDistanceUnit, trackingActive]);
  // Shot detail is personal, so it is offered only on your own score — a marker filling in the
  // rest of the group is not slowed down by it.
  const showEnhancedInput =
    enhancedInputEnabled &&
    !!scorePopupPlayer &&
    !!loggedInRoundPlayer &&
    normalizePlayerKey(scorePopupPlayer.id) === normalizePlayerKey(loggedInRoundPlayer.id);
  const holeMenuItems = useMemo(() => {
    const loggedInPlayerKey = normalizePlayerKey(loggedInRoundPlayer?.id ?? loggedInPlayerId ?? null);
    const loggedInPlayerNameKey = normalizePlayerKey(loggedInRoundPlayer?.name ?? null);

    const ownEntriesByHole = new Map<number, HoleEntry>();
    const playedHoles = new Set<number>();

    allHoleEntries.forEach((entry) => {
      if (entry.holeNumber < 1 || entry.holeNumber > HOLE_MENU_COUNT) {
        return;
      }

      playedHoles.add(entry.holeNumber);

      const entryPlayerIdKey = normalizePlayerKey(entry.playerId ?? null);
      const entryPlayerNameKey = normalizePlayerKey(entry.playerName ?? null);
      const isOwnEntryById = !!loggedInPlayerKey && !!entryPlayerIdKey && entryPlayerIdKey === loggedInPlayerKey;
      const isOwnEntryByName = !isOwnEntryById && !!loggedInPlayerNameKey && entryPlayerNameKey === loggedInPlayerNameKey;

      if (isOwnEntryById || isOwnEntryByName) {
        const existing = ownEntriesByHole.get(entry.holeNumber);
        if (!existing) {
          ownEntriesByHole.set(entry.holeNumber, entry);
          return;
        }
        const existingHasScore = existing.grossStrokes !== null && existing.grossStrokes !== undefined;
        const candidateHasScore = entry.grossStrokes !== null && entry.grossStrokes !== undefined;
        const existingCreatedAt = Date.parse(existing.createdAt ?? '');
        const candidateCreatedAt = Date.parse(entry.createdAt ?? '');
        const existingTs = Number.isFinite(existingCreatedAt) ? existingCreatedAt : Number.MIN_SAFE_INTEGER;
        const candidateTs = Number.isFinite(candidateCreatedAt) ? candidateCreatedAt : Number.MIN_SAFE_INTEGER;
        if (candidateTs === existingTs && candidateHasScore !== existingHasScore) {
          if (candidateHasScore) {
            ownEntriesByHole.set(entry.holeNumber, entry);
          }
          return;
        }
        if (!Number.isFinite(existingCreatedAt) && !Number.isFinite(candidateCreatedAt) && candidateHasScore && !existingHasScore) {
          ownEntriesByHole.set(entry.holeNumber, entry);
          return;
        }
        if (candidateTs >= existingTs) {
          ownEntriesByHole.set(entry.holeNumber, entry);
        }
      }
    });

    return Array.from({ length: HOLE_MENU_COUNT }, (_, index) => {
      const number = index + 1;
      const ownEntry = ownEntriesByHole.get(number);
      const ownScore = ownEntry
        ? ownEntry.grossStrokes === null
          ? ownEntry.pickup
            ? t('hole.pickup')
            : t('hole.menuScoreMissing')
          : String(ownEntry.grossStrokes)
        : t('hole.menuScoreMissing');
      return {
        holeNumber: number,
        isPlayed: playedHoles.has(number),
        ownScore,
      };
    });
  }, [allHoleEntries, loggedInPlayerId, loggedInRoundPlayer?.id, loggedInRoundPlayer?.name]);
  const closeHoleMenu = useCallback(() => {
    setHoleMenuOpen(false);
  }, []);
  const selectHoleFromMenu = useCallback(
    (targetHoleNumber: number) => {
      setHoleMenuOpen(false);
      if (targetHoleNumber === holeNumber) {
        return;
      }
      navigateToHole(targetHoleNumber);
    },
    [holeNumber, navigateToHole]
  );
  const holeMenuPopup = holeMenuOpen ? (
    <View style={styles.holeMenuPopupLayer} pointerEvents="box-none">
      <Pressable style={styles.holeMenuPopupBackdrop} onPress={closeHoleMenu}>
        <Pressable style={styles.holeMenuPopupCard} onPress={() => undefined}>
          <Text style={styles.modalTitle}>{t('hole.menuTitle')}</Text>
          {/* An action rather than a hole to jump to, so it sits above the hole list. Scoped to this
              round, which is the only view of the gallery that makes sense from inside a round. */}
          {tourId ? (
            <Pressable
              style={styles.holeMenuRow}
              onPress={() => {
                closeHoleMenu();
                router.push(`/tour/${tourId}/photos?roundId=${roundId}`);
              }}
              accessibilityRole="button"
              testID="hole-menu-photos"
            >
              <View style={styles.holeMenuRowLeft}>
                <Text style={styles.holeMenuRowTitle}>{t('tourPhotos.openAction')}</Text>
              </View>
            </Pressable>
          ) : null}
          <ScrollView style={styles.holeMenuScroll} contentContainerStyle={styles.holeMenuScrollContent}>
            {holeMenuItems.map((item) => (
              <Pressable
                key={`hole-menu-${item.holeNumber}`}
                style={[styles.holeMenuRow, item.holeNumber === holeNumber && styles.holeMenuRowCurrent]}
                onPress={() => selectHoleFromMenu(item.holeNumber)}
              >
                <View style={styles.holeMenuRowLeft}>
                  <Text style={styles.holeMenuRowTitle}>{`${t('hole.title')} ${item.holeNumber}`}</Text>
                  {item.holeNumber === holeNumber ? <Text style={styles.holeMenuCurrent}>{t('hole.menuCurrent')}</Text> : null}
                </View>
                <View style={styles.holeMenuRowRight}>
                  <Text style={styles.holeMenuStatus}>{item.isPlayed ? t('hole.menuPlayed') : t('hole.menuNotPlayed')}</Text>
                  <Text style={styles.holeMenuScore}>{t('hole.menuMyScore', { score: item.ownScore })}</Text>
                </View>
              </Pressable>
            ))}
          </ScrollView>
        </Pressable>
      </Pressable>
    </View>
  ) : null;
  /**
   * Buckets ranked entries by the ball they were scored in.
   *
   * Team and match games are played inside a ball, so every ball has its own winner and they all
   * earn the same merits. Ranking or tie-checking across balls therefore made a team that won its
   * ball outright read as sharing first place with strangers it never played against.
   */
  const byBall = useCallback(
    <T,>(entries: T[], ballOf: (entry: T) => number | null): T[][] => {
      const buckets = new Map<number | null, T[]>();
      entries.forEach((entry) => {
        const key = ballOf(entry);
        const bucket = buckets.get(key) ?? [];
        bucket.push(entry);
        buckets.set(key, bucket);
      });
      return Array.from(buckets.values());
    },
    [],
  );
  const gamePositionsByPlayer = useMemo(() => {
    const map: Record<string, Record<string, number>> = {};
    const positionSeed: Record<string, Record<string, number>> = {};
    leaderboardTotals.forEach((total) => {
      const playerId = total.playerId;
      if (!playerId || !total.roundGamePoints) return;
      total.roundGamePoints.forEach((gp) => {
        const gameId = gp.gameId ?? 'game';
        map[gameId] ??= {};
        map[gameId][playerId] = gp.roundGamePoints;
        if (gp.position !== undefined) {
          positionSeed[gameId] ??= {};
          positionSeed[gameId][playerId] = gp.position;
        }
      });
    });
    const positionMap: Record<string, Record<string, number>> = {};
    Object.entries(map).forEach(([gameId, pointsByPlayer]) => {
      const isTeamGame = gameDefinitionTypes[gameId] === 'team';
      if (isTeamGame && orderedTeams.length > 0) {
        const teamEntries = orderedTeams
          .map((team) => {
            const members = team.players ?? [];
            const memberPoints = members
              .map((pid) => pointsByPlayer[pid])
              .filter((value): value is number => typeof value === 'number');
            if (memberPoints.length === 0) return null;
            return {
              teamId: team.id,
              points: Math.max(...memberPoints),
              players: members,
              ballNumber: team.ballNumber ?? null,
            };
          })
          .filter(
            (entry): entry is { teamId: string; points: number; players: string[]; ballNumber: number | null } =>
              entry !== null,
          );
        byBall(teamEntries, (entry) => entry.ballNumber).forEach((entriesInBall) => {
          const sorted = [...entriesInBall].sort((a, b) => b.points - a.points);
          let positionIndex = 1;
          for (let i = 0; i < sorted.length; ) {
            const currentPoints = sorted[i].points;
            const tieGroup = sorted.slice(i).filter((e) => e.points === currentPoints);
            tieGroup.forEach((entry) => {
              entry.players.forEach((playerId) => {
                positionMap[gameId] ??= {};
                positionMap[gameId][playerId] = positionIndex;
              });
            });
            positionIndex += tieGroup.length;
            i += tieGroup.length;
          }
        });
        return;
      }
      if (!isTeamGame && positionSeed[gameId]) {
        positionMap[gameId] = { ...positionSeed[gameId] };
        return;
      }
      const entries = Object.entries(pointsByPlayer).map(([playerId, points]) => ({ playerId, points }));
      byBall(entries, (entry) => resolveBallNumber(entry.playerId, null)).forEach((entriesInBall) => {
        const sorted = [...entriesInBall].sort((a, b) => b.points - a.points);
        let positionIndex = 1;
        for (let i = 0; i < sorted.length; ) {
          const currentPoints = sorted[i].points;
          const tieGroup = sorted.slice(i).filter((e) => e.points === currentPoints);
          tieGroup.forEach((entry) => {
            positionMap[gameId] ??= {};
            positionMap[gameId][entry.playerId] = positionIndex;
          });
          positionIndex += tieGroup.length;
          i += tieGroup.length;
        }
      });
    });
    return positionMap;
  }, [byBall, gameDefinitionTypes, leaderboardTotals, orderedTeams, resolveBallNumber]);
  const positionShareByGame = useMemo(() => {
    const sharedByGame: Record<string, Record<string, boolean>> = {};
    const pointsByGame: Record<string, Record<string, number>> = {};
    leaderboardTotals.forEach((total) => {
      const playerId = total.playerId;
      if (!playerId || !total.roundGamePoints) return;
      total.roundGamePoints.forEach((gp) => {
        const gameId = gp.gameId ?? 'game';
        pointsByGame[gameId] ??= {};
        pointsByGame[gameId][playerId] = gp.roundGamePoints;
      });
    });

    Object.entries(pointsByGame).forEach(([gameId, pointsByPlayer]) => {
      const groupId = getGameGroupId(gameId, gameDefinitionNameById[gameId]);
      if (!gameGroupHasPositions(groupId)) return;
      // The group is the fallback for a definition whose type never reached the client: without it
      // a team game falls to the per-player branch, where two team mates on equal points read as a
      // shared position even though no other team is level with them.
      const isTeamGame = gameDefinitionTypes[gameId] === 'team' || groupId === 'team';
      if (isTeamGame && orderedTeams.length > 0) {
        const teamEntries = orderedTeams
          .map((team) => {
            const members = team.players ?? [];
            const memberPoints = members
              .map((pid) => pointsByPlayer[pid])
              .filter((value): value is number => typeof value === 'number');
            if (memberPoints.length === 0) return null;
            return {
              teamId: team.id,
              points: Math.max(...memberPoints),
              players: members,
              ballNumber: team.ballNumber ?? null,
            };
          })
          .filter(
            (entry): entry is { teamId: string; points: number; players: string[]; ballNumber: number | null } =>
              entry !== null,
          );
        byBall(teamEntries, (entry) => entry.ballNumber).forEach((entriesInBall) => {
          const sorted = [...entriesInBall].sort((a, b) => b.points - a.points);
          for (let i = 0; i < sorted.length; ) {
            const currentPoints = sorted[i].points;
            const tieGroup = sorted.slice(i).filter((e) => e.points === currentPoints);
            if (tieGroup.length > 1) {
              tieGroup.forEach((entry) => {
                entry.players.forEach((playerId) => {
                  sharedByGame[gameId] ??= {};
                  sharedByGame[gameId][playerId] = true;
                });
              });
            }
            i += tieGroup.length;
          }
        });
        return;
      }

      // Same rule for an individual game: a position is only shared with someone in the same ball,
      // so the count is keyed by ball as well as by position.
      const positionKey = (playerId: string, pos: number) =>
        `${resolveBallNumber(playerId, null) ?? 'all'}:${pos}`;
      const positionCounts: Record<string, number> = {};
      Object.entries(pointsByPlayer).forEach(([playerId]) => {
        const pos = gamePositionsByPlayer[gameId]?.[playerId];
        if (!pos) return;
        const key = positionKey(playerId, pos);
        positionCounts[key] = (positionCounts[key] ?? 0) + 1;
      });
      Object.entries(pointsByPlayer).forEach(([playerId]) => {
        const pos = gamePositionsByPlayer[gameId]?.[playerId];
        if (!pos) return;
        if ((positionCounts[positionKey(playerId, pos)] ?? 0) > 1) {
          sharedByGame[gameId] ??= {};
          sharedByGame[gameId][playerId] = true;
        }
      });
    });

    return sharedByGame;
  }, [byBall, gameDefinitionNameById, gameDefinitionTypes, gamePositionsByPlayer, leaderboardTotals, orderedTeams, resolveBallNumber]);
  // The hole-complete modal shows only the top of the round board, so each game the round is
  // playing gets its own podium: the leading team for a team game, the first two places otherwise.
  //
  // Only the match and team groups. Hazard and achievement games score in ones and zeroes, so
  // most of the field ties on the lead and the podium says nothing; main games (Stableford,
  // Stroke Play) already *are* the round board shown right above.
  const leaderboardChangeGameLines = useMemo(
    () =>
      buildLeaderboardChangeGameLines({
        rows: gameResultRows.filter((row) => {
          const group = getGameGroupId(row.key, row.name);
          return group === 'match' || group === 'team';
        }),
        gameResultPlayers,
        gameDefinitionTypes,
        orderedTeams,
      }),
    [gameDefinitionTypes, gameResultPlayers, gameResultRows, orderedTeams]
  );
  const defaultTeamColor = appColors.text.primary;
  const defaultScoreColor = '#A1D493';
  const hasSelectedTeamGame = useMemo(
    () =>
      selectedGameDefinitionIds.some(
        (gameId) => (gameDefinitionTypes[gameId] ?? '').toLowerCase() === 'team'
      ),
    [gameDefinitionTypes, selectedGameDefinitionIds]
  );
  const hasActiveTeamPlay = hasSelectedTeamGame && orderedTeams.length > 0;
  const teamColors = useMemo(() => {
    const palette = [appColors.text.primary, '#8FA08B', '#A1D493', '#44504a', '#6a5c2d', '#7a3a46'];
    const map: Record<string, string> = {};
    if (orderedTeams.length > 0) {
      orderedTeams.forEach((team, idx) => {
        const color = palette[idx % palette.length];
        (team.players ?? []).forEach((pid) => {
          map[pid] = color;
        });
      });
      return map;
    }
    gameResultPlayers.forEach((p, idx) => {
      const teamIdx = Math.floor(idx / 2);
      map[p.id] = palette[teamIdx % palette.length];
    });
    return map;
  }, [gameResultPlayers, orderedTeams]);
  const teamColorByKey = useMemo(() => {
    const map: Record<string, string> = { ...teamColors };
    players.forEach((player) => {
      const color = teamColors[player.id];
      if (!color) return;
      map[player.name] = color;
      map[normalizePlayerKey(player.name)] = color;
      map[normalizePlayerKey(player.id)] = color;
    });
    return map;
  }, [players, teamColors]);
  const teamLabelByPlayer = useMemo(() => {
    const map: Record<string, string> = {};
    orderedTeams.forEach((team) => {
      const teamName = (team.name ?? '').trim();
      const label = teamName.length > 0 && !isUuidLike(teamName) ? teamName : null;
      if (!label) {
        return;
      }
      (team.players ?? []).forEach((pid) => {
        map[pid] = label;
      });
    });
    players.forEach((player) => {
      const label = map[player.id];
      if (label) {
        map[player.name] = label;
        map[normalizePlayerKey(player.name)] = label;
        map[normalizePlayerKey(player.id)] = label;
      }
    });
    return map;
  }, [orderedTeams, players]);
  const resolveTeamLabel = useCallback(
    (key?: string) => {
      if (!key) return null;
      return teamLabelByPlayer[normalizePlayerKey(key)] ?? teamLabelByPlayer[key] ?? null;
    },
    [teamLabelByPlayer]
  );
  const resolveTeamColor = useCallback(
    (key?: string) => {
      if (!hasActiveTeamPlay) return defaultTeamColor;
      if (!key) return defaultTeamColor;
      return teamColorByKey[normalizePlayerKey(key)] ?? teamColorByKey[key] ?? defaultTeamColor;
    },
    [defaultTeamColor, hasActiveTeamPlay, teamColorByKey]
  );
  const hasRealtimeGameData = gameResultRows.length > 0 || Object.keys(gameResults).length > 0;
  const shouldShowGamePanels = hasSelectedGames || hasRealtimeGameData;
  const leaderboardPanel = !shouldShowGamePanels ? (
    <View style={styles.scoreboard} lightColor="#07200D" darkColor="#07200D">
      <Text style={styles.scoreboardEmpty}>{t('hole.noGamesSelected')}</Text>
    </View>
  ) : displayTotals.length > 0 ? (
    <View style={styles.scoreboard} lightColor="#07200D" darkColor="#07200D">
      <View style={styles.scoreboardColumnsHeader}>
        <Text style={styles.scoreboardNameHeader}>{t('hole.leaderboardShortTitle')}</Text>
        <View style={styles.scoreboardScores}>
          <Text style={styles.scoreboardScoreColumnHeader}>{t('hole.roundColumn')}</Text>
          <Text style={styles.scoreboardScoreColumnHeader}>{t('hole.tourColumn')}</Text>
        </View>
      </View>
      {(leaderboardExpanded ? displayTotals : displayTotals.slice(0, LEADERBOARD_COLLAPSED_COUNT)).map((total) => (
        <Pressable
          key={total.key}
          style={styles.scoreboardRow}
          onPress={() => setSelectedPlayerId(total.key)}
        >
          {(() => {
            const placement =
              tourPlacementByKey[normalizePlayerKey(total.key)] ??
              tourPlacementByKey[normalizePlayerKey(total.name)];
            const roundPlacement =
              roundPlacementByKey[normalizePlayerKey(total.key)] ??
              roundPlacementByKey[normalizePlayerKey(total.name)];
            return (
          <View style={styles.scoreboardNameRow}>
            <View style={styles.scoreboardNameContent}>
              {renderBallBadge(total.key, total.name)}
              {renderPlacementBadge(roundPlacement, placement)}
              {/* One fixed-width slot, always present even when empty, so every name in the
                  leaderboard starts at the same x. The ghost and the player's hazards take turns
                  in it rather than lining up beside each other and pushing the name along. */}
              {(() => {
                const isAi = aiPlayerKeys.has(normalizePlayerKey(total.key))
                  || aiPlayerKeys.has(normalizePlayerKey(total.name));
                const isGhost = ghostPlayerKeys.has(normalizePlayerKey(total.key))
                  || ghostPlayerKeys.has(normalizePlayerKey(total.name));
                const slotItems = [
                  ...(isAi || isGhost
                    ? [isAi
                        ? <GhostAvatar key="ghost" size={18} />
                        : <MaterialCommunityIcons key="ghost" name="ghost" size={14} color="#8FA3B8" />]
                    : []),
                  ...hazardTypesInOrder(
                    roundHazardTypesFor(total.key, total.name),
                  ).map((type) => (hazardAnimationsEnabled ? (
                    <Image
                      key={type}
                      source={hazardCritterForType(type)}
                      style={styles.hazardCritter}
                      resizeMode="contain"
                      accessibilityLabel={t(HAZARD_CHIPS[type].labelKey)}
                    />
                  ) : (
                    <View key={type} style={[styles.compactScoreHazardBadge, HAZARD_CHIPS[type].style]}>
                      <Text style={[styles.compactScoreHazardBadgeTextBase, HAZARD_CHIPS[type].textStyle]}>
                        {t(HAZARD_CHIPS[type].labelKey).charAt(0)}
                      </Text>
                    </View>
                  ))),
                ];
                return (
                  <View style={styles.scoreboardHazardSlot}>
                    <CyclingSlot items={slotItems} />
                  </View>
                );
              })()}
              {mysteryWinnerIdsForHole.has(total.key) ? (
                <Image source={MysteryPrizeIcon} style={styles.mysteryWinnerIcon} resizeMode="contain" />
              ) : null}
              <Text style={[styles.scoreboardName, { color: resolveTeamColor(total.key ?? total.name) }]}>
                {`${(() => {
                  const placement =
                    tourPlacementByKey[normalizePlayerKey(total.key)] ??
                    tourPlacementByKey[normalizePlayerKey(total.name)];
                  const isTourLeader =
                    tourLeaderKeys.has(normalizePlayerKey(total.key)) || tourLeaderKeys.has(normalizePlayerKey(total.name));
                  return isTourLeader && !placement ? '🏆 ' : '';
                })()}${total.name}`}
                {(() => {
                  const teamLabel = resolveTeamLabel(total.key ?? total.name);
                  return teamLabel ? ` (${teamLabel})` : '';
                })()}
              </Text>
            </View>
            <View style={styles.infoIconSmall}>
              <Text style={styles.infoIconSmallText}>i</Text>
            </View>
          </View>
            );
          })()}
          <View style={styles.scoreboardScores}>
            <Text style={[styles.scoreboardValue, { color: resolveTeamColor(total.key ?? total.name) }]}>
              {total.roundPoints !== undefined ? total.roundPoints : t('hole.roundPointsMissing')}
            </Text>
            <Text style={[styles.scoreboardValue, { color: resolveTeamColor(total.key ?? total.name) }]}>
              {total.totalStrokes}
            </Text>
          </View>
        </Pressable>
      ))}
      {displayTotals.length > LEADERBOARD_COLLAPSED_COUNT ? (
        <Pressable
          style={styles.leaderboardToggle}
          onPress={() => setLeaderboardExpanded((prev) => !prev)}
          accessibilityRole="button"
          testID="leaderboard-toggle"
        >
          <Text style={styles.leaderboardToggleText}>
            {leaderboardExpanded
              ? t('hole.leaderboardShowLess')
              : t('hole.leaderboardShowAll', { count: String(displayTotals.length) })}
          </Text>
          <Ionicons name={leaderboardExpanded ? 'chevron-up' : 'chevron-down'} size={16} color="#8FA88F" />
        </Pressable>
      ) : null}
    </View>
  ) : null;
  const resolveMatchTeamLabel = (status: MatchStatus) => {
    if (status.teamName) return status.teamName;
    const names = status.teamPlayerIds
      .map((pid) => players.find((player) => player.id === pid)?.name)
      .filter((name): name is string => !!name)
      .map((name) => compactPlayerName(name));
    return names.length > 0 ? names.join(' & ') : t('hole.matchTeamFallback');
  };
  // The games table is read one ball at a time. Showing every ball at once put eight columns on a
  // phone, and a player only competes inside their own ball anyway. Columns follow the input rows
  // above, so a name sits in the same place in both.
  const gamesBallNumbers = useMemo(() => {
    const numbers = playerRowsByBall
      .map((row) => row.ballNumber)
      .filter((ballNumber): ballNumber is number => ballNumber !== null);
    return Array.from(new Set(numbers)).sort((a, b) => a - b);
  }, [playerRowsByBall]);
  const loggedInBallNumber = useMemo(
    () =>
      loggedInRoundPlayer
        ? resolveBallNumber(loggedInRoundPlayer.id, loggedInRoundPlayer.name)
        : null,
    [loggedInRoundPlayer, resolveBallNumber],
  );
  // Falls through to the player's own ball, so the table opens on the group they are standing in.
  const activeGamesBall =
    selectedGamesBall !== null && gamesBallNumbers.includes(selectedGamesBall)
      ? selectedGamesBall
      : loggedInBallNumber ?? gamesBallNumbers[0] ?? null;
  /**
   * The columns to draw, as indexes into `gameResultPlayers` — every row's `scores`, `leaders` and
   * `losers` are positional against that list, so reordering or filtering the columns means
   * carrying the original index rather than rebuilding the player array.
   */
  const gameResultColumns = useMemo(() => {
    const indexByPlayerId = new Map(gameResultPlayers.map((player, index) => [player.id, index]));
    const inRowOrder = playerRowsByBall
      .filter((row) => !hasMultipleBalls || activeGamesBall === null || row.ballNumber === activeGamesBall)
      .map((row) => ({ player: row.player, index: indexByPlayerId.get(row.player.id) }))
      .filter((column): column is { player: Player; index: number } => column.index !== undefined);
    // A player the games table knows but the row list does not still deserves a column.
    return inRowOrder.length > 0 || hasMultipleBalls
      ? inRowOrder
      : gameResultPlayers.map((player, index) => ({ player, index }));
  }, [activeGamesBall, gameResultPlayers, hasMultipleBalls, playerRowsByBall]);

  const gameResultsPanel = gameResultRows.length > 0 ? (
    <View style={styles.scoreboard} lightColor="#07200D" darkColor="#07200D">
      {hasMultipleBalls && gamesBallNumbers.length > 1 ? (
        <View style={styles.gamesBallTabs}>
          {gamesBallNumbers.map((ballNumber) => {
            const isActive = ballNumber === activeGamesBall;
            return (
              <Pressable
                key={`games-ball-${ballNumber}`}
                testID={`games-ball-tab-${ballNumber}`}
                style={[styles.gamesBallTab, isActive && styles.gamesBallTabActive]}
                onPress={() => setSelectedGamesBall(ballNumber)}
                accessibilityRole="button"
                accessibilityState={{ selected: isActive }}
                accessibilityLabel={t('hole.ballNumberLabel', { number: String(ballNumber) })}
              >
                <Text style={[styles.gamesBallTabText, isActive && styles.gamesBallTabTextActive]}>
                  {t('hole.ballNumberLabel', { number: String(ballNumber) })}
                </Text>
              </Pressable>
            );
          })}
        </View>
      ) : null}
      <View style={styles.gameResultsFrame}>
        <View style={[styles.gameResultsRow, styles.gameResultsHeader]}>
          <Text style={[styles.scoreboardTitle, styles.gameResultsName]}>{t('hole.gamesTitle')}</Text>
          {gameResultColumns.map(({ player }) => (
            <Text
              key={player.id}
              style={[styles.gameResultsPlayer, { color: hasActiveTeamPlay ? (teamColors[player.id] ?? defaultTeamColor) : defaultTeamColor }]}
              numberOfLines={1}
              ellipsizeMode="tail"
            >
              {compactPlayerName(player.name)}
            </Text>
          ))}
        </View>
        {groupedGameResultRows.map((group) => (
          <View key={group.groupId} style={styles.gameResultsGroup}>
            <Text style={styles.gameResultsGroupHeader}>{group.title}</Text>
            {group.items.map((row) => (
              <View key={row.key} style={styles.gameResultsRow}>
                <Text style={styles.gameResultsName} numberOfLines={1} ellipsizeMode="tail">
                  {row.name}
                </Text>
                {gameResultColumns.map(({ player, index: idx }) => (
                  <View key={`${row.key}-${player.id}`} style={styles.gameResultCell}>
                    <View
                      style={[
                        styles.gameResultValueWrapper,
                        !row.isHazardGame && row.sharedLeaders?.[idx] ? styles.gameResultLeaderShared : null,
                      ]}
                    >
                      <View style={styles.gameResultValueContent}>
                        {!row.isHazardGame && row.leaders[idx] ? <Text style={styles.gameResultMedalText}>🥇</Text> : null}
                        {row.isHazardGame && row.losers?.[idx] ? <Text style={styles.gameResultFishText}>🐟</Text> : null}
                        <Text
                          style={[
                            styles.gameResultValue,
                            {
                              color: hasActiveTeamPlay
                                ? (teamColors[player.id] ?? defaultScoreColor)
                                : defaultScoreColor,
                            },
                          ]}
                        >
                          {row.scores[idx] ?? '–'}
                        </Text>
                      </View>
                    </View>
                  </View>
                ))}
              </View>
            ))}
          </View>
        ))}
      </View>
    </View>
  ) : null;
  const openMysteryPrizeSetup = () => {
    if (!roundId) return;
    const params = [
      `tourId=${tourId ?? ''}`,
      `debug=${debug ? 'true' : 'false'}`,
      `returnHole=${holeNumber}`,
    ];
    router.push(`/round/${roundId}/mystery-prize?${params.join('&')}`);
  };
  // Dormie / match-won banners render in the header next to the skins banner so
  // match-deciding state is visible without scrolling to the games table.
  const matchStatusBanners = (showsCurrentMatchState ? matchStatuses : []).map((status) => ({
    key: `match-status-${status.bannerKey ?? status.gameId}`,
    icon: status.state === 'won' ? '🏆' : '🔒',
    text: (() => {
      const body = t(status.state === 'won' ? 'hole.matchWon' : 'hole.matchDormie', {
        game: status.gameName,
        team: resolveMatchTeamLabel(status),
        lead: status.lead,
        holes: status.holesRemaining,
      });
      return status.ballNumber
        ? `${t('hole.ballHeading', { number: String(status.ballNumber) })}: ${body}`
        : body;
    })(),
    color: resolveTeamColor(status.teamPlayerIds[0]),
  }));
  const holeHeader = (
    <HoleHeaderCard
      holeNumber={holeNumber}
      showLeaderboardLabel={false}
      skinsCarryText={skinsBannerText}
      matchStatusBanners={matchStatusBanners}
      hasMysteryPrizeForHole={hasMysteryPrizeForHole}
      mysteryPrizeText={mysteryPrizeText}
      onOpenMystery={() => setMysteryModalOpen(true)}
      showMysteryPrizeButton={isMysterySelected && !allPlayersHaveMystery}
      onOpenMysteryPrizeSetup={openMysteryPrizeSetup}
      holeName={currentHoleMeta.name}
      holeDescription={currentHoleMeta.description}
      showHoleDetails={showHoleDetails}
      onToggleDetails={() => setShowHoleDetails((prev) => !prev)}
      shotTrackerText={shotTrackerText}
    />
  );
  const viewModel: HoleScreenViewModel = {
    state: {
      roundId,
      holeNumber,
      players,
      holeEntries,
      totals,
      leaderboardTotals,
      gameResults,
      holeMeta,
      holePar,
      teams,
      holeEvents,
      subgames,
      loading,
      error,
    },
    derived: {
      displayTotals,
      gameResultRows,
      hasSelectedGames,
      strokeIndexForHole,
    },
    actions: {
      navigateToHole,
      finishRound,
    },
    uiState: {
      mysteryModalOpen,
      nextMysteryModalOpen,
      scorePopupPlayerId,
    },
  };

  if (!isNavigationReady) {
    return null;
  }

  if (Platform.OS === 'web' && !isClient) {
    return null;
  }

  if (!user && !isInviteViewer && !claimSessionLoaded) {
    return null;
  }

  if (!user && !isGuestClaimMode && !isInviteViewer) {
    return <Redirect href="/" />;
  }

  if (!roundId) {
    return (
      <View style={styles.container}>
        <Text style={styles.subtitle}>{t('hole.missingRound')}</Text>
        <GoffyButton
          label={t('hole.goPlayers')}
          onPress={() => {
            const debugSuffix = debug ? '?debug=true' : '';
            const nextPath = tourId ? `/tour/${tourId}/round-init${debugSuffix}` : '/my-tours';
            router.push(nextPath);
          }}
          tone="accent"
          oldButtonStyle={styles.primaryButton}
          oldTextStyle={styles.primaryButtonText}
          style={styles.primaryButtonImageMode}
          textStyle={styles.primaryButtonImageText}
        />
      </View>
    );
  }

  if (!navState?.key) {
    return null;
  }

  return (
      <View style={[styles.compactRoot, { minHeight: windowHeight }]}>
        <ScrollView
          contentContainerStyle={[styles.scrollContent, styles.compactScrollContent]}
          style={[styles.scroll, styles.compactScroll]}
          showsVerticalScrollIndicator
          nestedScrollEnabled
          keyboardShouldPersistTaps="handled"
          scrollEnabled={!scorePopupPlayerId}
        >
          <View style={[styles.pageBackground, { minHeight: windowHeight }]}>
            <View style={[styles.container, styles.containerCompact]}>
              {holeHeader}
              <ConnectionStatusPill client={socketClient} subjectKey={offlineSubjectKey} />
              {debug ? (
                <>
                  <View style={styles.metaRow}>
                    {roundId ? <Text style={styles.meta}>{t('hole.roundId', { id: roundId })}</Text> : null}
                  </View>
                  {tourId ? <Text style={styles.meta}>{t('hole.tourId', { id: tourId })}</Text> : null}
                </>
              ) : null}
              {viewModel.state.loading && viewModel.state.players.length === 0 ? <Text style={styles.subtitle}>{t('hole.loading')}</Text> : null}
              {viewModel.state.error ? <Text style={styles.error}>{viewModel.state.error}</Text> : null}
              {leaderboardPanel}
              {viewModel.state.players.length === 0 && !viewModel.state.loading ? <Text style={styles.subtitle}>{t('hole.noPlayers')}</Text> : null}
              <View style={styles.compactList}>
              {playerRowsByBall.map(({ player, ballNumber, startsBall }, playerIndex) => (
                (() => {
                  const existing = hasExactHoleData ? holeEntries.find((entry) => {
                    if (entry.holeNumber !== holeNumber) return false;
                    const entryName = (entry.playerName ?? '').trim().toLowerCase();
                    const playerName = player.name.trim().toLowerCase();
                    return entry.playerId === player.id || entryName === playerName || entry.playerName === player.id;
                  }) : undefined;
                  const selectedFromExisting =
                    existing && existing.grossStrokes !== null
                      ? String(existing.grossStrokes)
                      : existing?.pickup
                        ? 'Pickup'
                        : undefined;
                  const selectedScore =
                    hasExactHoleData
                      ? (
                        scores[player.id] ??
                        scores[player.name] ??
                        selectedFromExisting
                      )
                      : undefined;
                  return (
                <Fragment key={player.id}>
                {startsBall && ballNumber !== null ? (
                  <View style={styles.ballHeading}>
                    <View style={styles.ballBadge}>
                      <Text style={styles.ballBadgeText}>{ballNumber}</Text>
                    </View>
                    <Text style={styles.ballHeadingText}>{t('hole.ballHeading', { number: String(ballNumber) })}</Text>
                  </View>
                ) : null}
                <View testID={`hole-player-row-${player.id}`} style={styles.compactRow}>
                  <View style={styles.compactInfoColumn}>
                    <View style={styles.compactNameRow}>
                      <View style={styles.compactNameBlock}>
                      {(() => {
                        const avatarSource = getPlayerAvatarImageSource(apiBaseUrl, player);
                        // The photo holds the slot longest, then each hazard takes a shorter turn.
                        const avatarCycle = [
                          ...(avatarSource
                            ? [<Image
                                key="avatar"
                                source={avatarSource}
                                style={styles.slotFill}
                                accessibilityLabel={player.name}
                              />]
                            : []),
                          // Round-wide, not this hole's: beside a player's name the question is who
                          // has found trouble at all, so a bunker on hole 3 still shows on hole 12.
                          // The score button stays per-hole — that one is about the score being
                          // entered right now.
                          ...(hazardAnimationsEnabled
                            ? hazardTypesInOrder(
                                roundHazardTypesFor(player.id, player.name),
                              ).map((type) => (
                                <Image
                                  key={type}
                                  source={hazardCritterForType(type)}
                                  style={styles.slotFill}
                                  resizeMode="contain"
                                  accessibilityLabel={t(HAZARD_CHIPS[type].labelKey)}
                                />
                              ))
                            : []),
                        ];
                        return (
                          <View style={styles.compactNameLine}>
                            <View style={styles.compactAvatarWrap}>
                              {player.isAi || isGhostPlayerName(player.name) ? (
                                <GhostAvatar size={28} />
                              ) : avatarCycle.length > 0 ? (
                                <CyclingSlot items={avatarCycle} style={styles.slotFill} />
                              ) : null}
                            </View>
                            <Text style={styles.compactName}>
                              {playerHasRealName(player.name) ? player.name : t('hole.unknownPlayer')}
                            </Text>
                            {renderPlacementBadge(
                              roundPlacementByKey[normalizePlayerKey(player.id)] ??
                                roundPlacementByKey[normalizePlayerKey(player.name)],
                              tourPlacementByKey[normalizePlayerKey(player.id)] ??
                                tourPlacementByKey[normalizePlayerKey(player.name)]
                            )}
                          </View>
                        );
                      })()}
                      {strokeIndexForHole !== null
                        ? (() => {
                            const hcapStrokes =
                              ((hasExactHoleData ? holeEntries.find((h) => h.playerId === player.id) : undefined)?.handicapStrokes ??
                                computeStrokesReceivedForHole(player.handicap ?? null, strokeIndexForHole)) ?? 0;
                            const keyNorm = normalizePlayerKey(player.id);
                            const nameNorm = normalizePlayerKey(player.name);
                            const isTourLeader = tourLeaderKeys.has(keyNorm) || tourLeaderKeys.has(nameNorm);
                            const placement =
                              tourPlacementByKey[keyNorm] ??
                              tourPlacementByKey[nameNorm];
                            return (
                              <View style={styles.compactHandicapRow}>
                                <Text style={styles.compactHandicapText}>{`+${hcapStrokes}`}</Text>
                                {isTourLeader && !placement ? <Text style={styles.compactLeaderTrophy}> 🥇</Text> : null}
                              </View>
                            );
                          })()
                        : null}
                      </View>
                    </View>
                  </View>
                  {(() => {
                    const displayScore = selectedScore;
                    const stablefordPoints =
                      isStablefordGame && selectedScore && selectedScore !== 'Pickup' && parForHole
                        ? computeStablefordPointsForHole({
                            gross: selectedScore,
                            handicap: player.handicap ?? null,
                            strokeIndex: strokeIndexForHole,
                            par: parForHole,
                          })
                        : null;
                    const compactCounts = resolveHazardCountsForPlayer(player);
                    const compactHazardBadges = hazardBadgesForPlayer(player);
                    return (
                      <View style={styles.compactScoreActions}>
                        {mysteryWinnerIdsForHole.has(player.id) ? (
                          <Image source={MysteryPrizeIcon} style={styles.compactScoreMysteryIcon} resizeMode="contain" />
                        ) : null}
                        {(() => {
                          const freeze = freezeStatusByPlayerForHole.get(player.id);
                          if (!freeze) return null;
                          const pointsLabel = freeze.points === null ? '–' : String(freeze.points);
                          return (
                            <View
                              style={styles.compactScoreFreezeBadge}
                              accessibilityLabel={
                                freeze.frozen
                                  ? t('hole.frozenHoleBadgePoints', { points: pointsLabel })
                                  : freeze.points === null
                                    ? t('hole.unfrozenHoleBadge')
                                    : t('hole.unfrozenHoleBadgePoints', { points: pointsLabel })
                              }
                              testID={`hole-freeze-badge-${player.id}`}
                            >
                              <Text
                                style={styles.compactScoreFreezeBadgePoints}
                                accessible={false}
                                testID={`hole-freeze-points-${player.id}`}
                              >
                                {pointsLabel}
                              </Text>
                              <Ionicons
                                name={freeze.frozen ? 'snow' : 'snow-outline'}
                                size={20}
                                color={freeze.frozen ? appColors.accent.freeze : appColors.accent.freezeMuted}
                                testID={`hole-freeze-snowflake-${player.id}`}
                              />
                            </View>
                          );
                        })()}
                        {eclecticBestByPlayerForHole.has(player.id) ? (
                          <Text
                            style={styles.compactScoreEclecticBadge}
                            accessibilityLabel={t('hole.eclecticBestBadge', {
                              points: eclecticBestByPlayerForHole.get(player.id) as number,
                            })}
                            testID={`hole-eclectic-best-${player.id}`}
                          >
                            {`(${eclecticBestByPlayerForHole.get(player.id)})`}
                          </Text>
                        ) : null}
                        <Pressable
                          ref={playerIndex === 0 ? compactScoreButtonCoachMarkRef : undefined}
                          testID={`hole-score-button-${player.id}`}
                          style={styles.compactScoreButton}
                          // AI players' scores come from the server; the row shows them
                          // but never opens the manual score popup. Viewers see every score
                          // but can open none of them.
                          disabled={player.isAi === true || !canWrite}
                          accessibilityLabel={
                            player.isAi && !displayScore ? t('ai.pendingReveal') : undefined
                          }
                          onPress={() => {
                            if (player.isAi || !canWrite) return;
                            scorePopupInitialScoreRef.current = displayScore ?? null;
                            setScorePopupPlayerId(player.id);
                          }}
                        >
                          <Image
                            source={ScorePopupLargeButtonImage}
                            style={styles.compactScoreButtonImage}
                            resizeMode="contain"
                            accessible={false}
                          />
                          <Text
                            style={[
                              styles.compactScoreButtonText,
                              !displayScore && styles.compactScoreButtonTextPlaceholder,
                            ]}
                          >
                            {displayScore ?? (player.isAi ? '…' : t('hole.scoreButtonLabel'))}
                          </Text>
                          {compactHazardBadges.length === 0 ? null : hazardAnimationsEnabled ? (
                            <CyclingSlot
                              style={styles.compactScoreHazardCritter}
                              items={compactHazardBadges.map((badge) => (
                                <Image
                                  key={badge.key}
                                  source={hazardCritterForType(badge.hazardType)}
                                  style={styles.slotFill}
                                  resizeMode="contain"
                                  accessibilityLabel={badge.label}
                                />
                              ))}
                            />
                          ) : (
                            <View style={styles.compactScoreHazardBadges}>
                              {compactHazardBadges.map((badge) => (
                                <View key={`${player.id}-${badge.key}`} style={[styles.compactScoreHazardBadge, badge.style]}>
                                  <Text style={[styles.compactScoreHazardBadgeTextBase, badge.textStyle]}>
                                    {badge.label.charAt(0)}
                                  </Text>
                                </View>
                              ))}
                            </View>
                          )}
                        </Pressable>
                        {isStablefordGame ? (
                          <Text style={styles.compactStablefordText}>
                            {stablefordPoints !== null ? String(stablefordPoints) : '–'}
                          </Text>
                        ) : null}
                      </View>
                    );
                  })()}
                </View>
                </Fragment>
                );
                })()
              ))}
            </View>
            <Modal
              visible={!!scorePopupPlayerId}
              transparent
              animationType="none"
              onRequestClose={closeScorePopup}
            >
              <Pressable
                style={[
                  styles.modalBackdrop,
                  styles.scoreModalBackdrop,
                  shouldTopAlignScorePopup && styles.scoreModalBackdropTop,
                ]}
                onPress={closeScorePopup}
              >
                <Pressable onPress={() => null}>
                  <Animated.View
                    style={[
                      styles.modalCard,
                      styles.scoreModalCard,
                      shouldTopAlignScorePopup ? { marginBottom: 0, maxHeight: windowHeight - 24 } : null,
                      { transform: [{ translateY: scorePopupSheetTranslateY }] },
                    ]}
                  >
                  <ScrollView
                    style={styles.scorePopupScroll}
                    contentContainerStyle={styles.scorePopupScrollContent}
                    showsVerticalScrollIndicator
                    bounces={false}
                  >
                    {(() => {
                      const name = scorePopupPlayer?.name ?? '';
                      const hcapStrokes =
                        scorePopupPlayer && strokeIndexForHole !== null
                          ? ((hasExactHoleData ? holeEntries.find((h) => h.playerId === scorePopupPlayer.id) : undefined)?.handicapStrokes ??
                              computeStrokesReceivedForHole(scorePopupPlayer.handicap ?? null, strokeIndexForHole)) ?? 0
                          : 0;
                      return (
                        <>
                          <Text style={[styles.modalTitle, styles.scorePopupTitle]}>
                            {t('hole.scorePopupTitle', {
                              name,
                              holeLabel: t('hole.title'),
                              hole: holeNumber,
                            })}
                          </Text>
                          {scorePopupPlayer ? (
                            <Text style={styles.scorePopupSubtitle}>
                              {t('hole.scorePopupHandicapAndIndexLabel', {
                                handicap: `+${hcapStrokes}`,
                                index: strokeIndexForHole ?? '-',
                              })}
                            </Text>
                          ) : null}
                        </>
                      );
                    })()}
                    <View style={styles.scorePopupGrid}>
                    {scorePopupPlayer ? (
                      <View style={styles.scorePopupHazardColumn}>
                        {showWaterEvent ? (
                          <View style={styles.scorePopupHazardButton}>
                            <Text style={styles.scorePopupHazardLabel}>{t('hole.event.water')}</Text>
                            <View style={styles.scorePopupHazardControlRow}>
                              <Pressable
                                style={styles.scorePopupHazardAdjustButton}
                                onPress={() => updateCompactHazardCount(scorePopupPlayer, 'water', -1)}
                                accessibilityRole="button"
                                accessibilityLabel={`${t('hole.event.water')} -`}
                              >
                                <Text style={styles.scorePopupHazardAdjustText}>-</Text>
                              </Pressable>
                              <Pressable
                                style={styles.scorePopupHazardIconWrap}
                                onPress={() => updateCompactHazardCount(scorePopupPlayer, 'water', 1)}
                                accessibilityRole="button"
                                accessibilityLabel={t('hole.event.water')}
                              >
                                <Image source={hazardScoreInputSymbol('water', hazardAnimationsEnabled)} style={styles.scorePopupHazardIcon} resizeMode="contain" />
                                <Text style={styles.scorePopupHazardCount}>{scorePopupEventCounts.water}</Text>
                              </Pressable>
                              <Pressable
                                style={styles.scorePopupHazardAdjustButton}
                                onPress={() => updateCompactHazardCount(scorePopupPlayer, 'water', 1)}
                                accessibilityRole="button"
                                accessibilityLabel={`${t('hole.event.water')} +`}
                              >
                                <Text style={styles.scorePopupHazardAdjustText}>+</Text>
                              </Pressable>
                            </View>
                          </View>
                        ) : null}
                        {showPenaltyEvent ? (
                          <View style={styles.scorePopupHazardButton}>
                            <Text style={styles.scorePopupHazardLabel}>{t('hole.event.penalty')}</Text>
                            <View style={styles.scorePopupHazardControlRow}>
                              <Pressable
                                style={styles.scorePopupHazardAdjustButton}
                                onPress={() => updateCompactHazardCount(scorePopupPlayer, 'penalty', -1)}
                                accessibilityRole="button"
                                accessibilityLabel={`${t('hole.event.penalty')} -`}
                              >
                                <Text style={styles.scorePopupHazardAdjustText}>-</Text>
                              </Pressable>
                              <Pressable
                                style={styles.scorePopupHazardIconWrap}
                                onPress={() => updateCompactHazardCount(scorePopupPlayer, 'penalty', 1)}
                                accessibilityRole="button"
                                accessibilityLabel={t('hole.event.penalty')}
                              >
                                <Image source={hazardScoreInputSymbol('penalty', hazardAnimationsEnabled)} style={styles.scorePopupHazardIcon} resizeMode="contain" />
                                <Text style={styles.scorePopupHazardCount}>{scorePopupEventCounts.penalty}</Text>
                              </Pressable>
                              <Pressable
                                style={styles.scorePopupHazardAdjustButton}
                                onPress={() => updateCompactHazardCount(scorePopupPlayer, 'penalty', 1)}
                                accessibilityRole="button"
                                accessibilityLabel={`${t('hole.event.penalty')} +`}
                              >
                                <Text style={styles.scorePopupHazardAdjustText}>+</Text>
                              </Pressable>
                            </View>
                          </View>
                        ) : null}
                        {showOutEvent ? (
                          <View style={styles.scorePopupHazardButton}>
                            <Text style={styles.scorePopupHazardLabel}>{t('hole.event.out')}</Text>
                            <View style={styles.scorePopupHazardControlRow}>
                              <Pressable
                                style={styles.scorePopupHazardAdjustButton}
                                onPress={() => updateCompactHazardCount(scorePopupPlayer, 'out', -1)}
                                accessibilityRole="button"
                                accessibilityLabel={`${t('hole.event.out')} -`}
                              >
                                <Text style={styles.scorePopupHazardAdjustText}>-</Text>
                              </Pressable>
                              <Pressable
                                style={styles.scorePopupHazardIconWrap}
                                onPress={() => updateCompactHazardCount(scorePopupPlayer, 'out', 1)}
                                accessibilityRole="button"
                                accessibilityLabel={t('hole.event.out')}
                              >
                                <Image source={hazardScoreInputSymbol('out', hazardAnimationsEnabled)} style={styles.scorePopupHazardIcon} resizeMode="contain" />
                                <Text style={styles.scorePopupHazardCount}>{scorePopupEventCounts.out}</Text>
                              </Pressable>
                              <Pressable
                                style={styles.scorePopupHazardAdjustButton}
                                onPress={() => updateCompactHazardCount(scorePopupPlayer, 'out', 1)}
                                accessibilityRole="button"
                                accessibilityLabel={`${t('hole.event.out')} +`}
                              >
                                <Text style={styles.scorePopupHazardAdjustText}>+</Text>
                              </Pressable>
                            </View>
                          </View>
                        ) : null}
                        {showBunkerEvent ? (
                          <View style={styles.scorePopupHazardButton}>
                            <Text style={styles.scorePopupHazardLabel}>{t('hole.event.bunker')}</Text>
                            <View style={styles.scorePopupHazardControlRow}>
                              <Pressable
                                style={styles.scorePopupHazardAdjustButton}
                                onPress={() => updateCompactHazardCount(scorePopupPlayer, 'bunker', -1)}
                                accessibilityRole="button"
                                accessibilityLabel={`${t('hole.event.bunker')} -`}
                              >
                                <Text style={styles.scorePopupHazardAdjustText}>-</Text>
                              </Pressable>
                              <Pressable
                                style={styles.scorePopupHazardIconWrap}
                                onPress={() => updateCompactHazardCount(scorePopupPlayer, 'bunker', 1)}
                                accessibilityRole="button"
                                accessibilityLabel={t('hole.event.bunker')}
                              >
                                <Image source={hazardScoreInputSymbol('bunker', hazardAnimationsEnabled)} style={styles.scorePopupHazardIcon} resizeMode="contain" />
                                <Text style={styles.scorePopupHazardCount}>{scorePopupEventCounts.bunker}</Text>
                              </Pressable>
                              <Pressable
                                style={styles.scorePopupHazardAdjustButton}
                                onPress={() => updateCompactHazardCount(scorePopupPlayer, 'bunker', 1)}
                                accessibilityRole="button"
                                accessibilityLabel={`${t('hole.event.bunker')} +`}
                              >
                                <Text style={styles.scorePopupHazardAdjustText}>+</Text>
                              </Pressable>
                            </View>
                          </View>
                        ) : null}
                        {showThreePuttEvent ? (
                          <View style={styles.scorePopupHazardButton}>
                            <Text style={styles.scorePopupHazardLabel}>{t('hole.event.threePutt')}</Text>
                            <View style={styles.scorePopupHazardControlRow}>
                              {/* A hole can only be three-putted once, so this is a toggle, not a
                                  counter — same on/off shape as chip in. */}
                              <Pressable
                                style={[
                                  styles.scorePopupHazardIconWrap,
                                  styles.scorePopupHazardToggle,
                                  scorePopupEventCounts.threePutt > 0 && styles.scorePopupHazardToggleSelected,
                                ]}
                                onPress={() =>
                                  updateCompactHazardCount(
                                    scorePopupPlayer,
                                    'threePutt',
                                    scorePopupEventCounts.threePutt > 0 ? -1 : 1
                                  )
                                }
                                accessibilityRole="button"
                                accessibilityState={{ selected: scorePopupEventCounts.threePutt > 0 }}
                                accessibilityLabel={t('hole.event.threePutt')}
                              >
                                <Image source={hazardScoreInputSymbol('threeputt', hazardAnimationsEnabled)} style={styles.scorePopupHazardIcon} resizeMode="contain" />
                                {scorePopupEventCounts.threePutt > 0 ? (
                                  <Text style={styles.scorePopupChipInCheck}>✓</Text>
                                ) : null}
                              </Pressable>
                            </View>
                          </View>
                        ) : null}
                        {showChipInEvent ? (
                          <View style={styles.scorePopupHazardButton}>
                            <Text style={styles.scorePopupHazardLabel}>{t('hole.event.chipIn')}</Text>
                            <View style={styles.scorePopupHazardControlRow}>
                              <Pressable
                                style={[
                                  styles.scorePopupChipInToggle,
                                  scorePopupEventCounts.chipIn > 0 && styles.scorePopupChipInToggleSelected,
                                ]}
                                onPress={() =>
                                  updateCompactHazardCount(
                                    scorePopupPlayer,
                                    'chipIn',
                                    scorePopupEventCounts.chipIn > 0 ? -1 : 1
                                  )
                                }
                                accessibilityRole="button"
                                accessibilityState={{ selected: scorePopupEventCounts.chipIn > 0 }}
                                accessibilityLabel={t('hole.event.chipIn')}
                              >
                                <Image source={ChipInIcon} style={styles.scorePopupHazardIcon} resizeMode="contain" />
                                {scorePopupEventCounts.chipIn > 0 ? (
                                  <Text style={styles.scorePopupChipInCheck}>✓</Text>
                                ) : null}
                              </Pressable>
                            </View>
                          </View>
                        ) : null}
                        {showEnhancedInput ? (
                          <>
                            {/* Hairline between the game inputs above and the personal ones below. */}
                            <View style={styles.scorePopupEnhancedDivider} />
                            {parForHole !== 3 ? (
                              <View style={styles.scorePopupHazardButton}>
                                <Text style={styles.scorePopupHazardLabel}>{t('hole.event.fairway')}</Text>
                                <View style={styles.scorePopupHazardControlRow}>
                                  {/* One button per side, left to right. Tapping the chosen one clears it. */}
                                  {FAIRWAY_RESULTS.map((choice) => {
                                    const selected = scorePopupEnhancedValues.fairway === choice;
                                    const label = t(`hole.enhanced.fairway${capitalizeFirst(choice)}`);
                                    return (
                                      <Pressable
                                        key={`score-popup-fairway-${choice}`}
                                        style={[
                                          styles.scorePopupEnhancedTile,
                                          styles.scorePopupFairwayButton,
                                          selected
                                            ? styles.scorePopupEnhancedTileSelected
                                            : styles.scorePopupEnhancedTileUnset,
                                        ]}
                                        onPress={() =>
                                          setEnhancedInputValue(
                                            scorePopupPlayer,
                                            'fairway',
                                            selected ? null : choice
                                          )
                                        }
                                        accessibilityRole="button"
                                        accessibilityState={{ selected }}
                                        accessibilityLabel={label}
                                      >
                                        <Text style={styles.scorePopupFairwayButtonText} numberOfLines={1}>
                                          {label}
                                        </Text>
                                      </Pressable>
                                    );
                                  })}
                                </View>
                              </View>
                            ) : null}
                            <View style={styles.scorePopupHazardButton}>
                              <Text style={styles.scorePopupHazardLabel}>{t('hole.event.putts')}</Text>
                              <View style={styles.scorePopupHazardControlRow}>
                                <Pressable
                                  style={styles.scorePopupHazardAdjustButton}
                                  onPress={() =>
                                    setEnhancedInputValue(
                                      scorePopupPlayer,
                                      'putts',
                                      stepPutts(scorePopupEnhancedValues.putts, -1)
                                    )
                                  }
                                  accessibilityRole="button"
                                  accessibilityLabel={t('hole.enhanced.puttsDecrease')}
                                >
                                  <Text style={styles.scorePopupHazardAdjustText}>-</Text>
                                </Pressable>
                                <Pressable
                                  style={[
                                    styles.scorePopupHazardIconWrap,
                                    styles.scorePopupEnhancedTile,
                                    scorePopupEnhancedValues.putts === null && styles.scorePopupEnhancedTileUnset,
                                  ]}
                                  onPress={() =>
                                    setEnhancedInputValue(
                                      scorePopupPlayer,
                                      'putts',
                                      stepPutts(scorePopupEnhancedValues.putts, 1)
                                    )
                                  }
                                  accessibilityRole="button"
                                  accessibilityLabel={t('hole.event.putts')}
                                >
                                  <Text style={styles.scorePopupHazardCount}>
                                    {scorePopupEnhancedValues.putts ?? t('hole.enhanced.unset')}
                                  </Text>
                                </Pressable>
                                <Pressable
                                  style={styles.scorePopupHazardAdjustButton}
                                  onPress={() =>
                                    setEnhancedInputValue(
                                      scorePopupPlayer,
                                      'putts',
                                      stepPutts(scorePopupEnhancedValues.putts, 1)
                                    )
                                  }
                                  accessibilityRole="button"
                                  accessibilityLabel={t('hole.enhanced.puttsIncrease')}
                                >
                                  <Text style={styles.scorePopupHazardAdjustText}>+</Text>
                                </Pressable>
                              </View>
                            </View>
                            <View style={styles.scorePopupHazardButton}>
                              <Text style={styles.scorePopupHazardLabel}>{t('hole.event.gir')}</Text>
                              <View style={styles.scorePopupHazardControlRow}>
                                {/* Read-only: the score, the putts and par decide it. */}
                                <View
                                  style={[
                                    styles.scorePopupHazardIconWrap,
                                    styles.scorePopupEnhancedTile,
                                    // Not the hazard toggle's selected style: that paints a green
                                    // fill, which would cover the slate. Border only here.
                                    scorePopupGir === true && styles.scorePopupEnhancedTileSelected,
                                    scorePopupGir === null && styles.scorePopupEnhancedTileUnset,
                                  ]}
                                  accessibilityLabel={t('hole.event.gir')}
                                >
                                  <Text style={styles.scorePopupEnhancedTileText} numberOfLines={1}>
                                    {scorePopupGir === null
                                      ? t('hole.enhanced.unset')
                                      : scorePopupGir
                                        ? t('hole.enhanced.girHit')
                                        : t('hole.enhanced.girMiss')}
                                  </Text>
                                </View>
                              </View>
                            </View>
                          </>
                        ) : null}
                        {mysteryEntriesForHole.map((entry) => {
                          const selected = mysteryWinnerForHole?.playerId === scorePopupPlayer.id;
                          return (
                            <Pressable
                              key={`score-popup-mystery-hazard-${entry.subgameId}`}
                              style={[
                                styles.scorePopupMysteryButton,
                                selected && styles.scorePopupMysteryButtonSelected,
                              ]}
                              onPress={() => handleMysteryWinnerSelect(entry.subgameId, scorePopupPlayer.id)}
                              accessibilityRole="button"
                              accessibilityState={{ selected }}
                              accessibilityLabel={subgameNameById[entry.subgameId] ?? t('hole.mysteryPrizeUnknownSubgame')}
                            >
                              <Image source={MysteryPrizeIcon} style={styles.scorePopupMysteryIcon} resizeMode="contain" />
                              <Text style={styles.scorePopupMysteryText} numberOfLines={2}>
                                {subgameNameById[entry.subgameId] ?? t('hole.mysteryPrizeUnknownSubgame')}
                              </Text>
                            </Pressable>
                          );
                        })}
                        {/* Only where the hole has no designated challenge: offering both awards
                            the same hole two prizes, and both used to be counted. */}
                        {isMysterySelected && mysteryEntriesForHole.length === 0 ? (
                          <Pressable
                            style={styles.scorePopupMysterySetupButton}
                            onPress={() => handleMysteryPrizeToggle(scorePopupPlayer.id)}
                            accessibilityRole="button"
                            accessibilityState={{ selected: mysteryWinnerForHole?.playerId === scorePopupPlayer.id }}
                            accessibilityLabel={t('hole.mysteryPrizeSetup')}
                          >
                            <Text style={styles.scorePopupHazardLabel}>{t('hole.mysteryPrizeSetup')}</Text>
                            <View style={styles.scorePopupHazardIconWrap}>
                              <Image source={MysteryPrizeIcon} style={styles.scorePopupHazardIcon} resizeMode="contain" />
                              {mysteryWinnerForHole?.playerId === scorePopupPlayer.id ? (
                                <Text style={styles.scorePopupHazardCount}>✓</Text>
                              ) : null}
                            </View>
                          </Pressable>
                        ) : null}
                      </View>
                    ) : null}
                    <View style={styles.scorePopupLeftColumn} pointerEvents="box-none">
                          <Image source={ScorePopupLongButtonImage} style={styles.scorePopupTopPlateImage} resizeMode="contain" pointerEvents="none" />
                          <Text style={styles.scorePopupTopPlateText} numberOfLines={1} pointerEvents="none">{`Par ${parLabel}`}</Text>
                          <Image source={ScorePopupRailBannerImage} style={styles.scorePopupRailImage} resizeMode="stretch" pointerEvents="none" />
                          <NativeView style={styles.scorePopupRailMarksColumn}>
                            {Array.from({ length: 8 }).map((_, index) => (
                              <Pressable
                                key={`score-popup-rail-mark-${index}`}
                                style={[
                                  styles.scorePopupRailMarkSlot,
                                  index === 0 && styles.scorePopupRailMarkIconFirst,
                                ]}
                                onPress={() => {
                                  if (!scorePopupPlayer) return;
                                  if (index === 7) {
                                    setScorePopupEightPlusOpen(true);
                                    return;
                                  }
                                  requestScoreFromPopup(scorePopupPlayer, String(index + 1));
                                }}
                                hitSlop={8}
                                accessibilityRole="button"
                                accessibilityLabel={
                                  index === 7
                                    ? t('hole.scoreButtonLabel', { value: '8+' })
                                    : t('hole.scoreButtonLabel', { value: String(index + 1) })
                                }
                              >
                                <Image
                                  source={HoleMarkImage}
                                  style={styles.scorePopupRailMarkIcon}
                                  resizeMode="contain"
                                  accessible={false}
                                />
                                {scorePopupParMarkIndex === index ? <NativeView style={styles.scorePopupRailParRing} /> : null}
                                {scorePopupSelectedMarkIndex === index ? (
                                  <Image source={PegImage} style={styles.scorePopupRailPegIcon} resizeMode="contain" accessible={false} />
                                ) : null}
                              </Pressable>
                            ))}
                          </NativeView>
                    </View>
                    <View style={styles.scorePopupRows}>
                    {(() => {
                      const selected = scorePopupPlayer
                        ? (hasExactHoleData ? (scores[scorePopupPlayer.id] ?? scores[scorePopupPlayer.name]) : null)
                        : null;
                      return (
                        <>
                    {[...Array(7)].map((_, idx) => String(idx + 1)).concat('8+').map((val) => {
                      const isParScore = parForHole ? val === String(parForHole) : false;
                      const selectedNumeric = selected && selected !== 'Pickup' ? Number.parseInt(selected, 10) : Number.NaN;
                      const selectedIsEightPlus =
                        val === '8+' && (selected === 'Pickup' || (Number.isFinite(selectedNumeric) && selectedNumeric >= 8));
                      const displayValue =
                        val === '8+'
                          ? selected === 'Pickup'
                            ? t('hole.pickup')
                            : selectedIsEightPlus
                              ? t('hole.eightPlusLabelWithValue', { value: selectedNumeric })
                              : val
                          : val;
                      return (
                        <Pressable
                          key={val}
                          testID={`score-popup-button-${val}`}
                          style={[
                            styles.scorePopupButton,
                            isParScore && styles.scorePopupButtonPar,
                            (selected === val || selectedIsEightPlus) && styles.scorePopupButtonSelected,
                          ]}
                          onPress={() => {
                            if (!scorePopupPlayer) return;
                            if (val === '8+') {
                              setScorePopupEightPlusOpen(true);
                              return;
                            }
                            requestScoreFromPopup(scorePopupPlayer, val);
                          }}
                          accessibilityRole="button"
                          accessibilityState={{ selected: selected === val || selectedIsEightPlus }}
                        >
                          <Image
                            source={ScorePopupLargeButtonImage}
                            style={styles.scorePopupButtonImage}
                            resizeMode="stretch"
                            accessible={false}
                          />
                          <Text
                            style={[
                              styles.scorePopupButtonText,
                              isParScore && styles.scorePopupButtonTextPar,
                            ]}
                          >
                            {displayValue}
                          </Text>
                        </Pressable>
                      );
                    })}
                        </>
                      );
                    })()}
                    </View>
                    </View>
                  {scorePopupEightPlusOpen && scorePopupPlayer ? (
                    <View style={styles.scorePopupEightPlusOverlay}>
                      <Pressable style={styles.scorePopupEightPlusBackdrop} onPress={() => setScorePopupEightPlusOpen(false)} />
                      <View style={styles.scorePopupEightPlusCard}>
                        {(isStrokePlayGame ? ['8', '9', '10', '11', '12'] : ['8', '9', '10', '11', '12', 'Pickup']).map((option) => (
                          <Pressable
                            key={`eight-plus-option-${option}`}
                            style={styles.scorePopupEightPlusOption}
                            onPress={() => {
                              setScorePopupEightPlusOpen(false);
                              requestScoreFromPopup(scorePopupPlayer, option);
                            }}
                          >
                            <Text style={styles.scorePopupEightPlusOptionText}>{option === 'Pickup' ? t('hole.pickup') : option}</Text>
                          </Pressable>
                        ))}
                      </View>
                    </View>
                  ) : null}
                  </ScrollView>
                  <GoffyButton
                    label="X"
                    onPress={closeScorePopup}
                    tone="dark"
                    oldButtonStyle={styles.modalCloseButtonBlue}
                    style={styles.scorePopupCloseButton}
                    textStyle={styles.scorePopupCloseButtonText}
                    accessibilityLabel={t('hole.close')}
                  />
                  {pendingScoreChange ? (
                    <View style={styles.scoreChangeConfirmOverlay}>
                      <Pressable style={styles.scoreChangeConfirmBackdrop} onPress={() => setPendingScoreChange(null)} />
                      <View style={styles.scoreChangeConfirmCard}>
                        <Text style={styles.modalTitle}>{t('hole.scoreChangeConfirmTitle')}</Text>
                        <Text style={styles.modalSubtitle}>
                          {t('hole.scoreChangeConfirmMessage', {
                            name: playerHasRealName(pendingScoreChange.player.name)
                              ? pendingScoreChange.player.name
                              : t('hole.unknownPlayer'),
                            current:
                              pendingScoreChange.currentValue === 'Pickup'
                                ? t('hole.pickup')
                                : pendingScoreChange.currentValue,
                            next:
                              pendingScoreChange.value === 'Pickup'
                                ? t('hole.pickup')
                                : pendingScoreChange.value,
                          })}
                        </Text>
                        <View style={styles.scoreChangeConfirmActions}>
                          <GoffyButton
                            label={t('hole.scoreChangeConfirmCancel')}
                            onPress={() => setPendingScoreChange(null)}
                            tone="dark"
                            oldButtonStyle={styles.modalCloseButtonBlue}
                            style={styles.scoreChangeConfirmButton}
                            textStyle={styles.modalBackText}
                            testID="score-change-cancel-button"
                          />
                          <GoffyButton
                            label={t('hole.scoreChangeConfirmCta')}
                            onPress={confirmPendingScoreChange}
                            tone="dark"
                            oldButtonStyle={styles.modalCloseButtonBlue}
                            style={styles.scoreChangeConfirmButton}
                            textStyle={styles.modalBackText}
                            testID="score-change-confirm-button"
                          />
                        </View>
                      </View>
                    </View>
                  ) : null}
                  </Animated.View>
                </Pressable>
              </Pressable>
            </Modal>
            <Modal
              visible={!!selectedPlayerId}
              transparent
              animationType="fade"
              onRequestClose={() => setSelectedPlayerId(null)}
            >
              <Pressable style={styles.modalBackdrop} onPress={() => setSelectedPlayerId(null)}>
                <Pressable style={[styles.modalCard, styles.playerBreakdownCard]} onPress={() => null}>
                  <Text style={styles.modalTitle}>
                    {t('hole.playerBreakdownTitle', { name: selectedPlayer?.name ?? '' })}
                  </Text>
                  {selectedPlayerGamePoints.length > 0 ? (
                    <ScrollView
                      style={styles.playerBreakdownScroll}
                      contentContainerStyle={styles.playerBreakdownScrollContent}
                      showsVerticalScrollIndicator
                      nestedScrollEnabled
                      bounces={false}
                    >
                      <View style={[styles.modalRow, styles.playerBreakdownHeaderRow]}>
                        <View style={styles.modalLabelGroup}>
                          <View style={styles.modalGameIconSpacer} />
                          <Text
                            style={[styles.playerBreakdownHeaderText, styles.modalGamePosition]}
                            numberOfLines={1}
                          >
                            {t('hole.playerBreakdownPositionHeader')}
                          </Text>
                          <Text style={styles.playerBreakdownHeaderText}>
                            {t('hole.playerBreakdownGameHeader')}
                          </Text>
                        </View>
                        <Text style={[styles.playerBreakdownHeaderText, styles.modalValue]}>
                          {t('hole.playerBreakdownMeritsHeader')}
                        </Text>
                      </View>
                      {selectedPlayerGamePoints.map((row) => {
                        // Hazard and achievement games place nobody, so their position column stays
                        // empty rather than showing a rank everyone shares at zero.
                        const ranked = gameGroupHasPositions(getGameGroupId(row.id, row.name));
                        const position = ranked
                          ? row.position
                            ?? (selectedPlayer?.id ? gamePositionsByPlayer[row.id]?.[selectedPlayer.id] : null)
                          : null;
                        const isShared = !!(position && positionShareByGame[row.id]?.[selectedPlayer?.id ?? '']);
                        return (
                          <View key={row.id} style={styles.modalRow}>
                            <View style={styles.modalLabelGroup}>
                              <Image source={row.icon} style={styles.modalGameIcon} resizeMode="contain" />
                              <Text
                                style={styles.modalGamePosition}
                                accessibilityLabel={
                                  position ? t('hole.positionShort', { position }) : undefined
                                }
                              >
                                {position ? String(position) : ''}
                              </Text>
                              <Text style={styles.modalLabel}>
                                {row.name}
                                {isShared ? ` (${t('hole.positionShared')})` : ''}
                              </Text>
                            </View>
                            <Text style={styles.modalValue}>{row.points}</Text>
                          </View>
                        );
                      })}
                    </ScrollView>
                  ) : (
                    <Text style={styles.modalEmpty}>{t('hole.playerBreakdownEmpty')}</Text>
                  )}
                  {selectedPlayerGamePoints.length > 0 ? (
                    // Outside the scroll on purpose: the total is the number the player came for,
                    // and a long list of games would otherwise push it out of sight.
                    <View style={[styles.modalRow, styles.playerBreakdownTotalRow, styles.playerBreakdownTotalGutter]}>
                      <Text style={styles.playerBreakdownTotalLabel}>
                        {t('hole.playerBreakdownTotal')}
                      </Text>
                      <Text style={[styles.modalValue, styles.playerBreakdownTotalValue]}>
                        {selectedPlayerGamePoints.reduce((sum, row) => sum + (row.points ?? 0), 0)}
                      </Text>
                    </View>
                  ) : null}
                  <GoffyButton
                    label={t('hole.close')}
                    onPress={() => setSelectedPlayerId(null)}
                    tone="dark"
                    oldButtonStyle={styles.modalCloseButtonBlue}
                    style={[styles.modalCloseButton, styles.playerBreakdownOkButton]}
                    textStyle={styles.modalCloseText}
                    accessibilityLabel={t('hole.close')}
                  />
                </Pressable>
              </Pressable>
            </Modal>
            <Modal
              visible={leaderboardChangeModalOpen}
              transparent
              animationType="fade"
              onRequestClose={() => null}
            >
              <Pressable style={styles.modalBackdrop} onPress={() => null}>
                <Pressable style={[styles.modalCard, styles.leaderboardChangeModalCard]} onPress={() => null}>
                  <View style={styles.leaderboardChangeBadge}>
                    <Text style={styles.leaderboardChangeBadgeIcon}>🏆</Text>
                  </View>
                  <Text style={styles.leaderboardChangeTitle}>
                    {t('hole.leaderboardChangeTitle')}
                    <Text style={styles.leaderboardChangeTitleSpark}> ✨</Text>
                  </Text>
                  <Text style={styles.modalSubtitle}>{t('hole.leaderboardChangeSubtitle', { hole: holeNumber })}</Text>
                  {leaderboardChangeRows.map((row) => {
                    const movementArrow = row.movement === 'up' ? '↑' : row.movement === 'down' ? '↓' : '';
                    const movementStyle =
                      row.movement === 'up'
                        ? styles.leaderboardChangeArrowUp
                        : row.movement === 'down'
                          ? styles.leaderboardChangeArrowDown
                          : styles.leaderboardChangeArrowSame;
                    const isLeader = row.roundRank === 1;
                    return (
                      <View
                        key={row.playerId}
                        style={[styles.leaderboardChangeRow, isLeader && styles.leaderboardChangeRowLeader]}
                      >
                        <View
                          style={[styles.leaderboardChangeRankPill, isLeader && styles.leaderboardChangeRankPillLeader]}
                        >
                          <Text
                            style={[
                              styles.leaderboardChangeRankPillText,
                              isLeader && styles.leaderboardChangeRankPillTextLeader,
                            ]}
                          >
                            {row.roundRank ?? '—'}
                          </Text>
                        </View>
                        <Text style={styles.leaderboardChangeName}>{row.playerName}</Text>
                        <Text style={[styles.leaderboardChangeArrow, movementStyle]}>{movementArrow}</Text>
                        <Text
                          style={[styles.leaderboardChangeValue, isLeader && styles.leaderboardChangeValueLeader]}
                        >
                          {row.roundRank ? t('hole.positionShort', { position: row.roundRank }) : '—'}
                        </Text>
                      </View>
                    );
                  })}
                  {leaderboardChangeGameLines.length > 0 ? (
                    <View style={styles.leaderboardChangeGames}>
                      {leaderboardChangeGameLines.map((line) => (
                        <View key={line.gameId} style={styles.leaderboardChangeGameBlock}>
                          <Text style={styles.leaderboardChangeGameName} numberOfLines={1}>
                            {line.gameName}
                          </Text>
                          <View style={styles.leaderboardChangeGameChips}>
                            {line.entries.map((entry) => {
                              const isLeader = entry.position === 1;
                              return (
                                <View
                                  key={entry.position}
                                  style={[
                                    styles.leaderboardChangeChip,
                                    isLeader && styles.leaderboardChangeChipLeader,
                                  ]}
                                >
                                  <View
                                    style={[
                                      styles.leaderboardChangeChipPill,
                                      isLeader && styles.leaderboardChangeChipPillLeader,
                                    ]}
                                  >
                                    <Text
                                      style={[
                                        styles.leaderboardChangeChipPillText,
                                        isLeader && styles.leaderboardChangeChipPillTextLeader,
                                      ]}
                                    >
                                      {entry.position}
                                    </Text>
                                  </View>
                                  <Text style={styles.leaderboardChangeChipName} numberOfLines={1}>
                                    {entry.sharedWith > 0
                                      ? t('hole.leaderboardChangeSharedWith', {
                                          name: entry.name,
                                          count: entry.sharedWith,
                                        })
                                      : entry.name}
                                  </Text>
                                  <Text
                                    style={[
                                      styles.leaderboardChangeChipScore,
                                      isLeader && styles.leaderboardChangeChipScoreLeader,
                                    ]}
                                  >
                                    {entry.score}
                                  </Text>
                                </View>
                              );
                            })}
                          </View>
                        </View>
                      ))}
                    </View>
                  ) : null}
                  <View style={styles.leaderboardChangeActions}>
                    <GoffyButton
                      label={t('hole.leaderboardChangeViewLeaderboard')}
                      onPress={() => {
                        if (!roundId) return;
                        const params: string[] = ['from=hole'];
                        if (debug) params.push('debug=true');
                        if (selectedGameDefinitionIds.length > 0) {
                          params.push(`gameDefinitionIds=${encodeURIComponent(selectedGameDefinitionIds.join(','))}`);
                        }
                        const query = params.length > 0 ? `?${params.join('&')}` : '';
                        setLeaderboardChangeModalOpen(false);
                        setLeaderboardChangeHoleKey(null);
                        pendingAutoAdvanceHoleKeyRef.current = null;
                        router.push(`/round/${roundId}/summary${query}`);
                      }}
                      tone="dark"
                      oldButtonStyle={styles.modalCloseButtonBlue}
                      style={[styles.leaderboardChangeSecondaryButton, styles.leaderboardChangeButtonFlex]}
                      textStyle={styles.modalBackText}
                    />
                    <GoffyButton
                      label={t('hole.leaderboardChangeContinue')}
                      onPress={() => {
                        setLeaderboardChangeModalOpen(false);
                        setLeaderboardChangeHoleKey(null);
                        if (!roundId) return;
                        const upcomingHole = holeOrder.nextHole(holeNumber, startHole, holesCount);
                        if (upcomingHole === null) return;
                        // Crossing the wrap (18 -> 1) is the player's own choice via the
                        // continue/finish buttons, never an automatic advance.
                        if (holeOrder.isWrapHole(holeNumber, startHole, holesCount)) return;
                        const currentHoleKey = `${roundId}:${holeNumber}`;
                        if (pendingAutoAdvanceHoleKeyRef.current === currentHoleKey) {
                          pendingAutoAdvanceHoleKeyRef.current = null;
                          navigateToHole(upcomingHole);
                        }
                      }}
                      tone="accent"
                      style={[styles.leaderboardChangePrimaryButton, styles.leaderboardChangeButtonFlex]}
                      textStyle={styles.compactNavBlueButtonText}
                    />
                  </View>
                </Pressable>
              </Pressable>
            </Modal>
            <Modal
              visible={nextMysteryModalOpen}
              transparent
              animationType="fade"
              onRequestClose={() => setNextMysteryModalOpen(false)}
            >
              <Pressable style={styles.modalBackdrop} onPress={() => setNextMysteryModalOpen(false)}>
                <Pressable style={styles.mysteryModalFrame} onPress={() => null}>
                  <View style={styles.mysteryModalInner}>
                    <Text style={styles.modalTitle}>{t('hole.mysteryPrizeNextHoleTitle')}</Text>
                    <Text style={styles.modalSubtitle}>{t('hole.mysteryPrizeNextHoleSubtitle', { hole: holeNumber + 1 })}</Text>
                    {nextMysteryModalRows.map((row, index) => (
                      <View key={`${row.subgame}:${row.player}:${index}`} style={styles.modalRow}>
                        <Text style={styles.modalLabel}>{row.subgame}</Text>
                        <Text style={styles.modalValue}>{row.player}</Text>
                      </View>
                    ))}
                    <GoffyButton
                      label={t('hole.backButton')}
                      onPress={() => setNextMysteryModalOpen(false)}
                      tone="dark"
                      oldButtonStyle={styles.modalCloseButtonBlue}
                      style={styles.modalBackButton}
                      textStyle={styles.modalBackText}
                      accessibilityLabel={t('hole.close')}
                    />
                  </View>
                </Pressable>
              </Pressable>
            </Modal>
            {holeMenuPopup}
            <View style={styles.navRowBottom}>
              <GoffyButton
                label={t('hole.prevHole')}
                onPress={() => {
                  const previousHole = holeOrder.prevHole(holeNumber, startHole, holesCount);
                  if (previousHole !== null) viewModel.actions.navigateToHole(previousHole);
                }}
                disabled={holeOrder.isFirstHole(holeNumber, startHole, holesCount)}
                tone="dark"
                style={styles.compactNavButtonBlue}
                textStyle={styles.compactNavBlueButtonText}
              />
              {holeOrder.isWrapHole(holeNumber, startHole, holesCount) ? (
                // The round started past hole 1, so this hole continues on hole 1 — but a
                // group playing only these holes still finishes here, so both are offered.
                <GoffyButton
                  testID="hole-next-hole"
                  label={t('hole.continueToHole', { hole: 1 })}
                  onPress={() => viewModel.actions.navigateToHole(1)}
                  tone="accent"
                  style={[styles.compactNavButtonBlue, styles.compactNavButtonPrimary]}
                  textStyle={[styles.compactNavBlueButtonText, styles.compactNavButtonPrimaryText]}
                />
              ) : null}
              <GoffyButton
                // One control with two jobs, so the testID names the job rather than the button:
                // E2E has to be able to tell "advance" from "finish" without matching translated
                // labels, and this is the only route out of the last hole in play order.
                testID={
                  holeOrder.isLastHole(holeNumber, startHole, holesCount) ||
                  holeOrder.isWrapHole(holeNumber, startHole, holesCount)
                    ? 'hole-finish-round'
                    : 'hole-next-hole'
                }
                label={
                  holeOrder.isLastHole(holeNumber, startHole, holesCount) ||
                  holeOrder.isWrapHole(holeNumber, startHole, holesCount)
                    ? t('hole.finishRound')
                    : t('hole.nextHole')
                }
                onPress={() => {
                  if (
                    holeOrder.isLastHole(holeNumber, startHole, holesCount) ||
                    holeOrder.isWrapHole(holeNumber, startHole, holesCount)
                  ) {
                    viewModel.actions.finishRound();
                    return;
                  }
                  const upcomingHole = holeOrder.nextHole(holeNumber, startHole, holesCount);
                  if (upcomingHole !== null) viewModel.actions.navigateToHole(upcomingHole);
                }}
                tone="accent"
                style={[styles.compactNavButtonBlue, styles.compactNavButtonPrimary]}
                textStyle={[styles.compactNavBlueButtonText, styles.compactNavButtonPrimaryText]}
              />
            </View>
            {canUseHoleView ? (
              <HoleViewModal
                visible={holeViewOpen}
                onClose={() => setHoleViewOpen(false)}
                holeNumber={holeViewHoleNumber}
                tees={holeViewGeometry.tees}
                green={holeViewGeometry.green}
                loading={holeViewGeometry.loading}
                unavailable={holeViewGeometry.unavailable}
                shapes={holeViewGeometry.shapes}
                player={playerPosition.position}
                positionStatus={playerPosition.status}
                heading={deviceHeading.heading}
                wind={holeWind.wind}
                elevation={holeElevation.profile}
                courseId={holeViewGeometry.courseId}
                apiBaseUrl={apiBaseUrl}
                token={user?.token}
                holesCount={holesCount}
                // Only steps which hole's imagery is being previewed — never navigates, so
                // the score entry screen underneath stays exactly where it was.
                onPreviousHole={
                  holeViewHoleNumber > 1 ? () => setHoleViewStep(holeViewHoleNumber - 1) : undefined
                }
                onNextHole={
                  holeViewHoleNumber < holesCount
                    ? () => setHoleViewStep(holeViewHoleNumber + 1)
                    : undefined
                }
              />
            ) : null}
          {gameResultsPanel ? (
              <View style={styles.compactGameResultsUnderNav}>
                {gameResultsPanel}
              </View>
            ) : null}
          </View>
          </View>
      </ScrollView>
      <CoachMarkCallout
        visible={showCompactScoreCoachMark}
        targetRef={compactScoreButtonCoachMarkRef}
        title={t('coachMarks.holeScoreTitle')}
        description={t('coachMarks.holeScoreBody')}
        actionLabel={t('coachMarks.gotIt')}
        onDismiss={() => setCompactScoreCoachMarkDismissed(true)}
        hideForeverLabel={t('coachMarks.hideForever')}
        disableAllLabel={t('coachMarks.disableAll')}
        onDismissForever={() => {
          setCompactScoreCoachMarkDismissed(true);
          void dismissCompactScoreCoachMarkForever().catch(() => {
            setCompactScoreCoachMarkDismissed(false);
          });
        }}
        onDisableAll={() => {
          setCompactScoreCoachMarkDismissed(true);
          void disableAllCoachMarks().catch(() => {
            setCompactScoreCoachMarkDismissed(false);
          });
        }}
      />
      {birdsVisible ? (
        <View style={[styles.birdieOverlay, { pointerEvents: 'none' }]}>
          <Animated.Image
            source={BirdieImage}
            style={[
              styles.birdieImage,
              {
                opacity: birdieOpacity,
                transform: [
                  { scale: birdieScale },
                  {
                    rotate: birdieRotate.interpolate({
                      inputRange: [0, 1],
                      outputRange: ['0deg', '360deg'],
                    }),
                  },
                ],
              },
            ]}
            resizeMode="contain"
          />
        </View>
      ) : null}
      </View>
    );
}

function generateTraceId() {
  // `typeof` first: Hermes has no `crypto` global, and optional chaining does not
  // help when the identifier itself is undeclared — reading it throws outright.
  if (typeof crypto !== 'undefined') {
    const uuid = (crypto as { randomUUID?: () => string }).randomUUID?.();
    if (uuid) return uuid;
  }
  return `trace-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}
